import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
  Suspense,
} from "react";
import { BrandIcon, BRAND_LABELS } from './BrandIcon';
import { SvgIconModal } from './SvgIconModal';
import { SvgIconItem } from './svgCatalog';
import {
  Router,
  SwitchCamera,
  Server,
  Laptop,
  Wifi,
  Cloud,
  ShieldCheck,
  Trash2,
  X,
  Settings,
  Monitor,
  Cable,
  MousePointer2,
  Hand,
  ZoomIn,
  ZoomOut,
  Unplug,
  Plus,
  Layers,
  ChevronRight,
  Zap,
  Activity,
  Edit3,
  Tablet,
  Smartphone,
  Printer,
  Radio,
  Signal,
  AlertTriangle,
  Type,
  Square,
  Circle as CircleIcon,
  BringToFront,
  SendToBack,
  Move,
  Calculator,
  MoreVertical,
  Lock,
  Unlock,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Box,
  Magnet,
  Download,
  Save,
  FolderOpen,
  FileDown,
  PlusCircle,
  Sun,
  Moon,
  Maximize,
  Minimize,
  Minus,
  Grid,
  PlayCircle,
  Copy,
  Clipboard,
  Info,
  Menu,
  Check,
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Upload,
  Sparkles,
  RotateCcw,
  RotateCw,
  LayoutTemplate,
  Map as MapIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  MoveHorizontal,
  MoveVertical
} from "lucide-react";

// ==========================================
// Constants & Data
// ==========================================

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
const NODE_WIDTH = 80;
const NODE_HEIGHT = 80;
const GRID_SIZE = 20;
const MAX_NODES = 150; // Security Fix 6: Limit maximum nodes to prevent DoS
const MAX_PROJECTS = 10; // Security Guard: Limit concurrent project tabs to prevent memory/DOM exhaustion

const TAB_COLORS = {
  BLUE: "bg-blue-600",
  GREEN: "bg-emerald-600",
  RED: "bg-rose-600",
  ORANGE: "bg-orange-600",
  PURPLE: "bg-purple-600",
  SLATE: "bg-slate-600",
};

const ABOUT_TEXT = [
  "Welcome to NetMapper.co.uk!",
  "This app has been vibe coded using Gemini! As such, you may find it lacking some more indepth or detailed features found in many 'professional' network design solutions. But thats the idea!",
  "This Free, Lightweight, and Easy to Use web app is primarily designed for students getting into the world of Networking and do not want to pay for a professional software package, or engineers who need to create a quick and simple network diagram and export it.",
  "That being said, you can add some basic configuration to each device such as ports and IP addresses, WiFi SSIDs, VLAN IDs, and even choose your own cable type!",
  "This application doesn't store your data on any server, keeping your infrastructure completely private! To save your progress, use the 'Export Project' feature to download your project configuration locally, and simply import it when you're ready to continue.",
  "I hope you enjoy using NetMapper.co.uk!"
];

const CABLE_TYPES = {
  COPPER: { label: "Copper (Cat6)", color: "#fbbf24", style: "dashed", icon: Cable },
  MM_FIBRE: { label: "Multi-Mode Fibre", color: "#3b82f6", style: "solid", icon: Zap },
  SM_FIBRE: { label: "Single-Mode Fibre", color: "#f472b6", style: "double", icon: Activity },
  WIRELESS: { label: "Wireless Link", color: "#10b981", style: "dotted", icon: Radio },
};

const LINE_STYLES = {
  SOLID: { label: "Solid Line", value: "0" },
  DASHED: { label: "Standard Dash", value: "8,8" },
  DOTTED: { label: "Dotted", value: "2,4" },
  LONG_DASH: { label: "Long Dash", value: "16,8" },
  DASH_DOT: { label: "Dash Dot", value: "12,6,2,6" },
  LOOSE_DASH: { label: "Loose Dash", value: "8,16" },
  TIGHT_DASH: { label: "Tight Dash", value: "4,4" },
};

const DEVICE_TYPES = {
  ROUTER: { type: "ROUTER", label: "Router", icon: Router, color: "text-orange-500", border: "border-orange-500", category: "INFRA" },
  SWITCH: { type: "SWITCH", label: "Switch", icon: SwitchCamera, color: "text-blue-500", border: "border-blue-500", category: "INFRA" },
  FIREWALL: { type: "FIREWALL", label: "Firewall", icon: ShieldCheck, color: "text-red-500", border: "border-red-500", category: "INFRA" },
  SERVER: { type: "SERVER", label: "Server", icon: Server, color: "text-purple-500", border: "border-purple-500", category: "INFRA" },
  CLOUD: { type: "CLOUD", label: "Internet", icon: Cloud, color: "text-cyan-500", border: "border-cyan-500", category: "INFRA" },
  AP: { type: "AP", label: "Access Point", icon: Wifi, color: "text-green-500", border: "border-green-500", category: "INFRA" },
  PC: { type: "PC", label: "PC", icon: Monitor, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
  LAPTOP: { type: "LAPTOP", label: "Laptop", icon: Laptop, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
  TABLET: { type: "TABLET", label: "Tablet", icon: Tablet, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
  SMARTPHONE: { type: "SMARTPHONE", label: "Smartphone", icon: Smartphone, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
  PRINTER: { type: "PRINTER", label: "Printer", icon: Printer, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
  CUSTOM: { type: "CUSTOM", label: "Custom Device", icon: Box, color: "text-slate-600", border: "border-slate-400", category: "END_USER" },
};

const INITIAL_NODES = [
  { id: "1", type: "CLOUD", x: 400, y: 50, name: "Example Internet", interfaces: [] },
  { id: "2", type: "ROUTER", x: 400, y: 200, name: "Example Router", interfaces: [{ id: "if_2_1", name: "eth0", type: "PHYSICAL", ip: "Public IP", subInterfaces: [] }, { id: "if_2_2", name: "eth1", type: "PHYSICAL", ip: "192.168.1.1", subInterfaces: [{ id: "sub_2_2_1", name: "eth1.10", vlan: "10", ip: "10.0.10.1" }] }] },
  { id: "3", type: "SWITCH", x: 400, y: 350, name: "Example Switch", interfaces: [{ id: "if_3_1", name: "eth0", type: "PHYSICAL", ip: "", subInterfaces: [], switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "ALL" }, { id: "if_3_2", name: "eth1", type: "PHYSICAL", ip: "", subInterfaces: [], switchportMode: "ACCESS", accessVlan: "10" }] },
  { id: "4", type: "TEXT", x: 550, y: 200, content: "Example Text Box", fontSize: 16 },
  { id: "5", type: "SHAPE", shapeType: "RECTANGLE", x: 350, y: 150, width: 300, height: 300, color: "#3b82f6", borderWidth: 4, label: "Example Shape", borderStyle: "solid" },
];

const INITIAL_CONNECTIONS = [
  { id: "c1", from: "1", to: "2", fromIf: null, toIf: "if_2_1", cable: "COPPER" },
  { id: "c2", from: "2", to: "3", fromIf: "sub_2_2_1", toIf: "if_3_1", cable: "MM_FIBRE" },
];

export const STARTER_TEMPLATES = [
  {
    id: "roas",
    name: "Router-on-a-Stick (VLAN Trunking)",
    tag: "VLAN Trunking",
    description: "Router-on-a-stick topology routing traffic between multiple VLAN sub-interfaces across an 802.1Q trunk link to an access switch.",
    deviceCount: 5,
    nodes: [
      { id: "roas_1", type: "CLOUD", x: 380, y: 50, name: "Internet", interfaces: [] },
      {
        id: "roas_2",
        type: "ROUTER",
        x: 380,
        y: 180,
        name: "Core Router",
        interfaces: [
          { id: "roas_r_if0", name: "eth0", type: "PHYSICAL", ip: "203.0.113.1/24", subInterfaces: [] },
          {
            id: "roas_r_if1",
            name: "eth1",
            type: "PHYSICAL",
            ip: "",
            subInterfaces: [
              { id: "roas_r_sub10", name: "eth1.10", vlan: "10", ip: "192.168.10.1/24" },
              { id: "roas_r_sub20", name: "eth1.20", vlan: "20", ip: "192.168.20.1/24" }
            ]
          }
        ]
      },
      {
        id: "roas_3",
        type: "SWITCH",
        x: 380,
        y: 320,
        name: "Access Switch",
        interfaces: [
          { id: "roas_sw_if0", name: "eth0", type: "PHYSICAL", ip: "", switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "10,20", subInterfaces: [] },
          { id: "roas_sw_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "10", subInterfaces: [] },
          { id: "roas_sw_if2", name: "eth2", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "20", subInterfaces: [] }
        ]
      },
      {
        id: "roas_4",
        type: "PC",
        x: 230,
        y: 470,
        name: "VLAN 10 PC",
        interfaces: [{ id: "roas_pc1_eth", name: "eth0", type: "PHYSICAL", ip: "192.168.10.50/24", subInterfaces: [] }]
      },
      {
        id: "roas_5",
        type: "PC",
        x: 530,
        y: 470,
        name: "VLAN 20 PC",
        interfaces: [{ id: "roas_pc2_eth", name: "eth0", type: "PHYSICAL", ip: "192.168.20.50/24", subInterfaces: [] }]
      }
    ],
    connections: [
      { id: "roas_c1", from: "roas_1", to: "roas_2", fromIf: null, toIf: "roas_r_if0", cable: "COPPER" },
      { id: "roas_c2", from: "roas_2", to: "roas_3", fromIf: "roas_r_if1", toIf: "roas_sw_if0", cable: "COPPER" },
      { id: "roas_c3", from: "roas_3", to: "roas_4", fromIf: "roas_sw_if1", toIf: "roas_pc1_eth", cable: "COPPER" },
      { id: "roas_c4", from: "roas_3", to: "roas_5", fromIf: "roas_sw_if2", toIf: "roas_pc2_eth", cable: "COPPER" }
    ]
  },
  {
    id: "enterprise_3tier",
    name: "Classic 3-Tier Enterprise",
    tag: "Enterprise",
    description: "Hierarchical Core, Distribution, and Access switching topology with high-speed fibre uplinks, redundant access switching, and enterprise endpoints.",
    deviceCount: 8,
    nodes: [
      {
        id: "ent_core",
        type: "SWITCH",
        x: 380,
        y: 60,
        name: "Core Switch",
        interfaces: [
          { id: "ent_core_if0", name: "eth0", type: "PHYSICAL", ip: "10.0.0.1/30", subInterfaces: [] },
          { id: "ent_core_if1", name: "eth1", type: "PHYSICAL", ip: "10.0.0.5/30", subInterfaces: [] },
          { id: "ent_core_if2", name: "eth2", type: "PHYSICAL", ip: "10.0.50.1/24", subInterfaces: [] }
        ]
      },
      {
        id: "ent_dist1",
        type: "SWITCH",
        x: 210,
        y: 200,
        name: "Distribution Switch 1",
        interfaces: [
          { id: "ent_d1_if0", name: "eth0", type: "PHYSICAL", ip: "10.0.0.2/30", subInterfaces: [] },
          { id: "ent_d1_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "ALL", subInterfaces: [] }
        ]
      },
      {
        id: "ent_dist2",
        type: "SWITCH",
        x: 550,
        y: 200,
        name: "Distribution Switch 2",
        interfaces: [
          { id: "ent_d2_if0", name: "eth0", type: "PHYSICAL", ip: "10.0.0.6/30", subInterfaces: [] },
          { id: "ent_d2_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "ALL", subInterfaces: [] }
        ]
      },
      {
        id: "ent_srv",
        type: "SERVER",
        x: 590,
        y: 60,
        name: "Enterprise Server",
        interfaces: [
          { id: "ent_srv_if0", name: "eth0", type: "PHYSICAL", ip: "10.0.50.10/24", subInterfaces: [] }
        ]
      },
      {
        id: "ent_acc1",
        type: "SWITCH",
        x: 210,
        y: 340,
        name: "Access Switch 1",
        interfaces: [
          { id: "ent_a1_if0", name: "eth0", type: "PHYSICAL", ip: "", switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "ALL", subInterfaces: [] },
          { id: "ent_a1_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "10", subInterfaces: [] }
        ]
      },
      {
        id: "ent_acc2",
        type: "SWITCH",
        x: 550,
        y: 340,
        name: "Access Switch 2",
        interfaces: [
          { id: "ent_a2_if0", name: "eth0", type: "PHYSICAL", ip: "", switchportMode: "TRUNK", nativeVlan: "1", allowedVlans: "ALL", subInterfaces: [] },
          { id: "ent_a2_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "20", subInterfaces: [] }
        ]
      },
      {
        id: "ent_pc1",
        type: "PC",
        x: 210,
        y: 480,
        name: "Workstation 1",
        interfaces: [{ id: "ent_pc1_eth", name: "eth0", type: "PHYSICAL", ip: "10.10.1.25/24", subInterfaces: [] }]
      },
      {
        id: "ent_pc2",
        type: "PC",
        x: 550,
        y: 480,
        name: "Workstation 2",
        interfaces: [{ id: "ent_pc2_eth", name: "eth0", type: "PHYSICAL", ip: "10.10.2.25/24", subInterfaces: [] }]
      }
    ],
    connections: [
      { id: "ent_c1", from: "ent_core", to: "ent_dist1", fromIf: "ent_core_if0", toIf: "ent_d1_if0", cable: "MM_FIBRE" },
      { id: "ent_c2", from: "ent_core", to: "ent_dist2", fromIf: "ent_core_if1", toIf: "ent_d2_if0", cable: "MM_FIBRE" },
      { id: "ent_c3", from: "ent_core", to: "ent_srv", fromIf: "ent_core_if2", toIf: "ent_srv_if0", cable: "COPPER" },
      { id: "ent_c4", from: "ent_dist1", to: "ent_acc1", fromIf: "ent_d1_if1", toIf: "ent_a1_if0", cable: "COPPER" },
      { id: "ent_c5", from: "ent_dist2", to: "ent_acc2", fromIf: "ent_d2_if1", toIf: "ent_a2_if0", cable: "COPPER" },
      { id: "ent_c6", from: "ent_acc1", to: "ent_pc1", fromIf: "ent_a1_if1", toIf: "ent_pc1_eth", cable: "COPPER" },
      { id: "ent_c7", from: "ent_acc2", to: "ent_pc2", fromIf: "ent_a2_if1", toIf: "ent_pc2_eth", cable: "COPPER" }
    ]
  },
  {
    id: "soho_wifi",
    name: "SOHO Dual-WAN & WiFi",
    tag: "Small Office / Home",
    description: "Compact office or home network setup featuring Internet gateway firewall, central distribution switch, wireless AP, and roaming end-user devices.",
    deviceCount: 6,
    nodes: [
      { id: "soho_cloud", type: "CLOUD", x: 380, y: 50, name: "Internet Cloud", interfaces: [] },
      {
        id: "soho_fw",
        type: "FIREWALL",
        x: 380,
        y: 170,
        name: "Firewall / Router",
        interfaces: [
          { id: "soho_fw_if0", name: "eth0", type: "PHYSICAL", ip: "Public IP", subInterfaces: [] },
          { id: "soho_fw_if1", name: "eth1", type: "PHYSICAL", ip: "192.168.1.1/24", subInterfaces: [] }
        ]
      },
      {
        id: "soho_sw",
        type: "SWITCH",
        x: 380,
        y: 290,
        name: "Office Switch",
        interfaces: [
          { id: "soho_sw_if0", name: "eth0", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "1", subInterfaces: [] },
          { id: "soho_sw_if1", name: "eth1", type: "PHYSICAL", ip: "", switchportMode: "ACCESS", accessVlan: "1", subInterfaces: [] }
        ]
      },
      {
        id: "soho_ap",
        type: "AP",
        x: 380,
        y: 410,
        name: "Access Point",
        interfaces: [
          { id: "soho_ap_if0", name: "eth0", type: "PHYSICAL", ip: "192.168.1.2/24", subInterfaces: [] },
          { id: "soho_ap_wifi", name: "Office_WiFi", type: "SSID", ip: "", subInterfaces: [] }
        ]
      },
      {
        id: "soho_laptop",
        type: "LAPTOP",
        x: 230,
        y: 530,
        name: "Laptop",
        interfaces: [
          { id: "soho_lap_wifi", name: "wlan0", type: "WIFI", ip: "192.168.1.101/24", subInterfaces: [] }
        ]
      },
      {
        id: "soho_phone",
        type: "SMARTPHONE",
        x: 530,
        y: 530,
        name: "Smartphone",
        interfaces: [
          { id: "soho_phone_wifi", name: "wlan0", type: "WIFI", ip: "192.168.1.102/24", subInterfaces: [] }
        ]
      }
    ],
    connections: [
      { id: "soho_c1", from: "soho_cloud", to: "soho_fw", fromIf: null, toIf: "soho_fw_if0", cable: "COPPER" },
      { id: "soho_c2", from: "soho_fw", to: "soho_sw", fromIf: "soho_fw_if1", toIf: "soho_sw_if0", cable: "COPPER" },
      { id: "soho_c3", from: "soho_sw", to: "soho_ap", fromIf: "soho_sw_if1", toIf: "soho_ap_if0", cable: "COPPER" },
      { id: "soho_c4", from: "soho_ap", to: "soho_laptop", fromIf: "soho_ap_wifi", toIf: "soho_lap_wifi", cable: "WIRELESS" },
      { id: "soho_c5", from: "soho_ap", to: "soho_phone", fromIf: "soho_ap_wifi", toIf: "soho_phone_wifi", cable: "WIRELESS" }
    ]
  }
];

export const TUTORIAL_STEPS = [
  { targetId: "tutorial-sidebar", title: "Device Library", content: "Drag and drop devices, custom images, or browse 1,600+ brand icons.", position: "right" },
  { targetId: "tutorial-tabs", title: "Project Management", content: "Manage multiple sites or diagrams here. Use the menu on the tab to Rename, Lock or Delete projects.", position: "bottom" },
  { targetId: "tutorial-toolbar", title: "Tools & Modes", content: "Switch tools, open the Subnet Calculator, set custom backgrounds, or launch pre-built Topology Starter Templates.", position: "bottom" },
  { targetId: "tutorial-calc", title: "Subnet Calculator", content: "Plan IP ranges and visualize networks instantly.", position: "bottom" },
  { targetId: "tutorial-controls", title: "View Controls", content: "Undo/Redo actions, adjust zoom, print, download JPEG snapshots, or import/export full JSON project files.", position: "bottom" },
  { targetId: "tutorial-bottom-controls", title: "Preferences", content: "Toggle Dark/Light mode, grid, or snapping.", position: "top" },
  { targetId: "tutorial-faq", title: "Help & Resources", content: "Access FAQs and shortcuts here.", position: "right" },
];

export const FAQ_DATA = [
  { question: "What is NetMapper?", answer: "NetMapper is a browser-based infrastructure diagramming tool designed for quick network topology visualization." },
  { question: "Is this app mobile compatible?", answer: "Yes! NetMapper is fully responsive. You can tap to select, tap-and-drag to move devices, and pinch-to-zoom. The interface adapts to smaller screens automatically." },
  { 
    question: "How do I undo or redo actions?", 
    answer: "Use the keyboard shortcuts Ctrl+Z (Undo) and Ctrl+Y / Ctrl+Shift+Z (Redo), or click the curved arrow buttons in the top right control bar. History resets when switching project tabs." 
  },
  { 
    question: "How do Starter Templates work?", 
    answer: "Click the 'Templates' button in the toolbar to browse pre-configured network topologies (such as Router-on-a-Stick, 3-Tier Enterprise, or SOHO WiFi). Selecting a template opens it instantly as a new, editable project tab." 
  },
  { question: "How do I save and restore my projects?", answer: "NetMapper operates with a zero-server footprint to maintain absolute user privacy. Projects are temporarily stored in your browser's local memory. To permanently save your work, click the 'Export Project' (Save icon) button in the top toolbar to download your complete multi-tab project file as a .json file. You can restore your project anytime by clicking the 'Import Project' (Folder icon) button and choosing your saved JSON project file." },
  { question: "What features are included in the Shape tool?", answer: "The Shape tool in the top toolbar allows you to insert Squares (rectangles), Circles, and freely rotatable Lines. You can customize label text, change label positioning (Top, Center, Bottom, Inside/Outside), configure custom text and border colors, adjust line width, and modify fill colors or transparency in the Properties panel." },
  { question: "Can I upload custom images and icons?", answer: "Yes! Under the 'Assets' section in the left sidebar, click 'Import Image' to upload custom PNG, JPEG, WebP, or SVG images (up to 2MB) with resizable bounds and aspect ratio controls. You can also click 'Browse 1,600+ Icons' to access and insert official AWS, Azure, GCP, and Cisco brand icons directly onto your canvas." },
  { question: "How do I print my diagram?", answer: "Click the Printer icon in the top right toolbar. This will generate a high-quality snapshot of your current canvas view and open your system's print dialog." },
  { question: "How do I export my diagram?", answer: "Click the Download icon in the top right toolbar. This will generate a high-resolution JPEG of your current canvas." },
  { question: "How do I connect devices?", answer: "Select the 'Link' tool from the toolbar (or press 'L'). Click on the source device, then click on the target device. You can configure the cable type and interfaces in the popup." },
  { question: "How do I move the canvas?", answer: "Hold the Middle Mouse Button (scroll wheel) and drag to pan around the canvas." },
  { question: "What are the keyboard shortcuts?", answer: "Middle Mouse + Drag: Pan Canvas | Ctrl+C: Copy | Ctrl+V: Paste | Delete/Backspace: Remove Item | Ctrl+Click: Multi-select | Ctrl+A: Select All" },
  { question: "How do I use the Subnet Calculator?", answer: "Click the 'Calc' button in the toolbar. Drag the calculator window to position it. Enter an IP and use the slider to adjust the CIDR mask." },
  { question: "Can I add custom devices?", answer: "Yes. Drag the 'Custom Device' (Box icon) from the End User library. You can rename it and add interfaces via the Properties panel." },
  { question: "How do I manage multiple sites?", answer: "Use the tabs at the top of the canvas. Click '+' to add a new site (project). Each site maintains its own independent set of devices and settings. Use the three dots menu on a tab to Rename, Lock or Delete it." },
  { question: "How do I change interface settings?", answer: "Select a device. In the Properties panel on the right, you can add/remove interfaces, change IP addresses, and configure VLANs/Sub-interfaces." },
  { question: "How do I use Link Snapping?", answer: "Toggle 'Link Snapping' in the connection properties window. When enabled, a mock device icon with 8 yellow snap points will appear for both source and target devices, allowing you to choose exactly where the link joins." },
  { question: "How do I add curves or right angles to links?", answer: "Hover over a link, hold 'Ctrl', then click and drag to add a curve. To add a right angle, hold 'Alt', then click and drag. A yellow control handle will appear on the modified link to let you adjust it later." },
  { question: "How do I use a custom background?", answer: "Click the 'Background' (Image icon) button in the toolbar to open the background settings. Here you can choose a custom background color, upload a custom background image (.jpg, .png, .bmp) via drag-and-drop or file selection, or reset the background to default. Your custom background will be included when you export or print your diagram." },
  { question: "Where do the brand icons come from?", answer: <>The branded icons available in the sidebar (AWS, GCP, Azure, and more) are provided via an integration with <a href="https://thesvg.org" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">thesvg.org</a>.</> },
];

// ==========================================
// Utility Functions
// ==========================================

const snapValue = (val, isSnapping) => isSnapping ? Math.round(val / GRID_SIZE) * GRID_SIZE : val;

const generateId = (prefix = "id") => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

const getMouseWorldPos = (e, rect, viewOffset, zoom) => ({
  x: (e.clientX - rect.left - viewOffset.x) / zoom,
  y: (e.clientY - rect.top - viewOffset.y) / zoom,
});

const getValidInterfaces = (node, mediaType) => {
  if (node.type === "CLOUD") return [{ id: "cloud", name: "Backbone", type: "PHYSICAL" }];
  // Safely handle nodes that might not have interfaces (e.g. TEXT, SHAPE, or corrupted state)
  if (!node.interfaces) return [];
  return node.interfaces.flatMap((i) => {
    const matches = mediaType === "WIRELESS" ? i.type === "WIFI" || i.type === "SSID" : i.type === "PHYSICAL";
    const subs = mediaType !== "WIRELESS" && i.subInterfaces ? i.subInterfaces : [];
    return matches ? [i, ...subs] : [];
  });
};

const createNodeObject = (typeKey, x, y, existingNodes, brand = "default") => {
  const timestamp = Date.now();
  let defaultInterfaces: any[] = [];
  
  // Interface generation logic
  if (typeKey === "SWITCH") defaultInterfaces = Array.from({ length: 4 }, (_, i) => ({ id: `if_${timestamp}_${i}`, name: `eth${i}`, type: "PHYSICAL", ip: "", subInterfaces: [], switchportMode: "ACCESS", accessVlan: "1" }));
  else if (["ROUTER", "FIREWALL"].includes(typeKey)) defaultInterfaces = [{ id: `if_${timestamp}_0`, name: "eth0", type: "PHYSICAL", ip: "", subInterfaces: [] }, { id: `if_${timestamp}_1`, name: "eth1", type: "PHYSICAL", ip: "", subInterfaces: [] }];
  else if (typeKey === "AP") defaultInterfaces = [{ id: `if_${timestamp}_0`, name: "eth0", type: "PHYSICAL", ip: "", subInterfaces: [] }, { id: `if_${timestamp}_wifi`, name: "Office_WiFi", type: "SSID", ip: "", subInterfaces: [] }];
  else if (["PC", "LAPTOP", "PRINTER", "CUSTOM"].includes(typeKey)) defaultInterfaces = [{ id: `if_${timestamp}_eth`, name: "eth0", type: "PHYSICAL", ip: "", subInterfaces: [] }, { id: `if_${timestamp}_wlan`, name: "wlan0", type: "WIFI", ip: "", subInterfaces: [] }];
  else if (["TABLET", "SMARTPHONE"].includes(typeKey)) defaultInterfaces = [{ id: `if_${timestamp}_wlan`, name: "wlan0", type: "WIFI", ip: "", subInterfaces: [] }];
  else defaultInterfaces = [{ id: `if_${timestamp}_0`, name: "eth0", type: "PHYSICAL", ip: "", subInterfaces: [] }];

  // Overlap prevention
  let finalX = x, finalY = y;
  let attempts = 0;
  while (existingNodes.some(n => Math.abs(n.x - finalX) < NODE_WIDTH && Math.abs(n.y - finalY) < NODE_HEIGHT) && attempts < 50) {
    finalX += GRID_SIZE; finalY += GRID_SIZE; attempts++;
  }

  
  const defaultLabel = DEVICE_TYPES[typeKey]?.label || typeKey;
  const brandLabel = brand !== 'default' && BRAND_LABELS[brand] ? BRAND_LABELS[brand][typeKey] : defaultLabel;
  const finalName = brandLabel || defaultLabel;
  return { id: generateId("node"), type: typeKey, brand, x: finalX, y: finalY, name: finalName, interfaces: defaultInterfaces };
};

// --- Validation Helpers ---
const isValidIpInput = (val) => {
  // Allow empty string to permit backspacing
  if (val === "") return true;
  // Basic char check: numbers and dots only
  if (/[^0-9.]/.test(val)) return false;
  
  const parts = val.split('.');
  // Must not have more than 4 octets
  if (parts.length > 4) return false;
  
  // Check each part
  return !parts.some(p => {
    // If part is empty (e.g. "192."), it's allowed during typing
    if (p === "") return false;
    // Each part must be at most 3 chars long and <= 255
    return p.length > 3 || parseInt(p) > 255;
  });
};

const isValidVlanInput = (val) => {
  if (val === "") return true;
  if (/[^0-9]/.test(val)) return false;
  const num = parseInt(val);
  return num <= 4092;
};

// --- Project Tab Naming & Security Helpers ---
const sanitizeProjectName = (input: string): string => {
  let sanitized = input;
  // Length Constraint: Hard-cap using .substring(0, 30) fallback block
  sanitized = sanitized.substring(0, 30);
  // Input Filtering: Strip out potential HTML/Script tag injection vectors permitting only alphanumeric, spaces, dashes, underscores
  sanitized = sanitized.replace(/[^a-zA-Z0-9 _-]/g, "");
  // Formatting Cleanup: trim
  sanitized = sanitized.trim();
  // If completely empty after cleaning, default to "Site Layout"
  if (!sanitized) {
    sanitized = "Site Layout";
  }
  return sanitized;
};

const getUniqueProjectName = (name: string, existingProjects: { name: string }[]): string => {
  let candidate = name;
  let counter = 1;
  const existingNames = new Set(existingProjects.map(p => p.name));
  while (existingNames.has(candidate)) {
    candidate = `${name} (${counter})`;
    counter++;
  }
  return candidate;
};

// ==========================================
// Hooks
// ==========================================

// Centralized Project State Management
const useProjectManager = () => {
  const [projects, setProjects] = useState([{ id: "p1", name: "Example Project", color: "BLUE", isLocked: false, nodes: INITIAL_NODES, connections: INITIAL_CONNECTIONS, zoom: 1, viewOffset: { x: 0, y: 0 }, customBackground: null, customBackgroundColor: null }]);
  const [activeProjectId, setActiveProjectId] = useState("p1");

  const activeProject = useMemo(() => projects.find(p => p.id === activeProjectId) || projects[0], [projects, activeProjectId]);

  const updateProject = useCallback((updater) => {
    setProjects(prev => prev.map(p => p.id === activeProjectId ? updater(p) : p));
  }, [activeProjectId]);

  const setNodes = useCallback((newNodesOrFn) => {
    updateProject(p => ({ ...p, nodes: typeof newNodesOrFn === 'function' ? newNodesOrFn(p.nodes) : newNodesOrFn }));
  }, [updateProject]);

  const setConnections = useCallback((newConnsOrFn) => {
    updateProject(p => ({ ...p, connections: typeof newConnsOrFn === 'function' ? newConnsOrFn(p.connections) : newConnsOrFn }));
  }, [updateProject]);

  return { projects, setProjects, activeProjectId, setActiveProjectId, activeProject, setNodes, setConnections, updateProject };
};

// ==========================================
// Sub-Components
// ==========================================

const DeleteProjectModal = ({ projectName, onConfirm, onCancel, styles, isDark }) => (
  <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`}>
    <div className={`${styles.modalBg} w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`}>
      <h3 className="font-bold text-lg mb-2 text-red-500 flex items-center gap-2"><AlertTriangle size={20} /> Delete Project?</h3>
      <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        Are you sure you want to delete <strong>{projectName}</strong>? This action cannot be undone and all data will be lost.
      </p>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className={`px-4 py-2 text-xs font-bold ${styles.textSubtle} hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg`}>Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold shadow-lg">Delete Project</button>
      </div>
    </div>
  </div>
);

const ConfirmationModal = ({ title, message, onConfirm, onCancel, styles, isDark }) => (
  <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`}>
    <div className={`${styles.modalBg} w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`}>
      <h3 className="font-bold text-lg mb-2 text-red-500 flex items-center gap-2"><AlertTriangle size={20} /> {title}</h3>
      <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        {message}
      </p>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className={`px-4 py-2 text-xs font-bold ${styles.textSubtle} hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg`}>Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold shadow-lg">Delete</button>
      </div>
    </div>
  </div>
);

const TutorialOverlay = React.memo(({ step, steps, onNext, onSkip, isDark }: any) => {
  const [overlayState, setOverlayState] = useState<any>(null);
  const currentStep = steps[step];

  useLayoutEffect(() => {
    const el = document.getElementById(currentStep.targetId);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 320, height = 280, padding = 12;
    let top = 0, left = 0, arrowStyle = {};

    if (currentStep.position === "right") { top = rect.top; left = rect.right + padding; arrowStyle = { left: -6.5, top: 20, transform: 'rotate(45deg)' }; }
    else if (currentStep.position === "bottom") { top = rect.bottom + padding; left = rect.left; arrowStyle = { top: -6.5, left: 20, transform: 'rotate(135deg)' }; }
    else if (currentStep.position === "top") { top = rect.top - height; left = rect.left; arrowStyle = { bottom: -6.5, left: 20, transform: 'rotate(-45deg)' }; }

    // Constraints
    if (top + height > window.innerHeight) top = window.innerHeight - height - 20;
    if (top < 20) top = 20;
    if (left + width > window.innerWidth) left = window.innerWidth - width - 20;

    setOverlayState({ top, left, arrowStyle, targetRect: rect });
  }, [step, currentStep]);

  if (!overlayState) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/50 pointer-events-none" />
      <div className={`fixed z-[101] w-80 p-5 rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-2 border ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-200 text-slate-900"}`} style={{ top: overlayState.top, left: overlayState.left }}>
        <h3 className="font-bold text-lg mb-2 text-blue-500">{currentStep.title}</h3>
        <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>{currentStep.content}</p>
        <div className="flex justify-between">
          <button onClick={onSkip} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-400">Skip</button>
          <button onClick={onNext} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold shadow-lg">
            {step === steps.length - 1 ? "Finish" : "Next"} <ChevronRight size={14} className="inline" />
          </button>
        </div>
        <div className={`absolute w-3 h-3 border-l border-b ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"}`} style={overlayState.arrowStyle} />
      </div>
      <div className="fixed z-[100] border-2 border-blue-500 rounded-lg animate-pulse pointer-events-none" style={{ top: overlayState.targetRect.top - 4, left: overlayState.targetRect.left - 4, width: overlayState.targetRect.width + 8, height: overlayState.targetRect.height + 8 }} />
    </>
  );
});

const ConnectionModal = ({ source, target, isInterfaceUsed, onConfirm, onCancel, initialData, styles, isDark }: any) => {
  const [sIf, setSIf] = useState(initialData?.initialFromIf || "");
  const [tIf, setTIf] = useState(initialData?.initialToIf || "");
  const [cable, setCable] = useState(initialData?.initialCable || "COPPER");

  const sourceIfs = useMemo(() => getValidInterfaces(source, cable), [source, cable]);
  const targetIfs = useMemo(() => getValidInterfaces(target, cable), [target, cable]);
  const valid = (source.type === "CLOUD" || sIf) && (target.type === "CLOUD" || tIf);

  return (
    <div className={`absolute inset-0 z-[60] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-md p-4 animate-in fade-in`}>
      <div className={`${styles.modalBg} border ${styles.panelBorder} rounded-2xl shadow-2xl w-full max-w-md`}>
        <div className={`p-4 border-b ${styles.panelBorder} flex justify-between items-center ${styles.panelHeaderBg} rounded-t-2xl`}>
          <h3 className={`font-bold flex items-center gap-2 ${styles.text}`}><Cable size={18} className="text-blue-500" /> Link Configuration</h3>
          <button onClick={onCancel}><X size={20} className="text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(CABLE_TYPES).map(([key, cfg]) => (
              <button key={key} onClick={() => { setCable(key); setSIf(""); setTIf(""); }} className={`flex items-center gap-2 p-2 rounded-xl border transition-all ${cable === key ? "bg-blue-600/10 border-blue-600" : `${styles.inputBg} ${styles.inputBorder}`}`}>
                {React.createElement(cfg.icon, { size: 14, color: cfg.color })}
                <span className={`text-[10px] font-bold ${cable === key ? "text-blue-400" : styles.textSubtle}`}>{cfg.label}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[ { label: source.name, val: sIf, set: setSIf, opts: sourceIfs, node: source }, { label: target.name, val: tIf, set: setTIf, opts: targetIfs, node: target } ].map((f, i) => (
              <div key={i} className="space-y-1">
                <label className={`text-[10px] font-bold ${styles.label} uppercase tracking-widest`}>{f.label}</label>
                <select value={f.val} onChange={(e) => f.set(e.target.value)} className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded-lg p-2 text-xs outline-none`}>
                  <option value="">-- Port --</option>
                  {f.opts.map((item) => (
                    <option key={item.id} value={item.id} disabled={isInterfaceUsed(f.node.id, item.id)} className={isInterfaceUsed(f.node.id, item.id) ? "text-slate-400" : styles.text}>
                      {item.name} {isInterfaceUsed(f.node.id, item.id) ? "(Used)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
        <div className={`p-4 ${styles.panelHeaderBg} flex justify-end gap-3 rounded-b-2xl border-t ${styles.panelBorder}`}>
          <button onClick={onCancel} className={`px-4 py-2 text-xs font-bold ${styles.textSubtle}`}>Cancel</button>
          <button disabled={!valid} onClick={() => onConfirm(sIf, tIf, cable)} className={`px-6 py-2 rounded-lg text-xs font-bold ${valid ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-500 cursor-not-allowed"}`}>
            {initialData?.existingId ? "Update Link" : "Establish Link"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SubnetCalculator = ({ onClose, initialPos, styles }) => {
  const [ip, setIp] = useState("192.168.1.1");
  const [cidr, setCidr] = useState(24);
  const [pos, setPos] = useState(initialPos);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const result = useMemo(() => {
    if (!ip.match(/^(\d{1,3}\.){3}\d{1,3}$/)) return null;
    const parts = ip.split(".").map(Number);
    if (parts.some(p => p < 0 || p > 255)) return null;
    const ipLong = (parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3];
    const maskLong = cidr === 0 ? 0 : ~0 << (32 - cidr);
    const netLong = ipLong & maskLong;
    const broadLong = netLong | ~maskLong;
    const longToIp = (l) => [(l >>> 24) & 255, (l >>> 16) & 255, (l >>> 8) & 255, l & 255].join(".");
    return {
      network: longToIp(netLong), broadcast: longToIp(broadLong), netmask: longToIp(maskLong),
      firstUsable: cidr >= 31 ? "N/A" : longToIp(netLong + 1), lastUsable: cidr >= 31 ? "N/A" : longToIp(broadLong - 1),
      hosts: Math.pow(2, 32 - cidr) - 2 > 0 ? Math.pow(2, 32 - cidr) - 2 : 0,
      isPrivate: (netLong >>> 0) >= ((10 << 24) >>> 0) && (broadLong >>> 0) <= (((10 << 24) | 0xffffff) >>> 0) || ((netLong >>> 0) >= (((172 << 24) | (16 << 16)) >>> 0) && (broadLong >>> 0) <= (((172 << 24) | (31 << 16) | 0xffff) >>> 0)) || ((netLong >>> 0) >= (((192 << 24) | (168 << 16)) >>> 0) && (broadLong >>> 0) <= (((192 << 24) | (168 << 16) | 0xffff) >>> 0))
    };
  }, [ip, cidr]);

  useEffect(() => {
    const move = (e) => isDragging && setPos({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    const stop = () => setIsDragging(false);
    if (isDragging) { window.addEventListener('mousemove', move); window.addEventListener('mouseup', stop); }
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', stop); };
  }, [isDragging, dragOffset]);

  // Touch handlers for dragging the calculator on mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragOffset({ x: touch.clientX - pos.x, y: touch.clientY - pos.y });
  };
  
  const handleTouchMove = (e) => {
    if (isDragging) {
      const touch = e.touches[0];
      setPos({ x: touch.clientX - dragOffset.x, y: touch.clientY - dragOffset.y });
    }
  };
  
  const handleTouchEnd = () => setIsDragging(false);

  return (
    <div className={`absolute z-[70] w-80 ${styles.panelBg} border border-indigo-500/50 rounded-xl shadow-2xl overflow-hidden`} style={{ left: pos.x, top: pos.y }}>
      <div 
        onMouseDown={(e) => { setIsDragging(true); setDragOffset({ x: e.clientX - pos.x, y: e.clientY - pos.y }); }} 
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
        className="bg-indigo-600 px-4 py-2 flex justify-between items-center cursor-move text-white font-bold text-sm touch-none">
        <span className="flex items-center gap-2"><Calculator size={14} /> Subnet Calculator</span>
        <button onClick={onClose}><X size={16} /></button>
      </div>
      <div className="p-4 space-y-4">
        {/* Validated IP Input */}
        <div>
            <label className={`text-[10px] font-bold ${styles.label} uppercase`}>IP Address</label>
            <input 
                inputMode="decimal" 
                type="text" 
                value={ip} 
                onChange={e => { if(isValidIpInput(e.target.value)) setIp(e.target.value); }} 
                className={`w-full ${styles.inputBg} border-2 border-blue-500/50 focus:border-blue-500 rounded px-3 py-2 text-sm font-mono outline-none shadow-sm transition-all`} 
            />
        </div>
        <div><label className={`text-[10px] font-bold ${styles.label} uppercase`}>Mask (/{cidr})</label><input type="range" min="0" max="32" value={cidr} onChange={e => setCidr(parseInt(e.target.value))} className="w-full accent-indigo-500" /></div>
        
        <div className="grid grid-cols-2 gap-3">
          {/* IP Type Indicator */}
          <div className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2 flex items-center justify-between`}>
              <span className={`text-[9px] ${styles.label} font-bold uppercase`}>Range Type</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1 select-text ${result?.isPrivate ? "text-orange-500 bg-orange-500/10" : "text-emerald-500 bg-emerald-500/10"}`}>
                  {result?.isPrivate ? <Lock size={10} /> : <Cloud size={10} />}
                  {result?.isPrivate ? "Private Network" : "Public Internet"}
              </span>
          </div>

          {[ { l: "Network", v: result?.network, c: "text-emerald-400" }, { l: "Broadcast", v: result?.broadcast, c: "text-orange-400" }, { l: "First IP", v: result?.firstUsable, c: "text-blue-400" }, { l: "Last IP", v: result?.lastUsable, c: "text-blue-400" } ].map((f, i) => (
            <div key={i} className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder}`}>
              <div className={`text-[9px] ${styles.label} font-bold uppercase`}>{f.l}</div>
              <input readOnly value={f.v || ""} className={`text-xs font-mono truncate ${f.c} bg-transparent w-full outline-none`} />
            </div>
          ))}
          <div className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2`}>
            <div className={`text-[9px] ${styles.label} font-bold uppercase`}>Hosts</div>
            <input readOnly value={result?.hosts.toLocaleString() || "0"} className={`text-sm font-bold ${styles.text} bg-transparent w-full outline-none`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQModal = React.memo(({ onClose, onRestartTutorial, styles, data, isDark }: any) => {
  const [openIndex, setOpenIndex] = useState<any>(null);

  return (
    <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onClose}>
      <div className={`${styles.modalBg} w-full max-w-lg p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`} onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-xl flex items-center gap-2"><HelpCircle className="text-blue-500" /> Help & Resources</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
        </div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          {(data || []).map((item, i) => (
            <div key={i} className={`border ${styles.panelBorder} rounded-xl overflow-hidden transition-all ${isDark ? "hover:border-white" : "hover:border-slate-400"}`}>
              <button 
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className={`w-full flex items-center justify-between p-4 text-left font-bold text-sm transition-colors ${openIndex === i ? "bg-blue-500/5 text-blue-500" : ""}`}
              >
                {item.question}
                {openIndex === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {openIndex === i && (
                <div className={`p-4 text-sm ${isDark ? "text-slate-300" : "text-slate-600"} border-t ${styles.panelBorder} leading-relaxed bg-opacity-50`}>
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
        <button onClick={onRestartTutorial} className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-500/20 transition-all active:scale-95">
          Restart Interactive Tutorial
        </button>
      </div>
    </div>
  );
});


const BackgroundSettingsModal = ({ onClose, isDark, styles, onUpload, onColorChange, onReset, currentColor }: any) => {
  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = useCallback((e: any) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: any) => { e.preventDefault(); setIsDragging(false); }, []);
  
  const handleFile = useCallback((file: any) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      alert("Invalid file type. Please upload .jpg, .png, or .bmp images.");
      return;
    }
    // Limit image files to 2MB (2 * 1024 * 1024 bytes) before parsing
    if (file.size > 2 * 1024 * 1024) {
      alert("Image is too large. Background image upload is limited to 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;

      // Enforce a strict regex for image URIs to block javascript: or text/html injection vectors
      const imageUriRegex = /^data:image\/(jpeg|png|bmp);base64,[A-Za-z0-9+/=]+$/;
      if (!imageUriRegex.test(dataUrl)) {
        alert("Image validation failed. Only standard base64 JPEG, PNG, or BMP images are allowed.");
        return;
      }

      // Memory Bomb Protection: load asynchronously into a detached, off-screen Image object
      const img = new Image();
      img.onload = () => {
        if (img.width > 4096 || img.height > 4096) {
          alert("Image dimensions exceed 4096x4096 pixels. Upload terminated to prevent browser tab-crashing decompression memory exploits.");
          return;
        }
        onUpload(dataUrl);
        onClose();
      };
      img.onerror = () => {
        alert("Failed to load image for dimension validation.");
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, [onUpload, onClose]);

  const handleDrop = useCallback((e: any) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileChange = useCallback((e: any) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onClose}>
      <div className={`${styles.modalBg} w-full max-w-md p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4 relative`} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><ImageIcon className="text-blue-500" /> Background Settings</h2>
        
        <div className="space-y-6">
          <div>
            <label className={`block text-sm font-bold mb-2 ${styles.label}`}>Background Color</label>
            <input 
              type="color" 
              value={currentColor || (isDark ? "#0f172a" : "#f8fafc")} 
              onChange={(e) => onColorChange(e.target.value)} 
              className="w-full h-10 rounded cursor-pointer" 
            />
          </div>
          
          <div>
            <label className={`block text-sm font-bold mb-2 ${styles.label}`}>Custom Image (.jpg, .png, .bmp)</label>
            <div 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-500/10' : `${styles.panelBorder} hover:border-blue-400`}`}
            >
              <ImageIcon className="mx-auto mb-2 text-slate-400" size={32} />
              <p className={`text-sm mb-4 ${styles.textSubtle}`}>Drag and drop an image here</p>
              <p className={`text-xs mb-4 ${styles.textSubtle}`}>or</p>
              <input 
                type="file" 
                id="modal-bg-upload" 
                accept=".jpg,.jpeg,.png,.bmp" 
                className="hidden" 
                onChange={handleFileChange} 
              />
              <button 
                onClick={() => document.getElementById('modal-bg-upload')?.click()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-sm shadow transition-colors"
              >
                Choose File...
              </button>
            </div>
          </div>
          
          <div>
            <button 
              onClick={() => { onReset(); onClose(); }}
              className={`w-full py-2.5 rounded-lg border border-red-500/50 text-red-500 hover:bg-red-500/10 font-bold text-sm transition-colors`}
            >
              Reset Background
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutModal = ({ onClose, onGetStarted, styles, isDark }) => {
  const [visibleLines, setVisibleLines] = useState(0);

  useEffect(() => {
    if (visibleLines < ABOUT_TEXT.length) {
      const timeout = setTimeout(() => {
        setVisibleLines(prev => prev + 1);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [visibleLines]);

  return (
    <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onClose}>
      <div className={`${styles.modalBg} w-full max-w-2xl p-8 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4 relative`} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
        <div className="space-y-6">
          {ABOUT_TEXT.map((line, i) => (
            <p key={i} className={`transition-opacity duration-1000 ${i < visibleLines ? 'opacity-100' : 'opacity-0'} ${i === 0 ? 'text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent mb-6' : 'text-sm leading-relaxed'}`}>
              {line}
            </p>
          ))}
        </div>
         {visibleLines === ABOUT_TEXT.length && (
            <div className="mt-8 flex justify-center animate-in fade-in slide-in-from-bottom-4 duration-1000">
                <button onClick={onGetStarted} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-lg transition-transform hover:scale-105">Get Started</button>
            </div>
         )}
      </div>
    </div>
  );
};

export const WhatsNewModal = ({ onClose, styles, isDark }: any) => {
  const items = [
    {
      icon: "↩️",
      badge: "History",
      title: "Undo & Redo History",
      desc: "Effortlessly revert or reapply changes with Ctrl+Z / Ctrl+Y keyboard shortcuts or toolbar buttons.",
    },
    {
      icon: "📑",
      badge: "Templates",
      title: "Topology Starter Templates",
      desc: "Instantly launch pre-configured Router-on-a-Stick, 3-Tier Enterprise, or SOHO WiFi diagrams.",
    },
    {
      icon: "🎨",
      badge: "1,600+ Icons",
      title: "1,600+ Brand Icon Library",
      desc: "Access AWS, Azure, GCP, and Cisco-style icons directly from the sidebar.",
    },
    {
      icon: "🖼️",
      badge: "Upload",
      title: "Custom Image Upload",
      desc: "Import your own PNG, JPEG, WebP, or SVG images (up to 2MB) onto the canvas.",
    },
    {
      icon: "💾",
      badge: "Offline",
      title: "JSON Project Backup",
      desc: "Save and load complete multi-tab project files locally with zero server storage.",
    },
    {
      icon: "📐",
      badge: "Shapes & Lines",
      title: "Enhanced Shape & Line Tool",
      desc: "Insert Squares, Circles, and rotatable Lines with customizable labels and colors.",
    },
  ];

  return (
    <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onClose}>
      <div className={`${styles.modalBg} w-full max-w-lg p-6 md:p-8 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4 relative`} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-500 flex items-center justify-center">
            <Sparkles size={24} className="text-amber-400 fill-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 bg-clip-text text-transparent">
              ✨ What's New in NetMapper
            </h2>
            <p className={`text-xs ${styles.textSubtle} mt-0.5`}>Explore our latest updates & capabilities</p>
          </div>
        </div>
        <div className="space-y-3.5">
          {items.map((item, idx) => (
            <div key={idx} className={`p-3.5 rounded-xl border ${styles.panelBorder} ${isDark ? "bg-slate-800/40" : "bg-slate-50"} flex items-start gap-3.5`}>
              <span className="text-2xl select-none leading-none pt-0.5">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-sm font-bold ${styles.text}`}>{item.title}</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/30">{item.badge}</span>
                </div>
                <p className={`text-xs ${styles.textSubtle} leading-relaxed`}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-xs font-bold shadow-lg shadow-amber-500/20 transition-transform hover:scale-105"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export const TemplateModal = ({ onClose, onSelectTemplate, styles, isDark }: any) => {
  return (
    <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onClose}>
      <div className={`${styles.modalBg} w-full max-w-3xl p-6 md:p-8 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4 relative max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-500 flex items-center justify-center">
            <LayoutTemplate size={24} className="text-blue-500" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${styles.text} flex items-center gap-2`}>
              Topology Starter Templates
            </h2>
            <p className={`text-xs ${styles.textSubtle} mt-0.5`}>Choose a pre-configured starter network architecture to launch in a new project tab</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STARTER_TEMPLATES.map((tmpl: any) => (
            <div
              key={tmpl.id}
              onClick={() => {
                onSelectTemplate(tmpl);
              }}
              className={`p-5 rounded-xl border ${styles.panelBorder} ${isDark ? "bg-slate-800/40 hover:bg-slate-800" : "bg-slate-50 hover:bg-blue-50/50"} cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg hover:border-blue-500/50 flex flex-col justify-between group`}
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/30">
                    {tmpl.tag}
                  </span>
                  <span className={`text-[11px] ${styles.textSubtle} font-mono flex items-center gap-1`}>
                    <Layers size={12} /> {tmpl.deviceCount} Devices
                  </span>
                </div>
                <h3 className={`text-base font-bold ${styles.text} group-hover:text-blue-500 transition-colors mb-2`}>
                  {tmpl.name}
                </h3>
                <p className={`text-xs ${styles.textSubtle} leading-relaxed`}>
                  {tmpl.description}
                </p>
              </div>
              <div className="mt-5 pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs font-semibold text-blue-500">
                <span>Load Template</span>
                <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Updated Sidebar for Mobile responsiveness and Tap-to-Add
const LeftSidebar = React.memo(({ onAddNode, styles, onShowFAQ, onShowAbout, isOpen, toggleSidebar, selectedBrand, onBrandChange, onImportImage, onOpenIconModal }: any) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      alert("Image size exceeds 2MB limit.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Data = event.target?.result as string;
      if (base64Data && onImportImage) {
        onImportImage(base64Data, file);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
  <div id="tutorial-sidebar" className={`
    fixed md:relative z-30 h-full w-64 ${styles.panelBg} ${styles.panelBorder} border-r flex flex-col shadow-xl transition-transform duration-300 transform 
    ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
  `}>
    <div className={`p-4 ${styles.panelBorder} border-b flex justify-between items-center`}>
       <div>
         <h1 className="text-lg font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">NetMapper</h1>
         <p className={`text-[10px] ${styles.label} font-mono tracking-widest uppercase mt-1`}>Infrastructure Builder</p>
       </div>
       <button onClick={toggleSidebar} className="md:hidden p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X size={20}/></button>
    </div>
    <div className="flex-1 overflow-y-auto p-4 space-y-6">
      <div className="mb-4">
        <label className={`text-[10px] font-bold ${styles.label} uppercase tracking-widest mb-1 block`}>Icon Brand</label>
        <select
          value={selectedBrand}
          onChange={(e) => onBrandChange(e.target.value)}
          className={`w-full p-2 text-xs rounded-lg ${styles.inputBg} border ${styles.inputBorder} ${styles.textBase} focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer appearance-none`}
        >
          <option value="default">Default (NetMapper)</option>
          <option value="aws">AWS</option>
          <option value="azure">Microsoft Azure</option>
          <option value="gcp">Google Cloud</option>
        </select>
        <button
          type="button"
          onClick={onOpenIconModal}
          className="w-full mt-2 py-2 px-3 rounded-lg border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-semibold flex items-center justify-center gap-2 transition-all shadow-sm"
        >
          <Layers size={14} />
          <span>Browse Icon Library (1,600+)</span>
        </button>
      </div>
      {["INFRA", "END_USER"].map(cat => (
        <div key={cat}>
          <h3 className={`text-[10px] font-bold ${styles.label} uppercase tracking-widest mb-3`}>{cat === "INFRA" ? "Infrastructure" : "End User Devices"}</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(DEVICE_TYPES).filter(([_, d]) => d.category === cat).map(([k, d]) => {
              const hasBrandEquivalent = selectedBrand !== 'default' && BRAND_LABELS[selectedBrand]?.[k];
              return (
              <React.Fragment key={k}>
                {(selectedBrand === 'default' || !hasBrandEquivalent) && (
                  <div 
                    draggable 
                    onDragStart={(e) => { e.dataTransfer.setData("deviceType", k); e.dataTransfer.setData("deviceBrand", 'default'); e.dataTransfer.effectAllowed = "move"; }} 
                    onClick={() => { onAddNode(k, 'default'); if(window.innerWidth < 768) toggleSidebar(); }} 
                    className={`flex flex-col items-center justify-center p-3 rounded-xl ${styles.cardBg} border ${styles.panelBorder} hover:border-blue-500/50 ${styles.buttonHover} transition-all cursor-grab active:cursor-grabbing group`}
                  >
                    <BrandIcon type={k} brand='default' size={24} className={`${d.color} mb-2 group-hover:scale-110 transition-transform`} />
                    <span className={`text-[10px] font-bold ${styles.label} uppercase tracking-tighter text-center`}>{d.label}</span>
                  </div>
                )}
                {hasBrandEquivalent && (
                  <div 
                    draggable 
                    onDragStart={(e) => { e.dataTransfer.setData("deviceType", k); e.dataTransfer.setData("deviceBrand", selectedBrand); e.dataTransfer.effectAllowed = "move"; }} 
                    onClick={() => { onAddNode(k, selectedBrand); if(window.innerWidth < 768) toggleSidebar(); }} 
                    className={`flex flex-col items-center justify-center p-3 rounded-xl ${styles.cardBg} border ${styles.panelBorder} hover:border-blue-500/50 ${styles.buttonHover} transition-all cursor-grab active:cursor-grabbing group`}
                  >
                    <BrandIcon type={k} brand={selectedBrand} size={24} className={`${d.color} mb-2 group-hover:scale-110 transition-transform`} />
                    <span className={`text-[10px] font-bold ${styles.label} uppercase tracking-tighter text-center`}>{BRAND_LABELS[selectedBrand][k]}</span>
                  </div>
                )}
              </React.Fragment>
            )})}
          </div>
        </div>
      ))}
      <div>
        <h3 className={`text-[10px] font-bold ${styles.label} uppercase tracking-widest mb-3`}>Assets</h3>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png, image/jpeg, image/webp, image/svg+xml"
          className="hidden"
          onChange={handleFileChange}
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              fileInputRef.current?.click();
              if (window.innerWidth < 768) toggleSidebar();
            }}
            className={`col-span-2 flex flex-col items-center justify-center p-3 rounded-xl ${styles.cardBg} border ${styles.panelBorder} hover:border-blue-500/50 ${styles.buttonHover} transition-all cursor-pointer group`}
          >
            <ImageIcon size={24} className="text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
            <span className={`text-[10px] font-bold ${styles.label} uppercase tracking-tighter text-center`}>Import Image</span>
          </button>
        </div>
      </div>
    </div>
    <div className={`p-4 ${styles.panelBorder} border-t ${styles.panelBg} space-y-2`}>
        <button onClick={onShowAbout} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg ${styles.inputBg} ${styles.panelBorder} border ${styles.textSubtle} hover:text-blue-500 font-bold text-xs uppercase tracking-wider`}><Info size={16} /> About</button>
        <button id="tutorial-faq" onClick={onShowFAQ} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg ${styles.inputBg} ${styles.panelBorder} border ${styles.textSubtle} hover:text-blue-500 font-bold text-xs uppercase tracking-wider`}><HelpCircle size={16} /> Help & FAQ</button>
    </div>
  </div>
  );
});

export const TopToolbar = React.memo(({ mode, setMode, showCalc, setShowCalc, zoom, setZoom, isExporting, onExport, onPrint, isFullScreen, onToggleFullScreen, styles, isDark, connectSourceId, isPanning, toggleSidebar, onDeselectSource, onUploadBackground, onSaveLayout, onOpenLayout, onAddShape, onShowWhatsNew, onUndo, onRedo, history, onOpenTemplates }: any) => {
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const shapeMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showShapeMenu) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (shapeMenuRef.current && !shapeMenuRef.current.contains(e.target as Node)) {
        setShowShapeMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showShapeMenu]);

  return (
    <div className={`h-14 ${styles.panelBorder} border-b ${isDark ? "bg-slate-900/50" : "bg-white/50"} backdrop-blur-sm flex items-center px-4 md:px-6 justify-between z-40 overflow-visible gap-4`}>
      <div className="flex items-center gap-2">
        <button onClick={toggleSidebar} className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><Menu size={20} className={styles.textSubtle}/></button>
        <div id="tutorial-toolbar" className="flex items-center space-x-2">
          {[ { m: "SELECT", i: MousePointer2, l: "Select" }, { m: "PAN", i: Hand, l: "Pan" }, { m: "CONNECT", i: Cable, l: "Link" }, { m: "TEXT", i: Type, l: "Text" } ].map(tool => (
            <button key={tool.m} onClick={() => { setMode(tool.m); onDeselectSource(); }} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${mode === tool.m ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : `${styles.textSubtle} ${styles.buttonHover} ${styles.text}`}`}>
              <tool.i size={16} /> <span className="hidden sm:inline">{tool.l}</span>
            </button>
          ))}
          <div className="relative" ref={shapeMenuRef}>
            <button
              onClick={() => setShowShapeMenu(prev => !prev)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${showShapeMenu ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : `${styles.textSubtle} ${styles.buttonHover} ${styles.text}`}`}
            >
              <Square size={16} /> <span className="hidden sm:inline">Shape</span> <ChevronDown size={14} />
            </button>
            {showShapeMenu && (
              <div className={`absolute top-full mt-2 left-0 w-32 py-1 rounded-lg border ${styles.panelBorder} ${styles.panelBg} shadow-xl z-[100]`}>
                <button
                  onClick={() => { onAddShape("RECTANGLE"); setShowShapeMenu(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm ${styles.text} hover:bg-blue-500/10 hover:text-blue-500 text-left`}
                >
                  <Square size={16} /> Square
                </button>
                <button
                  onClick={() => { onAddShape("CIRCLE"); setShowShapeMenu(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm ${styles.text} hover:bg-blue-500/10 hover:text-blue-500 text-left`}
                >
                  <CircleIcon size={16} /> Circle
                </button>
                <button
                  onClick={() => { onAddShape("LINE"); setShowShapeMenu(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm ${styles.text} hover:bg-blue-500/10 hover:text-blue-500 text-left`}
                >
                  <Minus size={16} /> Line
                </button>
              </div>
            )}
          </div>
          <div className={`h-6 w-px ${isDark ? "bg-slate-700" : "bg-slate-300"} mx-2`} />
          <button id="tutorial-calc" onClick={() => setShowCalc(!showCalc)} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${showCalc ? "bg-indigo-600 text-white shadow-lg" : `${styles.textSubtle} ${styles.buttonHover} ${styles.text}`}`}><Calculator size={16} /> <span className="hidden sm:inline">Calc</span></button>
          <button onClick={onUploadBackground} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${styles.textSubtle} ${styles.buttonHover} ${styles.text}`}><ImageIcon size={16} /> <span className="hidden sm:inline">Background</span></button>
          <button onClick={onOpenTemplates} className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${styles.textSubtle} ${styles.buttonHover} ${styles.text}`}><LayoutTemplate size={16} /> <span className="hidden sm:inline">Templates</span></button>
          <button onClick={onShowWhatsNew} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-all whitespace-nowrap"><Sparkles size={14} className="text-amber-400 fill-amber-400" /> <span className="hidden md:inline">What's New</span>!</button>
          {mode === "CONNECT" && connectSourceId && <span className="text-[10px] font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-emerald-500/20 whitespace-nowrap">Target...</span>}
          {isPanning && <span className="flex items-center gap-2 text-[10px] font-bold text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-lg border border-blue-500/20 whitespace-nowrap"><Move size={12} /> Panning</span>}
        </div>
      </div>
      <div id="tutorial-controls" className={`flex items-center space-x-2 ${isDark ? "bg-slate-950/50" : "bg-slate-100/50"} px-3 py-1 rounded-full border ${styles.panelBorder} shrink-0`}>
        <button onClick={onUndo} disabled={!history || history.past.length === 0} title="Undo (Ctrl+Z)" className={`p-1 ${!history || history.past.length === 0 ? "opacity-30 cursor-not-allowed" : `${styles.textSubtle} hover:text-blue-500`}`}><RotateCcw size={16} /></button>
        <button onClick={onRedo} disabled={!history || history.future.length === 0} title="Redo (Ctrl+Y)" className={`p-1 ${!history || history.future.length === 0 ? "opacity-30 cursor-not-allowed" : `${styles.textSubtle} hover:text-blue-500`}`}><RotateCw size={16} /></button>
        <div className={`h-4 w-px ${isDark ? "bg-slate-700" : "bg-slate-300"} mx-1`} />
        <button onClick={() => setZoom((z: number) => Math.max(0.1, z - 0.1))} title="Zoom Out" className={`p-1 ${styles.textSubtle} hover:text-blue-500`}><ZoomOut size={16} /></button>
        <span className={`text-[10px] ${styles.textSubtle} w-10 text-center font-mono`}>{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom((z: number) => Math.min(2, z + 0.1))} title="Zoom In" className={`p-1 ${styles.textSubtle} hover:text-blue-500`}><ZoomIn size={16} /></button>
        <div className={`h-4 w-px ${isDark ? "bg-slate-700" : "bg-slate-300"} mx-1`} />
        <button onClick={onPrint} disabled={isExporting} title="Print Diagram" className={`p-1 ${isExporting ? "text-blue-500 animate-pulse" : `${styles.textSubtle} hover:text-blue-500`}`}><Printer size={16} /></button>
        <button onClick={onExport} disabled={isExporting} title="Download Image (JPEG)" className={`p-1 ${isExporting ? "text-blue-500 animate-pulse" : `${styles.textSubtle} hover:text-blue-500`}`}><Download size={16} /></button>
        <button onClick={onSaveLayout} title="Export Project" className={`p-1 ${styles.textSubtle} hover:text-blue-500`}><Save size={16} /></button>
        <button onClick={onOpenLayout} title="Import Project" className={`p-1 ${styles.textSubtle} hover:text-blue-500`}><FolderOpen size={16} /></button>
        <button onClick={onToggleFullScreen} title={isFullScreen ? "Exit Full Screen" : "Full Screen"} className={`p-1 ml-1 ${styles.textSubtle} hover:text-blue-500`}>{isFullScreen ? <Minimize size={16} /> : <Maximize size={16} />}</button>
      </div>
    </div>
  );
});

const SnapWidget = ({ value, onChange }) => {
    const points = [
        { id: 'tl', top: '-4px', left: '-4px' },
        { id: 'tc', top: '-4px', left: '16px' },
        { id: 'tr', top: '-4px', left: '36px' },
        { id: 'rc', top: '16px', left: '36px' },
        { id: 'br', top: '36px', left: '36px' },
        { id: 'bc', top: '36px', left: '16px' },
        { id: 'bl', top: '36px', left: '-4px' },
        { id: 'lc', top: '16px', left: '-4px' },
    ];
    return (
        <div className="relative w-10 h-10 border-2 border-slate-500 rounded bg-slate-800 cursor-pointer" onClick={() => onChange(null)}>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Server size={16} className="text-slate-500" />
            </div>
            {points.map(p => (
                <div 
                    key={p.id}
                    onClick={(e) => { e.stopPropagation(); onChange(p.id); }}
                    className={`absolute w-3 h-3 rounded-full border-2 border-slate-800 ${value === p.id ? 'bg-emerald-500 z-10' : 'bg-yellow-400 hover:bg-yellow-300'} cursor-pointer`}
                    style={{ top: p.top, left: p.left }}
                />
            ))}
        </div>
    )
}

const PropertiesPanel = React.memo(({ selectedIds, nodes, connections, setNodes, setConnections, deleteSelection, clipboard, copyToClipboard, pasteFromClipboard, styles, isDark, isOpen, togglePanel, onDeleteInterface }: any) => {
  const selectedNode = useMemo(() => selectedIds.length === 1 && nodes.find(n => n.id === selectedIds[0]), [selectedIds, nodes]);
  const selectedConnection = useMemo(() => selectedIds.length === 1 && connections.find(c => c.id === selectedIds[0]), [selectedIds, connections]);

  // Helper to update interface safely
  const updateInterface = (nodeId, ifId, field, value) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? {
      ...n,
      interfaces: n.interfaces.map(iface => 
        iface.id === ifId ? { ...iface, [field]: value } : 
        { ...iface, subInterfaces: iface.subInterfaces?.map(sub => sub.id === ifId ? { ...sub, [field]: value } : sub) || [] }
      )
    } : n));
  };

  const isPanelVisible = isOpen || (window.innerWidth >= 768 && selectedIds.length > 0);
  const showPlaceholder = selectedIds.length === 0 && window.innerWidth >= 768;

  return (
    <div className={`
       fixed md:relative right-0 z-[90] h-full w-80 ${styles.panelBg} border-l ${styles.panelBorder} flex flex-col shadow-xl transition-all duration-300 transform
       ${isPanelVisible ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
       ${showPlaceholder ? '-mr-80' : 'mr-0'}
    `}>
      {showPlaceholder ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 text-center p-8 space-y-4">
          <div className={`w-16 h-16 rounded-full ${styles.inputBg} border ${styles.inputBorder} flex items-center justify-center`}>
            <MousePointer2 size={24} />
          </div>
          <p className="text-xs font-bold uppercase tracking-widest">Select an item</p>
        </div>
      ) : (
        <>
          <div className={`p-4 border-b ${styles.panelBorder} flex justify-between items-center ${styles.panelHeaderBg}`}>
        <h2 className="font-bold text-xs uppercase tracking-widest flex items-center gap-2">{selectedIds.length > 1 ? <Layers size={14} /> : selectedNode ? <Settings size={14} /> : <Cable size={14} />} {selectedIds.length > 1 ? "Multi-Select" : selectedNode ? "Properties" : "Connection"}</h2>
        <button onClick={togglePanel} className="md:hidden p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16}/></button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
        {selectedIds.length > 1 ? (
           <div className="space-y-4">
             <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 text-center"><div className="text-3xl font-black text-blue-400">{selectedIds.length}</div><div className="text-[9px] text-blue-300 uppercase font-bold">Items Selected</div></div>
             <div className="space-y-2 max-h-60 overflow-y-auto pr-1">{selectedIds.map(id => { const n = nodes.find(n => n.id === id); return n ? <div key={id} className={`text-xs p-2 rounded ${styles.inputBg} border ${styles.inputBorder} truncate`}>{n.name || n.label || "Item"}</div> : null; })}</div>
           </div>
        ) : selectedNode ? (
          <>
             {selectedNode.type === "SHAPE" ? (
               <div className="space-y-4">
                 <div className="flex gap-2">
                   {["RECTANGLE", "CIRCLE", "LINE"].map(t => <button key={t} onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, shapeType: t } : n))} className={`flex-1 py-2 rounded border ${selectedNode.shapeType === t ? "bg-blue-500/20 border-blue-500 text-blue-400" : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle}`}`}>{t === "RECTANGLE" ? <Square size={16} className="mx-auto" /> : t === "CIRCLE" ? <CircleIcon size={16} className="mx-auto" /> : <Minus size={16} className="mx-auto" />}</button>)}
                 </div>
                 {/* Security Fix 2: Added maxLength to Label input */}
                 <div><label className={`text-[10px] font-bold ${styles.label} uppercase`}>Label</label><input maxLength={30} value={selectedNode.label || ""} onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, label: e.target.value } : n))} className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none`} /></div>
                 
                 {/* Label Controls: Position & Size */}
                 {selectedNode.shapeType !== "LINE" && (
                   <div className="grid grid-cols-2 gap-2">
                      <div>
                         <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Position</label>
                         <select 
                             value={selectedNode.labelPosition || "center"} 
                             onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, labelPosition: e.target.value } : n))}
                             className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none mt-1 capitalize`}
                         >
                             <option value="top">Top</option>
                             <option value="center">Center</option>
                             <option value="bottom">Bottom</option>
                         </select>
                      </div>
                      <div>
                         <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Size ({selectedNode.labelSize || 14}px)</label>
                         <input 
                             type="range" min="10" max="72" 
                             value={selectedNode.labelSize || 14} 
                             onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, labelSize: parseInt(e.target.value) } : n))} 
                             className="w-full accent-blue-500 mt-2" 
                         />
                      </div>
                   </div>
                 )}

                 <div><label className={`text-[10px] font-bold ${styles.label} uppercase`}>Color</label><div className="grid grid-cols-5 gap-2 mt-1">{["#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b", "#ffffff", "#000000"].map(c => <button key={c} onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, color: c } : n))} className={`w-6 h-6 rounded-full border-2 ${selectedNode.color === c ? "border-slate-400" : "border-transparent"}`} style={{ backgroundColor: c }} />)}</div></div>

                 {/* Text Color Picker for Shape Labels */}
                 <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Text Color</label>
                    <div className="grid grid-cols-5 gap-2 mt-1">
                        {["#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b", "#ffffff", "#000000"].map(c => (
                            <button 
                                key={c} 
                                onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, textColor: c } : n))} 
                                className={`w-6 h-6 rounded-full border-2 ${selectedNode.textColor === c ? "border-slate-400" : "border-transparent"}`} 
                                style={{ backgroundColor: c }} 
                            />
                        ))}
                        {/* Reset Button to restore default */}
                        <button 
                            onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, textColor: undefined } : n))}
                            className={`col-span-5 text-[10px] ${styles.textSubtle} hover:text-blue-500 flex items-center justify-center gap-1 py-1`}
                        >
                             <Unplug size={10} /> Reset Text Color
                        </button>
                    </div>
                 </div>
                 
                 {/* Border Style Selector for Shapes */}
                 <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Border Style</label>
                    <select 
                        value={selectedNode.borderStyle || "solid"} 
                        onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, borderStyle: e.target.value } : n))}
                        className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none mt-1 capitalize`}
                    >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                        <option value="double">Double</option>
                    </select>
                 </div>
               </div>
             ) : selectedNode.type === "TEXT" ? (
                <div className="space-y-4">
                  {/* Security Fix 2: Added maxLength to Textarea input */}
                  <textarea maxLength={500} value={selectedNode.content} onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, content: e.target.value } : n))} className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-3 py-2 text-sm outline-none h-24`} />
                  <input type="range" min="10" max="72" value={selectedNode.fontSize} onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, fontSize: parseInt(e.target.value) } : n))} className="w-full accent-blue-500" />
                  
                  {/* Text Color Picker */}
                  <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Text Color</label>
                    <div className="grid grid-cols-5 gap-2 mt-1">
                        {["#1e293b", "#cbd5e1", "#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b"].map(c => (
                            <button 
                                key={c} 
                                onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, color: c } : n))} 
                                className={`w-6 h-6 rounded-full border-2 ${selectedNode.color === c ? "border-slate-400" : "border-transparent"}`} 
                                style={{ backgroundColor: c }} 
                            />
                        ))}
                         <button 
                            onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, color: undefined } : n))}
                            className={`col-span-5 text-[10px] ${styles.textSubtle} hover:text-blue-500 flex items-center justify-center gap-1 py-1`}
                        >
                             <Unplug size={10} /> Reset Color
                        </button>
                    </div>
                  </div>

                  {/* Border Style Selector */}
                  <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Border Style</label>
                    <select 
                        value={selectedNode.borderStyle || "none"} 
                        onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, borderStyle: e.target.value } : n))}
                        className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none mt-1 capitalize`}
                    >
                        <option value="none">None</option>
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                    </select>
                  </div>

                   {/* Border Color Picker (Conditional) */}
                   {(selectedNode.borderStyle && selectedNode.borderStyle !== 'none') && (
                      <div className="animate-in fade-in slide-in-from-top-1">
                        <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Border Color</label>
                        <div className="grid grid-cols-5 gap-2 mt-1">
                            {["#1e293b", "#cbd5e1", "#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b"].map(c => (
                                <button 
                                    key={c} 
                                    onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, borderColor: c } : n))} 
                                    className={`w-6 h-6 rounded-full border-2 ${selectedNode.borderColor === c ? "border-slate-400" : "border-transparent"}`} 
                                    style={{ backgroundColor: c }} 
                                />
                            ))}
                        </div>
                      </div>
                   )}

                </div>
             ) : selectedNode.type === "IMAGE" ? (
                <div className="space-y-4">
                  <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase block mb-1`}>File Name</label>
                    <div className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-3 py-2 text-xs truncate ${styles.textSubtle}`}>
                      {selectedNode.fileName || "Uploaded Image"}
                    </div>
                  </div>
                  <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase block mb-1`}>Aspect Ratio</label>
                    <select
                      value={selectedNode.objectFit || "contain"}
                      onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, objectFit: e.target.value } : n))}
                      className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none capitalize`}
                    >
                      <option value="contain">object-contain</option>
                      <option value="cover">object-cover</option>
                      <option value="fill">object-fill</option>
                    </select>
                  </div>
                </div>
             ) : (
               <div className="space-y-4">
                 {/* Security Fix 2: Added maxLength to Hostname input */}
                 <div><label className={`text-[10px] font-bold ${styles.label} uppercase`}>Hostname</label><input maxLength={30} value={selectedNode.name} onChange={e => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, name: e.target.value } : n))} className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none`} /></div>
                 
                 {/* Icon Color Picker for Devices */}
                 <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Icon Color</label>
                    <div className="grid grid-cols-5 gap-2 mt-1">
                        {["#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b", "#ffffff", "#000000"].map(c => (
                            <button 
                                key={c} 
                                onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, color: c } : n))} 
                                className={`w-6 h-6 rounded-full border-2 ${selectedNode.color === c ? "border-slate-400" : "border-transparent"}`} 
                                style={{ backgroundColor: c }} 
                            />
                        ))}
                        {/* Reset Button to restore default */}
                        <button 
                            onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, color: undefined } : n))}
                            className={`col-span-5 text-[10px] ${styles.textSubtle} hover:text-blue-500 flex items-center justify-center gap-1 py-1`}
                        >
                             <Unplug size={10} /> Reset to Default
                        </button>
                    </div>
                 </div>

                 {/* Text Color Picker for Device Labels */}
                 <div>
                    <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Text Color</label>
                    <div className="grid grid-cols-5 gap-2 mt-1">
                        {["#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b", "#ffffff", "#000000"].map(c => (
                            <button 
                                key={c} 
                                onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, textColor: c } : n))} 
                                className={`w-6 h-6 rounded-full border-2 ${selectedNode.textColor === c ? "border-slate-400" : "border-transparent"}`} 
                                style={{ backgroundColor: c }} 
                            />
                        ))}
                        {/* Reset Button to restore default */}
                        <button 
                            onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, textColor: undefined } : n))}
                            className={`col-span-5 text-[10px] ${styles.textSubtle} hover:text-blue-500 flex items-center justify-center gap-1 py-1`}
                        >
                             <Unplug size={10} /> Reset Text Color
                        </button>
                    </div>
                 </div>

                 <div>
                    <div className="flex justify-between items-center mb-2"><label className={`text-[10px] font-bold ${styles.label} uppercase`}>Interfaces</label>
                      <div className="flex items-center gap-2">
                        {selectedNode.type === "AP" && (
                          <button onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, interfaces: [...(n.interfaces || []), { id: generateId("if"), name: `SSID_${(n.interfaces || []).filter(i => i.type === "SSID").length + 1}`, type: "SSID", ip: "", subInterfaces: [] }] } : n))} className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><Plus size={10} /> SSID</button>
                        )}
                        <button onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, interfaces: [...(n.interfaces || []), { id: generateId("if"), name: `eth${(n.interfaces || []).filter(i => i.type === "PHYSICAL").length}`, type: "PHYSICAL", ip: "", subInterfaces: [] }] } : n))} className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1"><Plus size={10} /> Add</button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(selectedNode.interfaces || []).map(iface => (
                        <div key={iface.id} className={`pl-2 border-l-2 ${iface.type === "SSID" ? "border-emerald-500" : styles.panelBorder}`}>
                           <div className="flex gap-1 items-center mb-1">
                             {/* Security Fix 2: Added maxLength to Interface Name & IP inputs */}
                             <input maxLength={15} value={iface.name} onChange={e => updateInterface(selectedNode.id, iface.id, "name", e.target.value)} className={`w-16 ${styles.inputBg} border ${styles.inputBorder} rounded px-1 py-0.5 text-[10px]`} />
                             {/* Security Fix 2 & Strict IP Validation & Mobile Keyboard */}
                             <input 
                                maxLength={15} 
                                inputMode="decimal"
                                type="text"
                                value={iface.ip} 
                                placeholder="IP Addr" 
                                onChange={e => {
                                  if (isValidIpInput(e.target.value)) {
                                    updateInterface(selectedNode.id, iface.id, "ip", e.target.value);
                                  }
                                }} 
                                className={`flex-1 ${styles.inputBg} border ${styles.inputBorder} rounded px-1 py-0.5 text-[10px] font-mono`} 
                             />
                             {iface.type === "PHYSICAL" && ["ROUTER", "SWITCH"].includes(selectedNode.type) && <button onClick={() => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, interfaces: n.interfaces.map(i => i.id === iface.id ? { ...i, subInterfaces: [...(i.subInterfaces || []), { id: generateId("sub"), name: `${i.name}.${(i.subInterfaces?.length + 1) * 10}`, vlan: "10", ip: "" }] } : i) } : n))} className="text-emerald-500 p-0.5"><Layers size={10} /></button>}
                             <button onClick={() => onDeleteInterface(selectedNode.id, iface.id)} className="text-slate-500 hover:text-red-500 p-0.5"><Trash2 size={10} /></button>
                           </div>
                           {/* Sub Interfaces */}
                           {iface.subInterfaces?.map(sub => (
                             <div key={sub.id} className="flex gap-1 items-center ml-2 mt-1 relative">
                               <div className={`absolute -left-2 top-1/2 w-1.5 h-px bg-slate-500`}/>
                               <div className="flex items-center gap-1">
                                <span className="text-[8px] text-purple-400">VLAN</span>
                                {/* VLAN Validation & Editing: Converted Span to Input with Strict Validation & Mobile Keybaord */}
                                <input 
                                  maxLength={4} 
                                  inputMode="decimal"
                                  type="text"
                                  value={sub.vlan} 
                                  onChange={e => {
                                    if(isValidVlanInput(e.target.value)) {
                                      updateInterface(selectedNode.id, sub.id, "vlan", e.target.value);
                                    }
                                  }} 
                                  className={`w-8 ${styles.inputBg} border ${styles.inputBorder} rounded px-1 py-0.5 text-[8px] font-mono text-purple-400`} 
                                />
                               </div>
                               {/* Security Fix 2 & Strict IP Validation */}
                               <input 
                                  maxLength={15} 
                                  inputMode="decimal"
                                  type="text"
                                  value={sub.ip} 
                                  onChange={e => {
                                    if (isValidIpInput(e.target.value)) {
                                      updateInterface(selectedNode.id, sub.id, "ip", e.target.value);
                                    }
                                  }} 
                                  className={`flex-1 ${styles.inputBg} border ${styles.inputBorder} rounded px-1 py-0.5 text-[10px] font-mono`} 
                                />
                               <button onClick={() => onDeleteInterface(selectedNode.id, sub.id, true, iface.id)} className="text-red-400 p-0.5"><Trash2 size={10} /></button>
                             </div>
                           ))}
                        </div>
                      ))}
                    </div>
                 </div>
               </div>
             )}
          </>
        ) : selectedConnection ? (
          <div className="space-y-4">
             <div className={`${styles.inputBg} p-4 rounded-xl border ${styles.inputBorder} flex items-center gap-3`}>
               <div className={`w-8 h-8 rounded bg-slate-800 flex items-center justify-center`}>{React.createElement(CABLE_TYPES[selectedConnection.cable].icon, { size: 16, color: CABLE_TYPES[selectedConnection.cable].color })}</div>
               <div><div className={`text-[9px] ${styles.label} uppercase font-bold`}>Type</div><div className="text-sm font-medium">{CABLE_TYPES[selectedConnection.cable].label}</div></div>
             </div>
             <div className="grid grid-cols-2 gap-2 text-xs">
                <div><div className={`text-[9px] ${styles.label} uppercase font-bold`}>Source</div>{nodes.find(n => n.id === selectedConnection.from)?.name}</div>
                <div><div className={`text-[9px] ${styles.label} uppercase font-bold`}>Target</div>{nodes.find(n => n.id === selectedConnection.to)?.name}</div>
             </div>
             
             {/* Interface Label Toggles */}
             <div className="space-y-1">
               <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Interface Labels</label>
               <div className="grid grid-cols-2 gap-2">
                   <button
                      onClick={() => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, showSourceLabel: c.showSourceLabel === false ? true : false } : c))}
                      className={`flex items-center justify-center gap-2 px-2 py-2 rounded-lg border text-[10px] font-bold transition-all ${selectedConnection.showSourceLabel !== false ? "bg-blue-500/10 border-blue-500/50 text-blue-500" : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle}`}`}
                   >
                      {selectedConnection.showSourceLabel !== false ? <Eye size={12} /> : <EyeOff size={12} />} Source
                   </button>
                   <button
                      onClick={() => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, showTargetLabel: c.showTargetLabel === false ? true : false } : c))}
                      className={`flex items-center justify-center gap-2 px-2 py-2 rounded-lg border text-[10px] font-bold transition-all ${selectedConnection.showTargetLabel !== false ? "bg-blue-500/10 border-blue-500/50 text-blue-500" : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle}`}`}
                   >
                      {selectedConnection.showTargetLabel !== false ? <Eye size={12} /> : <EyeOff size={12} />} Target
                   </button>
               </div>
             </div>

             {/* Text Color Picker for Link Labels */}
             <div>
                <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Label Color</label>
                <div className="grid grid-cols-5 gap-2 mt-1">
                    {["#3b82f6", "#ef4444", "#f97316", "#eab308", "#22c55e", "#a855f7", "#ec4899", "#64748b", "#ffffff", "#000000"].map(c => (
                        <button 
                            key={c} 
                            onClick={() => setConnections(prev => prev.map(conn => conn.id === selectedConnection.id ? { ...conn, textColor: c } : conn))} 
                            className={`w-6 h-6 rounded-full border-2 ${selectedConnection.textColor === c ? "border-slate-400" : "border-transparent"}`} 
                            style={{ backgroundColor: c }} 
                        />
                    ))}
                    {/* Reset Button to restore default */}
                    <button 
                        onClick={() => setConnections(prev => prev.map(conn => conn.id === selectedConnection.id ? { ...conn, textColor: undefined } : conn))}
                        className={`col-span-5 text-[10px] ${styles.textSubtle} hover:text-blue-500 flex items-center justify-center gap-1 py-1`}
                    >
                         <Unplug size={10} /> Reset Label Color
                    </button>
                </div>
             </div>
             {/* Line Style Selector */}
             <div className="space-y-1">
                <label className={`text-[10px] font-bold ${styles.label} uppercase`}>Line Style</label>
                <select 
                  value={selectedConnection.lineStyle || "DEFAULT"} 
                  onChange={e => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, lineStyle: e.target.value === "DEFAULT" ? undefined : e.target.value } : c))}
                  className={`w-full ${styles.inputBg} border ${styles.inputBorder} rounded px-2 py-1.5 text-xs outline-none`}
                >
                  <option value="DEFAULT">Default (By Type)</option>
                  {Object.entries(LINE_STYLES).map(([k, v]) => (
                     <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
             </div>

             {/* Animation Toggle */}
             <div className={`${styles.inputBg} p-3 rounded-xl border ${styles.inputBorder} flex items-center justify-between`}>
                 <span className={`text-[9px] ${styles.label} uppercase font-bold`}>Animate Link</span>
                 <button 
                    onClick={() => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, animated: !c.animated } : c))}
                    className={`transition-colors ${selectedConnection.animated ? "text-emerald-500" : "text-slate-400"}`}
                 >
                    {selectedConnection.animated ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                 </button>
             </div>

             {/* Link Snapping Toggle */}
             <div className={`${styles.inputBg} p-3 rounded-xl border ${styles.inputBorder} flex items-center justify-between`}>
                 <span className={`text-[9px] ${styles.label} uppercase font-bold`}>Link Snapping</span>
                 <button 
                    onClick={() => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, snappingEnabled: !c.snappingEnabled, fromSnap: undefined, toSnap: undefined } : c))}
                    className={`transition-colors ${selectedConnection.snappingEnabled ? "text-emerald-500" : "text-slate-400"}`}
                 >
                    {selectedConnection.snappingEnabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                 </button>
             </div>

             {selectedConnection.snappingEnabled && (
                 <div className={`${styles.inputBg} p-4 rounded-xl border ${styles.inputBorder} flex flex-col gap-4`}>
                     <div className="text-[10px] font-bold uppercase text-center mb-2">Snap Points</div>
                     <div className="flex justify-between items-center">
                         <div className="flex flex-col items-center gap-2">
                             <span className="text-[9px] text-slate-500 uppercase">Source</span>
                             <SnapWidget value={selectedConnection.fromSnap} onChange={(val) => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, fromSnap: val } : c))} />
                         </div>
                         <div className="flex flex-col items-center gap-2">
                             <span className="text-[9px] text-slate-500 uppercase">Target</span>
                             <SnapWidget value={selectedConnection.toSnap} onChange={(val) => setConnections(prev => prev.map(c => c.id === selectedConnection.id ? { ...c, toSnap: val } : c))} />
                         </div>
                     </div>
                 </div>
             )}

          </div>
        ) : null}
      </div>

      <div className={`p-4 ${styles.panelHeaderBg} border-t ${styles.panelBorder} space-y-2`}>
        <div className="grid grid-cols-2 gap-2">
           <button onClick={copyToClipboard} className="bg-blue-500/10 text-blue-500 border border-blue-500/20 py-2 rounded text-xs font-bold uppercase hover:bg-blue-500/20 flex items-center justify-center gap-1"><Copy size={12}/> Copy</button>
           <button disabled={!clipboard} onClick={pasteFromClipboard} className={`${clipboard ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-slate-500/10 text-slate-500 cursor-not-allowed"} border py-2 rounded text-xs font-bold uppercase flex items-center justify-center gap-1`}><Clipboard size={12}/> Paste</button>
        </div>
        <button onClick={deleteSelection} className="w-full bg-red-500/10 text-red-500 border border-red-500/20 py-2 rounded text-xs font-bold uppercase hover:bg-red-500/20 flex items-center justify-center gap-2"><Trash2 size={14} /> Delete</button>
      </div>
        </>
      )}
    </div>
  );
});

const getSnapCoords = (node, snap) => {
  const w = node.width || 80;
  const h = node.height || 80;
  const hw = w / 2;
  const hh = h / 2;

  switch(snap) {
      case 'tl': return { x: node.x, y: node.y };
      case 'tc': return { x: node.x + hw, y: node.y };
      case 'tr': return { x: node.x + w, y: node.y };
      case 'rc': return { x: node.x + w, y: node.y + hh };
      case 'br': return { x: node.x + w, y: node.y + h };
      case 'bc': return { x: node.x + hw, y: node.y + h };
      case 'bl': return { x: node.x, y: node.y + h };
      case 'lc': return { x: node.x, y: node.y + hh };
      default: return { x: node.x + hw, y: node.y + hh };
  }
}

const renderPath = (conn, coords, isOverlay=false, stroke, strokeWidth, strokeDash, extraProps={}) => {
   let pathD = `M ${coords.x1} ${coords.y1}`;
   if (conn.controlPoint) {
       if (conn.controlPoint.type === 'rightAngle') {
           pathD += ` L ${conn.controlPoint.x} ${conn.controlPoint.y} L ${coords.x2} ${coords.y2}`;
       } else {
           pathD += ` Q ${conn.controlPoint.x} ${conn.controlPoint.y} ${coords.x2} ${coords.y2}`;
       }
   } else {
       pathD += ` L ${coords.x2} ${coords.y2}`;
   }
   return (
      <path d={pathD} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={strokeDash} {...extraProps} />
   );
}

const CanvasArea = ({ customBackground, customBackgroundColor, nodes, connections, zoom, viewOffset, selectedIds, selectionType, connectSourceId, isDark, showGrid, onMouseDown, onNodeMouseDown, onResizeMouseDown, onConnectionClick, onConnectionMouseDown, onNodeContextMenu, onConnectionContextMenu, selectionBox, onTouchStart, onTouchMove, onTouchEnd, isExporting, selectedBrand, isSpacePressed, isPanning, mode }: any) => {
  // Memoize grid style to prevent re-calc on every render
  const gridStyle = useMemo(() => ({
    backgroundColor: customBackgroundColor || 'transparent',
    backgroundImage: showGrid && !customBackground ? `radial-gradient(${isDark ? "#1e293b" : "#cbd5e1"} 1px, transparent 1px)` : 'none',
    backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
    backgroundPosition: `${viewOffset.x}px ${viewOffset.y}px`,
    touchAction: 'none' // Critical for mobile gestures
  }), [isDark, zoom, viewOffset, showGrid, customBackground, customBackgroundColor]);

  const getInterfaceName = useCallback((nodeId, ifId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return null;
    const iface = node.interfaces?.find(i => i.id === ifId) || node.interfaces?.flatMap(i => i.subInterfaces || []).find(s => s.id === ifId);
    return iface?.name;
  }, [nodes]);

  // Helper to get full interface object to check type
  const getInterface = useCallback((nodeId, ifId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return null;
    return node.interfaces?.find(i => i.id === ifId) || node.interfaces?.flatMap(i => i.subInterfaces || []).find(s => s.id === ifId);
  }, [nodes]);

  // Render lines - Layer 1 (Bottom)
  const connectionLines = useMemo(() => connections.map(conn => {
    const from = nodes.find(n => n.id === conn.from);
    const to = nodes.find(n => n.id === conn.to);
    if (!from || !to) return null;
    
    const startCoords = getSnapCoords(from, conn.snappingEnabled ? conn.fromSnap : null);
    const endCoords = getSnapCoords(to, conn.snappingEnabled ? conn.toSnap : null);
    const coords = { x1: startCoords.x, y1: startCoords.y, x2: endCoords.x, y2: endCoords.y };

    const isSelected = selectedIds.includes(conn.id);
    const type = CABLE_TYPES[conn.cable || "COPPER"];
    const isWireless = conn.cable === "WIRELESS";
    const isAnimated = conn.animated && !isExporting;
    
    // Determine Dash Array
    let strokeDash = "0";
    if (conn.lineStyle && LINE_STYLES[conn.lineStyle]) {
        strokeDash = LINE_STYLES[conn.lineStyle].value;
    } else {
        // Fallback to defaults
        strokeDash = type.style === "dashed" ? "6,4" : type.style === "dotted" ? "1,8" : "0";
    }

    return (
      <g
        key={conn.id}
        onMouseDown={(e) => onConnectionMouseDown?.(e, conn.id)}
        onClick={(e) => onConnectionClick(e, conn.id)}
        onContextMenu={(e) => {
          if (onConnectionContextMenu) {
            e.preventDefault();
            e.stopPropagation();
            onConnectionContextMenu(e, conn);
          }
        }}
        className="cursor-pointer group pointer-events-auto"
      >
        {renderPath(conn, coords, true, "transparent", "20", "0")}
        {renderPath(conn, coords, false, isSelected ? "#60a5fa" : type.color, isSelected ? "3" : "2", strokeDash)}
        {type.style === "double" && renderPath(conn, coords, false, isSelected ? "#60a5fa" : type.color, "2", strokeDash, { strokeDashoffset: "4" })}
        
        {/* Animation Overlay */}
        {isAnimated && renderPath(conn, coords, true, isWireless ? type.color : (isDark ? "white" : "black"), isWireless ? "5" : "2", isWireless ? "0, 20" : "4,8", { strokeLinecap: isWireless ? "round" : "butt", className: `${isWireless ? "opacity-80 animate-signal" : "opacity-50 animate-flow"}` })}

        {/* Control Point Handle (shows only when selected) */}
        {isSelected && conn.controlPoint && (
          <circle
              cx={conn.controlPoint.x}
              cy={conn.controlPoint.y}
              r={6}
              fill="#eab308"
              stroke="#1e293b"
              strokeWidth={2}
              className="cursor-move"
              onMouseDown={(e) => onConnectionMouseDown?.(e, conn.id, true)}
          />
        )}
      </g>
    );
  }), [connections, nodes, selectedIds, isDark, isExporting, onConnectionClick, onConnectionMouseDown]);

  // Render labels - Layer 2 (Middle - moved below nodes in visual stack, but kept separate)
  const connectionLabels = useMemo(() => connections.map(conn => {
    const from = nodes.find(n => n.id === conn.from);
    const to = nodes.find(n => n.id === conn.to);
    if (!from || !to) return null;

    const startCoords = getSnapCoords(from, conn.snappingEnabled ? conn.fromSnap : null);
    const endCoords = getSnapCoords(to, conn.snappingEnabled ? conn.toSnap : null);
    const coords = { x1: startCoords.x, y1: startCoords.y, x2: endCoords.x, y2: endCoords.y };

    const type = CABLE_TYPES[conn.cable || "COPPER"];
    
    // Labels
    const fromName = getInterfaceName(conn.from, conn.fromIf);
    const toName = getInterfaceName(conn.to, conn.toIf);
    
    let ux1, uy1, ux2, uy2;
    if (conn.controlPoint) {
        if (conn.controlPoint.type === 'rightAngle') {
            const dx1 = conn.controlPoint.x - coords.x1;
            const dy1 = conn.controlPoint.y - coords.y1;
            const len1 = Math.sqrt(dx1*dx1 + dy1*dy1) || 1;
            ux1 = dx1/len1; uy1 = dy1/len1;

            const dx2 = coords.x2 - conn.controlPoint.x;
            const dy2 = coords.y2 - conn.controlPoint.y;
            const len2 = Math.sqrt(dx2*dx2 + dy2*dy2) || 1;
            ux2 = dx2/len2; uy2 = dy2/len2;
        } else {
            const dx1 = conn.controlPoint.x - coords.x1;
            const dy1 = conn.controlPoint.y - coords.y1;
            const len1 = Math.sqrt(dx1*dx1 + dy1*dy1) || 1;
            ux1 = dx1/len1; uy1 = dy1/len1;

            const dx2 = coords.x2 - conn.controlPoint.x;
            const dy2 = coords.y2 - conn.controlPoint.y;
            const len2 = Math.sqrt(dx2*dx2 + dy2*dy2) || 1;
            ux2 = dx2/len2; uy2 = dy2/len2;
        }
    } else {
        const dx = coords.x2 - coords.x1;
        const dy = coords.y2 - coords.y1;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        ux1 = ux2 = dx/len;
        uy1 = uy2 = dy/len;
    }

    const offset = 65; 
    
    // Visibility checks (default to true if undefined)
    const showSource = conn.showSourceLabel !== false;
    const showTarget = conn.showTargetLabel !== false;

    let wirelessLabel = null;
    if (conn.cable === "WIRELESS") {
        const i1 = getInterface(conn.from, conn.fromIf);
        const i2 = getInterface(conn.to, conn.toIf);
        if (i1?.type === "SSID") wirelessLabel = i1.name;
        else if (i2?.type === "SSID") wirelessLabel = i2.name;
    }
    
    let midX, midY;
    if (conn.controlPoint) {
        if (conn.controlPoint.type === 'rightAngle') {
            midX = conn.controlPoint.x;
            midY = conn.controlPoint.y;
        } else {
            midX = 0.25 * coords.x1 + 0.5 * conn.controlPoint.x + 0.25 * coords.x2;
            midY = 0.25 * coords.y1 + 0.5 * conn.controlPoint.y + 0.25 * coords.y2;
        }
    } else {
        midX = (coords.x1 + coords.x2) / 2;
        midY = (coords.y1 + coords.y2) / 2;
    }

    return (
      <g key={conn.id} className="pointer-events-auto" onMouseDown={(e) => onConnectionMouseDown?.(e, conn.id)} onClick={(e) => onConnectionClick(e, conn.id)}>
        {fromName && showSource && <g transform={`translate(${coords.x1 + ux1 * offset}, ${coords.y1 + uy1 * offset})`}><rect x="-16" y="-7" width="32" height="14" rx="3" fill={isDark ? "#0f172a" : "white"} stroke={isDark ? "none" : "#e2e8f0"} /><text textAnchor="middle" dy="3" fontSize="8" fill={conn.textColor || "#94a3b8"} fontWeight="bold">{fromName}</text></g>}
        {toName && showTarget && <g transform={`translate(${coords.x2 - ux2 * offset}, ${coords.y2 - uy2 * offset})`}><rect x="-16" y="-7" width="32" height="14" rx="3" fill={isDark ? "#0f172a" : "white"} stroke={isDark ? "none" : "#e2e8f0"} /><text textAnchor="middle" dy="3" fontSize="8" fill={conn.textColor || "#94a3b8"} fontWeight="bold">{toName}</text></g>}
        
        {wirelessLabel && (
             <g transform={`translate(${midX}, ${midY})`}>
               <rect x="-40" y="-10" width="80" height="20" rx="4" fill={isDark ? "#0f172a" : "white"} stroke={type.color} strokeWidth="1" />
               <text textAnchor="middle" dy="4" fontSize="10" fill={conn.textColor || type.color} fontWeight="bold">{wirelessLabel}</text>
             </g>
        )}
      </g>
    );
  }), [connections, nodes, selectedIds, isDark, getInterfaceName, getInterface]);

  const canvasCursor = (isSpacePressed || mode === 'PAN')
    ? (isPanning ? 'cursor-grabbing' : 'cursor-grab')
    : (mode === 'CONNECT' ? 'cursor-crosshair' : (mode === 'TEXT' ? 'cursor-text' : 'cursor-default'));

  return (
    <div 
        id="canvas-area"
        className={`flex-1 w-full h-full overflow-hidden relative ${canvasCursor}`} 
        onMouseDown={onMouseDown} 
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={gridStyle}
    >
      {/* Inject Keyframes for Animation */}
      <style>{`
        @keyframes flow {
            to { stroke-dashoffset: -12; }
        }
        .animate-flow {
            animation: flow 0.5s linear infinite;
        }
        /* Specific animation for wireless signals (dots moving) */
        @keyframes signal {
            to { stroke-dashoffset: -20; }
        }
        .animate-signal {
            animation: signal 1s linear infinite;
        }
      `}</style>

      <div style={{ transform: `translate(${viewOffset.x}px, ${viewOffset.y}px) scale(${zoom})`, transformOrigin: "0 0", width: "100%", height: "100%" }}>
        {/* Layer 0: Custom Background */}
        {customBackground && (
            <img src={customBackground} className="absolute z-0 pointer-events-none" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', maxWidth: '4000px', maxHeight: '4000px', objectFit: 'contain' }} alt="Custom Background" />
        )}
        {/* Layer 1: Lines (Above Shapes, Below Devices) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible">{connectionLines}</svg>
        
        {/* Layer 2: Labels (Above Lines, Below Devices) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-20 overflow-visible">{connectionLabels}</svg>

        {/* Layer 3: Nodes (Top) */}
        {nodes.map(node => {
           const isSelected = selectedIds.includes(node.id) && selectionType === "NODE";
           const isSource = connectSourceId === node.id;
           if (node.type === "SHAPE") {
             if (node.shapeType === "LINE") {
               const dashArrayMap: Record<string, string> = {
                 dashed: "8 8",
                 dotted: "3 3",
                 double: "none",
                 solid: "none"
               };
               const strokeDasharray = dashArrayMap[node.borderStyle] || "none";
               const lineEndX = node.endX !== undefined ? node.endX : node.x + (node.width || 100);
               const lineEndY = node.endY !== undefined ? node.endY : node.y + (node.height || 0);

               return (
                 <svg key={node.id} className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible">
                   {/* Invisible wide hit area line for easier dragging */}
                   <line
                     x1={node.x}
                     y1={node.y}
                     x2={lineEndX}
                     y2={lineEndY}
                     stroke="transparent"
                     strokeWidth="20"
                     className="pointer-events-auto cursor-move"
                     onMouseDown={(e) => onNodeMouseDown(e, node)}
                     onTouchStart={(e) => onNodeMouseDown(e, node)}
                   />
                   {/* Visible styled line */}
                   <line
                     x1={node.x}
                     y1={node.y}
                     x2={lineEndX}
                     y2={lineEndY}
                     stroke={node.color || "#64748b"}
                     strokeWidth={node.borderWidth || 4}
                     strokeDasharray={strokeDasharray}
                   />
                   {/* Handle controls when selected */}
                   {isSelected && (
                     <>
                       <circle
                         cx={node.x}
                         cy={node.y}
                         r="5"
                         fill="white"
                         stroke="#3b82f6"
                         strokeWidth="2"
                         className="pointer-events-auto cursor-crosshair"
                         onMouseDown={(e) => onResizeMouseDown(e, node, 'p1')}
                         onTouchStart={(e) => onResizeMouseDown(e, node, 'p1')}
                       />
                       <circle
                         cx={lineEndX}
                         cy={lineEndY}
                         r="5"
                         fill="white"
                         stroke="#3b82f6"
                         strokeWidth="2"
                         className="pointer-events-auto cursor-crosshair"
                         onMouseDown={(e) => onResizeMouseDown(e, node, 'p2')}
                         onTouchStart={(e) => onResizeMouseDown(e, node, 'p2')}
                       />
                     </>
                   )}
                 </svg>
               );
             }

             const labelPos = node.labelPosition || "center";
             // Calculate absolute position style for label
             const labelStyle: React.CSSProperties = {
                position: 'absolute',
                left: '50%',
                transform: labelPos === 'center' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
                top: labelPos === 'top' ? '8px' : labelPos === 'center' ? '50%' : 'auto',
                bottom: labelPos === 'bottom' ? '8px' : 'auto',
                fontSize: `${node.labelSize || 14}px`,
                pointerEvents: 'none',
                width: '100%',
                textAlign: 'center' as const,
                padding: '0 8px',
                wordWrap: 'break-word',
                color: node.textColor ? node.textColor : undefined
                };
             return (
               // FIX: Shape is now ALWAYS z-0, even when selected. This prevents it from blocking clicks on devices sitting "inside" it.
               <div
                 key={node.id}
                 onTouchStart={(e) => onNodeMouseDown(e, node)}
                 onMouseDown={(e) => onNodeMouseDown(e, node)}
                 onContextMenu={(e) => {
                   if (onNodeContextMenu) {
                     e.preventDefault();
                     e.stopPropagation();
                     onNodeContextMenu(e, node);
                   }
                 }}
                 style={{
                   left: node.x,
                   top: node.y,
                   width: node.width,
                   height: node.height,
                   borderColor: node.color,
                   borderWidth: node.borderWidth,
                   borderStyle: node.borderStyle || 'solid',
                   borderRadius: node.shapeType === "CIRCLE" ? "50%" : "8px"
                 }}
                 className={`absolute flex items-center justify-center group z-0 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
               >
                 {isSelected && <>
                   <div onMouseDown={(e) => onResizeMouseDown(e, node, 'nw')} className="absolute -top-1 -left-1 w-2 h-2 bg-white border border-blue-500 cursor-nw-resize" />
                   <div onMouseDown={(e) => onResizeMouseDown(e, node, 'se')} className="absolute -bottom-1 -right-1 w-2 h-2 bg-white border border-blue-500 cursor-se-resize" />
                 </>}
                 {node.label && (
                    <span 
                        style={labelStyle}
                        className={`font-bold leading-tight ${isDark ? "text-slate-200" : "text-slate-800"}`}
                    >
                        {node.label}
                    </span>
                 )}
               </div>
             );
           }
           if (node.type === "IMAGE") {
             return (
               <div
                 key={node.id}
                 onTouchStart={(e) => onNodeMouseDown(e, node)}
                 onMouseDown={(e) => onNodeMouseDown(e, node)}
                 onContextMenu={(e) => {
                   if (onNodeContextMenu) {
                     e.preventDefault();
                     e.stopPropagation();
                     onNodeContextMenu(e, node);
                   }
                 }}
                 style={{ left: node.x, top: node.y, width: node.width, height: node.height }}
                 className={`absolute flex items-center justify-center group z-0 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
               >
                 {isSelected && <>
                   <div onMouseDown={(e) => onResizeMouseDown(e, node, 'nw')} className="absolute -top-1 -left-1 w-2 h-2 bg-white border border-blue-500 cursor-nw-resize" />
                   <div onMouseDown={(e) => onResizeMouseDown(e, node, 'se')} className="absolute -bottom-1 -right-1 w-2 h-2 bg-white border border-blue-500 cursor-se-resize" />
                 </>}
                 <div
                   className="w-full h-full pointer-events-none"
                   style={{
                     backgroundImage: `url(${node.src})`,
                     backgroundPosition: 'center',
                     backgroundRepeat: 'no-repeat',
                     backgroundSize: node.objectFit === 'object-fill' ? '100% 100%' : node.objectFit === 'object-cover' ? 'cover' : 'contain'
                   }}
                 />
               </div>
             );
           }
           if (node.type === "TEXT") return (
             // FIX: Text is now ALWAYS z-30.
             <div 
                key={node.id} 
                onTouchStart={(e) => onNodeMouseDown(e, node)} 
                onMouseDown={(e) => onNodeMouseDown(e, node)} 
                onContextMenu={(e) => {
                  if (onNodeContextMenu) {
                    e.preventDefault();
                    e.stopPropagation();
                    onNodeContextMenu(e, node);
                  }
                }}
                style={{ 
                    left: node.x, 
                    top: node.y, 
                    fontSize: node.fontSize, 
                    color: node.color || (isDark ? "#cbd5e1" : "#1e293b"),
                    borderStyle: node.borderStyle || 'none',
                    borderColor: node.borderColor || node.color || (isDark ? "#cbd5e1" : "#1e293b"),
                    borderWidth: node.borderStyle && node.borderStyle !== 'none' ? 2 : 0,
                    padding: node.borderStyle && node.borderStyle !== 'none' ? '8px' : '4px',
                    borderRadius: '4px'
                }} 
                className={`absolute cursor-move whitespace-pre border border-transparent z-30 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
             >
                {node.content}
             </div>
           );
           const def = DEVICE_TYPES[node.type] || { border: "border-blue-500/50", color: "text-blue-500", label: "Device", category: "INFRA" };
           // Determine hover border color class:
           // If custom color exists, use arbitrary value syntax (e.g., hover:border-[#123456])
           // If default, use the device type's border class prefixed with hover:
           const hoverBorderClass = node.color ? `hover:border-[${node.color}]` : `hover:${def.border}`;

           // Logic to handle border color when selected vs unselected
           // If Selected: Use custom color if exists, else default def.border
           // If Unselected: Transparent (unless hovered, handled by hoverBorderClass)
           const boxStyle = isSelected && node.color ? { borderColor: node.color } : {};
           const boxBorderClass = isSelected 
              ? (!node.color ? def.border : "") // Only use default class if no custom color
              : "border-transparent";

           return (
             // Devices remain z-40 (or z-50 when selected) to ensure they always float above shapes/lines/text
             <div
               key={node.id}
               onTouchStart={(e) => onNodeMouseDown(e, node)}
               onMouseDown={(e) => onNodeMouseDown(e, node)}
               onContextMenu={(e) => {
                 if (onNodeContextMenu) {
                   e.preventDefault();
                   e.stopPropagation();
                   onNodeContextMenu(e, node);
                 }
               }}
               style={{ left: node.x, top: node.y }}
               className={`absolute w-20 flex flex-col items-center cursor-pointer ${isSelected || isSource ? "z-50" : "z-40"}`}
             >
                <div 
                   style={boxStyle}
                   className={`relative w-16 h-16 rounded-2xl flex items-center justify-center mb-1 border-2 shadow-sm transition-all bg-transparent ${isSource ? "ring-4 ring-emerald-500 border-emerald-400" : isSelected ? `${boxBorderClass} shadow-blue-500/20 ring-1 ring-blue-500` : `${boxBorderClass} ${hoverBorderClass}`}`}
                >
                   {node.isLocked && (
                     <div className="absolute -top-1.5 -right-1.5 bg-slate-800 text-amber-400 p-0.5 rounded-full border border-slate-700 shadow-sm z-10" title="Locked Position">
                       <Lock size={10} />
                     </div>
                   )}
                   <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${isDark ? "slate-900" : "white"}`}>
                     <div className="w-8 h-8 flex items-center justify-center overflow-hidden">
                       <BrandIcon 
                          type={node.type} 
                          brand={node.brand || 'default'}
                          size={24} 
                          className={isSource ? "text-emerald-500" : (node.color ? "" : def.color)} 
                          color={!isSource && node.color ? node.color : undefined}
                          isDark={isDark}
                       />
                     </div>
                   </div>
                </div>
                {/* Dynamic Label rendering */}
                <div 
                  className={`text-[10px] font-bold px-1 rounded-md text-center leading-tight whitespace-normal max-w-[140%] min-h-[1.5em] pb-1 ${isSource ? "bg-emerald-500 text-white p-1" : isDark ? "text-slate-300" : "text-slate-800"}`}
                  style={{ overflow: 'visible', color: (!isSource && node.textColor) ? node.textColor : undefined }}
                >
                  {node.name}
                </div>
             </div>
           );
        })}
        
        {selectionBox && (
            <div
              className="absolute border border-blue-500 bg-blue-500/20 z-[60] pointer-events-none"
              style={{
                left: Math.min(selectionBox.startX, selectionBox.currentX),
                top: Math.min(selectionBox.startY, selectionBox.currentY),
                width: Math.abs(selectionBox.currentX - selectionBox.startX),
                height: Math.abs(selectionBox.currentY - selectionBox.startY),
              }}
            />
         )}
      </div>
      </div>
      );
      }

      // ==========================================
      // Layout Import / Export Helper Functions & Components
      // ==========================================

      const sanitizeImportedData = (rawData: any): any[] => {
      let projectsArray: any[] = [];
      if (Array.isArray(rawData)) {
      projectsArray = rawData;
      } else if (rawData && typeof rawData === 'object') {
      projectsArray = [rawData];
      } else {
      return [];
      }

      const sanitizedProjects: any[] = [];

      for (const proj of projectsArray) {
      if (!proj || typeof proj !== 'object' || proj.__proto__ !== Object.prototype || Object.getPrototypeOf(proj) !== Object.prototype) {
      continue;
      }

      const id = typeof proj.id === 'string' ? proj.id.slice(0, 50) : "";
      const name = typeof proj.name === 'string' ? proj.name.slice(0, 30) : "Imported Project";
      const color = (typeof proj.color === 'string' && TAB_COLORS[proj.color as keyof typeof TAB_COLORS]) ? proj.color : "BLUE";
      const isLocked = !!proj.isLocked;
      const zoom = typeof proj.zoom === 'number' && !isNaN(proj.zoom) ? Math.min(Math.max(proj.zoom, 0.1), 2) : 1;

      let viewOffset = { x: 0, y: 0 };
      if (proj.viewOffset && typeof proj.viewOffset === 'object' && proj.viewOffset.__proto__ === Object.prototype && Object.getPrototypeOf(proj.viewOffset) === Object.prototype) {
      const vx = typeof proj.viewOffset.x === 'number' && !isNaN(proj.viewOffset.x) ? proj.viewOffset.x : 0;
      const vy = typeof proj.viewOffset.y === 'number' && !isNaN(proj.viewOffset.y) ? proj.viewOffset.y : 0;
      viewOffset = { x: vx, y: vy };
      }

      let customBackgroundColor = null;
      if (typeof proj.customBackgroundColor === 'string') {
      const hexRegex = /^#[0-9a-fA-F]{3,6}$/;
      if (hexRegex.test(proj.customBackgroundColor)) {
       customBackgroundColor = proj.customBackgroundColor;
      }
      }

      let customBackground = null;
      if (typeof proj.customBackground === 'string') {
      const imageUriRegex = /^data:image\/(jpeg|png|bmp);base64,[A-Za-z0-9+/=]+$/;
      if (imageUriRegex.test(proj.customBackground)) {
       customBackground = proj.customBackground;
      }
      }

      const rawNodes = Array.isArray(proj.nodes) ? proj.nodes : [];
      const slicedNodes = rawNodes.slice(0, MAX_NODES);
      const sanitizedNodes: any[] = [];
      const nodeCap = slicedNodes.length;

      for (let i = 0; i < nodeCap; i++) {
      const node = slicedNodes[i];
      if (!node || typeof node !== 'object' || node.__proto__ !== Object.prototype || Object.getPrototypeOf(node) !== Object.prototype) continue;

      const nodeId = typeof node.id === 'string' ? node.id.slice(0, 50) : "";
      const nodeType = typeof node.type === 'string' ? node.type : "";

      const isDevice = DEVICE_TYPES[nodeType as keyof typeof DEVICE_TYPES] !== undefined || (Boolean(node.brand) && node.brand !== 'default') || nodeType.includes('-') || nodeType.startsWith('aws') || nodeType.startsWith('azure') || nodeType.startsWith('gcp') || nodeType.startsWith('google');
      const isText = nodeType === "TEXT";
      const isShape = nodeType === "SHAPE";
      const isImage = nodeType === "IMAGE";

      if (!isDevice && !isText && !isShape && !isImage) {
       continue;
      }

      const x = typeof node.x === 'number' && !isNaN(node.x) ? node.x : 100;
      const y = typeof node.y === 'number' && !isNaN(node.y) ? node.y : 100;

      const sanitizedNode: any = {
       id: nodeId,
       type: nodeType,
       x,
       y
      };

      if (isDevice) {
       sanitizedNode.name = typeof node.name === 'string' ? node.name.slice(0, 30) : "";
       sanitizedNode.brand = typeof node.brand === 'string' ? node.brand.slice(0, 30) : "default";

       const rawInterfaces = Array.isArray(node.interfaces) ? node.interfaces : [];
       const slicedInterfaces = rawInterfaces.slice(0, 32);
       const sanitizedInterfaces: any[] = [];
       const interfaceCap = slicedInterfaces.length;

       for (let j = 0; j < interfaceCap; j++) {
         const iface = slicedInterfaces[j];
         if (!iface || typeof iface !== 'object' || iface.__proto__ !== Object.prototype || Object.getPrototypeOf(iface) !== Object.prototype) continue;

         const ifaceId = typeof iface.id === 'string' ? iface.id.slice(0, 50) : "";
         const ifaceName = typeof iface.name === 'string' ? iface.name.slice(0, 30) : "";
         const ifaceType = typeof iface.type === 'string' ? iface.type.slice(0, 30) : "PHYSICAL";
         const ifaceIp = typeof iface.ip === 'string' ? iface.ip.slice(0, 30) : "";

         const sanitizedIface: any = {
           id: ifaceId,
           name: ifaceName,
           type: ifaceType,
           ip: ifaceIp
         };

         if (typeof iface.switchportMode === 'string') {
           sanitizedIface.switchportMode = iface.switchportMode.slice(0, 20);
         }
         if (typeof iface.nativeVlan === 'string') {
           sanitizedIface.nativeVlan = iface.nativeVlan.slice(0, 10);
         }
         if (typeof iface.allowedVlans === 'string') {
           sanitizedIface.allowedVlans = iface.allowedVlans.slice(0, 500);
         }
         if (typeof iface.accessVlan === 'string') {
           sanitizedIface.accessVlan = iface.accessVlan.slice(0, 10);
         }

         const rawSubInterfaces = Array.isArray(iface.subInterfaces) ? iface.subInterfaces : [];
         const slicedSubInterfaces = rawSubInterfaces.slice(0, 16);
         const sanitizedSubInterfaces: any[] = [];
         const subInterfaceCap = slicedSubInterfaces.length;

         for (let k = 0; k < subInterfaceCap; k++) {
           const subIface = slicedSubInterfaces[k];
           if (!subIface || typeof subIface !== 'object' || subIface.__proto__ !== Object.prototype || Object.getPrototypeOf(subIface) !== Object.prototype) continue;

           const subIfaceId = typeof subIface.id === 'string' ? subIface.id.slice(0, 50) : "";
           const subIfaceName = typeof subIface.name === 'string' ? subIface.name.slice(0, 30) : "";
           const subIfaceVlan = typeof subIface.vlan === 'string' ? subIface.vlan.slice(0, 10) : "";
           const subIfaceIp = typeof subIface.ip === 'string' ? subIface.ip.slice(0, 30) : "";

           sanitizedSubInterfaces.push({
             id: subIfaceId,
             name: subIfaceName,
             vlan: subIfaceVlan,
             ip: subIfaceIp
           });
         }

         sanitizedIface.subInterfaces = sanitizedSubInterfaces;
         sanitizedInterfaces.push(sanitizedIface);
       }

       sanitizedNode.interfaces = sanitizedInterfaces;

       if (typeof node.textColor === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(node.textColor)) {
         sanitizedNode.textColor = node.textColor;
       }
       if (typeof node.color === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(node.color)) {
         sanitizedNode.color = node.color;
       }

      } else if (isText) {
       sanitizedNode.content = typeof node.content === 'string' ? node.content.slice(0, 500) : "";
       sanitizedNode.fontSize = typeof node.fontSize === 'number' && !isNaN(node.fontSize) ? Math.min(Math.max(node.fontSize, 8), 72) : 16;
       if (typeof node.color === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(node.color)) {
         sanitizedNode.color = node.color;
       }
       if (typeof node.borderStyle === 'string') {
         sanitizedNode.borderStyle = node.borderStyle.slice(0, 20);
       }
       if (typeof node.borderColor === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(node.borderColor)) {
         sanitizedNode.borderColor = node.borderColor;
       }

      } else if (isShape) {
       const shapeType = typeof node.shapeType === 'string' ? node.shapeType : "RECTANGLE";
       sanitizedNode.shapeType = (shapeType === "CIRCLE" || shapeType === "RECTANGLE" || shapeType === "LINE") ? shapeType : "RECTANGLE";
       sanitizedNode.width = typeof node.width === 'number' && !isNaN(node.width) ? Math.min(Math.max(node.width, 10), 2000) : 100;
       sanitizedNode.height = typeof node.height === 'number' && !isNaN(node.height) ? Math.min(Math.max(node.height, 2), 2000) : 100;

       let shapeColor = "#64748b";
       if (typeof node.color === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(node.color)) {
         shapeColor = node.color;
       }
       sanitizedNode.color = shapeColor;
       sanitizedNode.borderWidth = typeof node.borderWidth === 'number' && !isNaN(node.borderWidth) ? Math.min(Math.max(node.borderWidth, 0), 20) : 4;
       sanitizedNode.borderStyle = typeof node.borderStyle === 'string' ? node.borderStyle.slice(0, 20) : "solid";
       sanitizedNode.label = typeof node.label === 'string' ? node.label.slice(0, 30) : "";
      } else if (isImage) {
       sanitizedNode.width = typeof node.width === 'number' && !isNaN(node.width) ? Math.min(Math.max(node.width, 10), 2000) : 100;
       sanitizedNode.height = typeof node.height === 'number' && !isNaN(node.height) ? Math.min(Math.max(node.height, 10), 2000) : 100;
       sanitizedNode.src = typeof node.src === 'string' ? node.src : "";
       sanitizedNode.fileName = typeof node.fileName === 'string' ? node.fileName.slice(0, 100) : "";
       if (typeof node.objectFit === 'string') {
         sanitizedNode.objectFit = node.objectFit.slice(0, 20);
       }
      }

      sanitizedNodes.push(sanitizedNode);
      }

      const rawConnections = Array.isArray(proj.connections) ? proj.connections : [];
      const slicedConnections = rawConnections.slice(0, MAX_NODES * 2);
      const sanitizedConnections: any[] = [];
      const connCap = slicedConnections.length;

      for (let i = 0; i < connCap; i++) {
      const conn = slicedConnections[i];
      if (!conn || typeof conn !== 'object' || conn.__proto__ !== Object.prototype || Object.getPrototypeOf(conn) !== Object.prototype) continue;

      const connId = typeof conn.id === 'string' ? conn.id.slice(0, 50) : "";
      const from = typeof conn.from === 'string' ? conn.from.slice(0, 50) : "";
      const to = typeof conn.to === 'string' ? conn.to.slice(0, 50) : "";
      const fromIf = typeof conn.fromIf === 'string' ? conn.fromIf.slice(0, 50) : null;
      const toIf = typeof conn.toIf === 'string' ? conn.toIf.slice(0, 50) : null;

      const cable = (typeof conn.cable === 'string' && CABLE_TYPES[conn.cable as keyof typeof CABLE_TYPES]) ? conn.cable : "COPPER";
      const lineStyle = (typeof conn.lineStyle === 'string' && LINE_STYLES[conn.lineStyle as keyof typeof LINE_STYLES]) ? conn.lineStyle : "SOLID";

      const sanitizedConn: any = {
       id: connId,
       from,
       to,
       fromIf,
       toIf,
       cable,
       lineStyle
      };

      if (typeof conn.animated === 'boolean') {
       sanitizedConn.animated = conn.animated;
      }
      if (typeof conn.snappingEnabled === 'boolean') {
       sanitizedConn.snappingEnabled = conn.snappingEnabled;
      }
      if (typeof conn.fromSnap === 'number' && !isNaN(conn.fromSnap)) {
       sanitizedConn.fromSnap = conn.fromSnap;
      }
      if (typeof conn.toSnap === 'number' && !isNaN(conn.toSnap)) {
       sanitizedConn.toSnap = conn.toSnap;
      }
      if (typeof conn.showSourceLabel === 'boolean') {
       sanitizedConn.showSourceLabel = conn.showSourceLabel;
      }
      if (typeof conn.showTargetLabel === 'boolean') {
       sanitizedConn.showTargetLabel = conn.showTargetLabel;
      }
      if (typeof conn.textColor === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(conn.textColor)) {
       sanitizedConn.textColor = conn.textColor;
      }

      if (conn.controlPoint && typeof conn.controlPoint === 'object' && conn.controlPoint.__proto__ === Object.prototype && Object.getPrototypeOf(conn.controlPoint) === Object.prototype) {
       const cpType = typeof conn.controlPoint.type === 'string' ? conn.controlPoint.type : "curve";
       const cpx = typeof conn.controlPoint.x === 'number' && !isNaN(conn.controlPoint.x) ? conn.controlPoint.x : 0;
       const cpy = typeof conn.controlPoint.y === 'number' && !isNaN(conn.controlPoint.y) ? conn.controlPoint.y : 0;
       sanitizedConn.controlPoint = {
         type: (cpType === "rightAngle" || cpType === "curve") ? cpType : "curve",
         x: cpx,
         y: cpy
       };
      }

      sanitizedConnections.push(sanitizedConn);
      }

      sanitizedProjects.push({
      id,
      name,
      color,
      isLocked,
      nodes: sanitizedNodes,
      connections: sanitizedConnections,
      zoom,
      viewOffset,
      customBackground,
      customBackgroundColor
      });
      }

      return sanitizedProjects;
      };

      const ImportOptionModal = ({ pendingDataCount, onOverwrite, onAppend, onCancel, styles, isDark }: any) => (
      <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`} onClick={onCancel}>
      <div className={`${styles.modalBg} w-full max-w-md p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`} onClick={e => e.stopPropagation()}>
      <h3 className="font-bold text-lg mb-2 text-blue-500 flex items-center gap-2">
       <FileDown size={20} /> Import Layout Options
      </h3>
      <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
       You are importing <strong>{pendingDataCount} project{pendingDataCount > 1 ? 's' : ''}</strong>. How would you like to apply this to your workspace?
      </p>

      <div className="space-y-3 mb-6">
       <button 
         onClick={onOverwrite}
         className="w-full p-4 rounded-xl border border-red-500/30 hover:border-red-500 bg-red-500/5 hover:bg-red-500/10 text-left transition-all active:scale-[0.99]"
       >
         <div className="font-bold text-sm text-red-500 flex items-center gap-1.5">
           <Trash2 size={16} /> Overwrite Workspace
         </div>
         <div className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
           Wipes all current projects entirely and replaces them with the imported configuration.
         </div>
       </button>

       <button 
         onClick={onAppend}
         className="w-full p-4 rounded-xl border border-blue-500/30 hover:border-blue-500 bg-blue-500/5 hover:bg-blue-500/10 text-left transition-all active:scale-[0.99]"
       >
         <div className="font-bold text-sm text-blue-400 flex items-center gap-1.5">
           <PlusCircle size={16} /> Append as New Tabs
         </div>
         <div className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
           Adds the imported files as new selectable project tabs. Keeps your existing tabs completely safe.
         </div>
       </button>
      </div>

      <div className="flex justify-end">
       <button 
         onClick={onCancel} 
         className={`px-4 py-2 text-xs font-bold ${styles.textSubtle} hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg`}
       >
         Cancel
       </button>
      </div>
      </div>
      </div>
      );

      // ==========================================
      // Main App Component
      // ==========================================

// ==========================================
// Canvas Minimap / Overview Box Component
// ==========================================
interface MinimapProps {
  nodes: any[];
  connections: any[];
  viewOffset: { x: number; y: number };
  zoom: number;
  canvasRef: React.RefObject<HTMLDivElement>;
  onPanTo: (newOffset: { x: number; y: number }) => void;
  showMinimap: boolean;
  setShowMinimap: React.Dispatch<React.SetStateAction<boolean>>;
  styles: any;
  isDark: boolean;
}

const Minimap: React.FC<MinimapProps> = ({
  nodes,
  viewOffset,
  zoom,
  canvasRef,
  onPanTo,
  showMinimap,
  setShowMinimap,
  styles,
  isDark
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isNavigatingRef = useRef(false);

  // Bounding box calculation: default bounds -1000px to +1000px, dynamically expanded
  const { minX, minY, worldWidth, worldHeight, scaleX, scaleY } = useMemo(() => {
    let min_x = -1000;
    let max_x = 1000;
    let min_y = -1000;
    let max_y = 1000;

    if (nodes && nodes.length > 0) {
      const xs = nodes.map((n: any) => n.x);
      const ys = nodes.map((n: any) => n.y);
      const maxXs = nodes.map((n: any) => n.x + (n.width || NODE_WIDTH));
      const maxYs = nodes.map((n: any) => n.y + (n.height || NODE_HEIGHT));

      min_x = Math.min(-1000, ...xs);
      max_x = Math.max(1000, ...maxXs);
      min_y = Math.min(-1000, ...ys);
      max_y = Math.max(1000, ...maxYs);
    }

    const w = Math.max(1, max_x - min_x);
    const h = Math.max(1, max_y - min_y);
    return {
      minX: min_x,
      maxX: max_x,
      minY: min_y,
      maxY: max_y,
      worldWidth: w,
      worldHeight: h,
      scaleX: 180 / w,
      scaleY: 120 / h
    };
  }, [nodes]);

  // Current screen viewport calculation relative to world bounds
  const canvasRect = canvasRef.current?.getBoundingClientRect();
  const canvasW = canvasRect?.width || (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const canvasH = canvasRect?.height || (typeof window !== 'undefined' ? window.innerHeight : 800);

  const worldViewX = -viewOffset.x / zoom;
  const worldViewY = -viewOffset.y / zoom;
  const worldViewW = canvasW / zoom;
  const worldViewH = canvasH / zoom;

  const viewportMinimapX = (worldViewX - minX) * scaleX;
  const viewportMinimapY = (worldViewY - minY) * scaleY;
  const viewportMinimapW = Math.max(4, worldViewW * scaleX);
  const viewportMinimapH = Math.max(4, worldViewH * scaleY);

  const handlePointerNavigate = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const localX = Math.max(0, Math.min(180, clientX - rect.left));
    const localY = Math.max(0, Math.min(120, clientY - rect.top));

    const clickedWorldX = minX + (localX / 180) * worldWidth;
    const clickedWorldY = minY + (localY / 120) * worldHeight;

    const cRect = canvasRef.current?.getBoundingClientRect();
    const cW = cRect?.width || window.innerWidth;
    const cH = cRect?.height || window.innerHeight;

    const newOffsetX = (cW / 2) - clickedWorldX * zoom;
    const newOffsetY = (cH / 2) - clickedWorldY * zoom;

    onPanTo({ x: newOffsetX, y: newOffsetY });
  }, [minX, minY, worldWidth, worldHeight, zoom, onPanTo, canvasRef]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    isNavigatingRef.current = true;
    handlePointerNavigate(e.clientX, e.clientY);

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isNavigatingRef.current) return;
      handlePointerNavigate(moveEvent.clientX, moveEvent.clientY);
    };

    const onMouseUp = () => {
      isNavigatingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      data-html2canvas-ignore="true"
      className={`fixed bottom-6 right-24 z-[80] select-none rounded-xl border shadow-2xl overflow-hidden transition-all duration-200 ${styles.panelBg} ${styles.panelBorder}`}
    >
      {/* Header Bar */}
      <div className={`px-3 py-1.5 flex items-center justify-between gap-3 border-b ${styles.panelBorder} ${styles.panelHeaderBg}`}>
        <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
          <MapIcon size={13} className="text-blue-500" />
          <span className="text-[11px] font-bold uppercase tracking-wider">Overview</span>
        </div>
        <button
          onClick={() => setShowMinimap(prev => !prev)}
          className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-800 ${styles.textSubtle} transition-transform`}
          title={showMinimap ? "Collapse Minimap" : "Expand Minimap"}
        >
          <ChevronDown size={14} className={`transform transition-transform duration-200 ${showMinimap ? "" : "rotate-180"}`} />
        </button>
      </div>

      {/* Thumbnail Window: Fixed 180px x 120px */}
      {showMinimap && (
        <div
          id="minimap-thumbnail"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          className={`w-[180px] h-[120px] relative cursor-crosshair overflow-hidden ${isDark ? "bg-slate-900/95" : "bg-slate-100/95"}`}
        >
          <svg className="w-full h-full pointer-events-none" viewBox="0 0 180 120">
            {/* Background grid line markers */}
            <line x1="90" y1="0" x2="90" y2="120" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="0.5" strokeDasharray="2,2" opacity="0.4" />
            <line x1="0" y1="60" x2="180" y2="60" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="0.5" strokeDasharray="2,2" opacity="0.4" />

            {/* Miniature colored dots/rectangles representing each node */}
            {nodes.map((n: any) => {
              const nx = (n.x - minX) * scaleX;
              const ny = (n.y - minY) * scaleY;
              const nw = Math.max(3, (n.width || NODE_WIDTH) * scaleX);
              const nh = Math.max(3, (n.height || NODE_HEIGHT) * scaleY);
              let color = n.color;
              if (!color) {
                if (n.type === 'ROUTER') color = '#f97316';
                else if (n.type === 'SWITCH') color = '#3b82f6';
                else if (n.type === 'FIREWALL') color = '#ef4444';
                else if (n.type === 'SERVER') color = '#a855f7';
                else if (n.type === 'AP') color = '#22c55e';
                else if (n.type === 'CLOUD') color = '#06b6d4';
                else if (n.type === 'TEXT') color = isDark ? '#94a3b8' : '#475569';
                else if (n.type === 'SHAPE') color = n.color || '#64748b';
                else color = '#3b82f6';
              }
              return (
                <rect
                  key={n.id}
                  x={nx}
                  y={ny}
                  width={nw}
                  height={nh}
                  fill={color}
                  rx={n.shapeType === 'CIRCLE' ? nw / 2 : 1}
                  opacity={0.85}
                />
              );
            })}

            {/* Highlighted Viewport Rectangle */}
            <rect
              x={viewportMinimapX}
              y={viewportMinimapY}
              width={viewportMinimapW}
              height={viewportMinimapH}
              fill="rgba(59, 130, 246, 0.2)"
              stroke="#3b82f6"
              strokeWidth="1.5"
              rx="2"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Context-Aware Right-Click Menu Component
// ==========================================
interface ContextMenuProps {
  contextMenu: {
    x: number;
    y: number;
    worldX: number;
    worldY: number;
    targetType: 'NODE' | 'CONNECTION' | 'SHAPE' | 'MULTI' | 'CANVAS';
    targetId?: string;
  };
  onClose: () => void;
  activeProject: any;
  selectedIds: any[];
  styles: any;
  isDark: boolean;
  showGrid: boolean;
  isSnapping: boolean;
  clipboard: any;
  onDuplicateNode: (node: any) => void;
  onToggleLockNode: (nodeId: string) => void;
  onRecolorNode: (nodeId: string, color?: string) => void;
  onAddPort: (node: any) => void;
  onCopyPrimaryIp: (node: any) => void;
  onBringToFront: (nodeId: string) => void;
  onSendToBack: (nodeId: string) => void;
  onSwitchCable: (connId: string, cable: string) => void;
  onAddBendPoint: (connId: string, x: number, y: number) => void;
  onResetPath: (connId: string) => void;
  onToggleAnimation: (connId: string) => void;
  onToggleLabels: (connId: string) => void;
  onDeleteLink: (connId: string) => void;
  onApplyShapePreset: (shapeId: string, preset: { color: string; borderStyle: string; borderWidth: number }) => void;
  onSendShapeToBackground: (shapeId: string) => void;
  onQuickAddNode: (type: string) => void;
  onPasteAtCursor: (worldX: number, worldY: number) => void;
  onCenterView: () => void;
  onToggleGrid: () => void;
  onToggleSnapping: () => void;
  onAlignNodes: (direction: 'left' | 'center' | 'right' | 'top' | 'bottom') => void;
  onDistributeNodes: (axis: 'horizontal' | 'vertical') => void;
  onGroupSelection: () => void;
  onDeleteSelection: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  onClose,
  activeProject,
  selectedIds,
  styles,
  isDark,
  showGrid,
  isSnapping,
  clipboard,
  onDuplicateNode,
  onToggleLockNode,
  onRecolorNode,
  onAddPort,
  onCopyPrimaryIp,
  onBringToFront,
  onSendToBack,
  onSwitchCable,
  onAddBendPoint,
  onResetPath,
  onToggleAnimation,
  onToggleLabels,
  onDeleteLink,
  onApplyShapePreset,
  onSendShapeToBackground,
  onQuickAddNode,
  onPasteAtCursor,
  onCenterView,
  onToggleGrid,
  onToggleSnapping,
  onAlignNodes,
  onDistributeNodes,
  onGroupSelection,
  onDeleteSelection
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState({ x: contextMenu.x, y: contextMenu.y });

  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
      let left = contextMenu.x;
      let top = contextMenu.y;

      if (left + rect.width > winW - 10) {
        left = Math.max(10, winW - rect.width - 10);
      }
      if (top + rect.height > winH - 10) {
        top = Math.max(10, winH - rect.height - 10);
      }
      setAdjustedPos({ x: left, y: top });
    }
  }, [contextMenu.x, contextMenu.y]);

  const targetNode = activeProject.nodes.find((n: any) => n.id === contextMenu.targetId);
  const targetConn = activeProject.connections.find((c: any) => c.id === contextMenu.targetId);

  return (
    <div
      ref={menuRef}
      style={{ left: `${adjustedPos.x}px`, top: `${adjustedPos.y}px` }}
      className={`fixed z-[200] min-w-[210px] max-w-[280px] rounded-xl border shadow-2xl overflow-hidden py-1.5 text-xs animate-in fade-in zoom-in-95 duration-100 select-none ${styles.panelBg} ${styles.panelBorder} ${styles.text}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* 1. SINGLE DEVICE MENU */}
      {contextMenu.targetType === 'NODE' && targetNode && (
        <div className="space-y-0.5">
          <div className={`px-3 py-1.5 border-b ${styles.panelBorder} flex items-center justify-between ${styles.panelHeaderBg}`}>
            <span className="font-bold text-[11px] truncate">{targetNode.name || targetNode.type}</span>
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-bold tracking-wider">Device</span>
          </div>

          <button
            onClick={() => { onDuplicateNode(targetNode); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Copy size={13} className="text-blue-400" />
            <span>Duplicate</span>
          </button>

          <button
            onClick={() => { onToggleLockNode(targetNode.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            {targetNode.isLocked ? <Unlock size={13} className="text-amber-400" /> : <Lock size={13} className="text-amber-400" />}
            <span>{targetNode.isLocked ? "Unlock Position" : "Lock Position"}</span>
          </button>

          {/* Quick Recolor Swatches */}
          <div className="px-3 py-1.5">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Quick Recolor</div>
            <div className="flex items-center gap-1.5">
              {['#3b82f6', '#ef4444', '#f97316', '#22c55e', '#a855f7'].map(c => (
                <button
                  key={c}
                  onClick={() => { onRecolorNode(targetNode.id, c); onClose(); }}
                  className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${targetNode.color === c ? 'border-white shadow-md' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
              {targetNode.color && (
                <button
                  onClick={() => { onRecolorNode(targetNode.id, undefined); onClose(); }}
                  className={`px-1.5 py-0.5 rounded text-[10px] border ${styles.inputBorder} ${styles.textSubtle} hover:text-red-400`}
                  title="Reset Color"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { onAddPort(targetNode); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Plus size={13} className="text-emerald-400" />
            <span>{targetNode.type === 'AP' ? "Add Port / SSID" : "Add Port"}</span>
          </button>

          <button
            onClick={() => { onCopyPrimaryIp(targetNode); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Clipboard size={13} className="text-purple-400" />
            <span>Copy Primary IP</span>
          </button>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { onBringToFront(targetNode.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <BringToFront size={13} />
            <span>Bring to Front</span>
          </button>

          <button
            onClick={() => { onSendToBack(targetNode.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <SendToBack size={13} />
            <span>Send to Back</span>
          </button>
        </div>
      )}

      {/* 2. CONNECTION MENU */}
      {contextMenu.targetType === 'CONNECTION' && targetConn && (
        <div className="space-y-0.5">
          <div className={`px-3 py-1.5 border-b ${styles.panelBorder} flex items-center justify-between ${styles.panelHeaderBg}`}>
            <span className="font-bold text-[11px]">Cable Link</span>
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold tracking-wider">Link</span>
          </div>

          {/* Cable Switcher */}
          <div className="px-3 py-1.5">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Cable Switcher</div>
            <div className="grid grid-cols-2 gap-1">
              {[
                { key: 'COPPER', label: 'Copper' },
                { key: 'MM_FIBRE', label: 'MM Fibre' },
                { key: 'SM_FIBRE', label: 'SM Fibre' },
                { key: 'WIRELESS', label: 'Wireless' }
              ].map(cable => (
                <button
                  key={cable.key}
                  onClick={() => { onSwitchCable(targetConn.id, cable.key); onClose(); }}
                  className={`px-2 py-1 rounded text-[11px] font-medium border text-left transition-colors ${(targetConn.cable || 'COPPER') === cable.key ? 'bg-blue-500/20 border-blue-500 text-blue-400 font-bold' : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle} hover:bg-slate-100 dark:hover:bg-slate-800`}`}
                >
                  {cable.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { onAddBendPoint(targetConn.id, contextMenu.worldX, contextMenu.worldY); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Zap size={13} className="text-yellow-400" />
            <span>Add Bend Point</span>
          </button>

          {targetConn.controlPoint && (
            <button
              onClick={() => { onResetPath(targetConn.id); onClose(); }}
              className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
            >
              <RotateCcw size={13} className="text-slate-400" />
              <span>Reset Path</span>
            </button>
          )}

          <button
            onClick={() => { onToggleAnimation(targetConn.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Activity size={13} className="text-cyan-400" />
            <span>{targetConn.animated ? "Disable Animation" : "Toggle Animation"}</span>
          </button>

          <button
            onClick={() => { onToggleLabels(targetConn.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            {(targetConn.showSourceLabel !== false || targetConn.showTargetLabel !== false) ? <EyeOff size={13} className="text-slate-400" /> : <Eye size={13} className="text-slate-400" />}
            <span>Toggle Labels</span>
          </button>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { onDeleteLink(targetConn.id); onClose(); }}
            className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
            <span>Delete Link</span>
          </button>
        </div>
      )}

      {/* 3. SHAPES & ANNOTATIONS MENU */}
      {contextMenu.targetType === 'SHAPE' && (
        <div className="space-y-0.5">
          <div className={`px-3 py-1.5 border-b ${styles.panelBorder} flex items-center justify-between ${styles.panelHeaderBg}`}>
            <span className="font-bold text-[11px] truncate">{targetNode?.label || targetNode?.type || "Shape / Annotation"}</span>
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-bold tracking-wider">Shape</span>
          </div>

          {/* Presets */}
          <div className="px-3 py-1.5">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Presets</div>
            <div className="space-y-1">
              <button
                onClick={() => { if (targetNode) onApplyShapePreset(targetNode.id, { color: '#ef4444', borderStyle: 'dashed', borderWidth: 2 }); onClose(); }}
                className="w-full px-2 py-1 rounded text-left flex items-center gap-2 border border-red-500/30 hover:border-red-500 bg-red-500/5 hover:bg-red-500/10 text-red-400 font-medium"
              >
                <Square size={12} className="text-red-500" /> Subnet Box (Red dashed)
              </button>
              <button
                onClick={() => { if (targetNode) onApplyShapePreset(targetNode.id, { color: '#3b82f6', borderStyle: 'solid', borderWidth: 2 }); onClose(); }}
                className="w-full px-2 py-1 rounded text-left flex items-center gap-2 border border-blue-500/30 hover:border-blue-500 bg-blue-500/5 hover:bg-blue-500/10 text-blue-400 font-medium"
              >
                <Square size={12} className="text-blue-500" /> VLAN Boundary (Blue solid)
              </button>
              <button
                onClick={() => { if (targetNode) onApplyShapePreset(targetNode.id, { color: '#f97316', borderStyle: 'dotted', borderWidth: 2 }); onClose(); }}
                className="w-full px-2 py-1 rounded text-left flex items-center gap-2 border border-orange-500/30 hover:border-orange-500 bg-orange-500/5 hover:bg-orange-500/10 text-orange-400 font-medium"
              >
                <Square size={12} className="text-orange-500" /> DMZ Zone (Orange dotted)
              </button>
            </div>
          </div>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { if (targetNode) onSendShapeToBackground(targetNode.id); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <SendToBack size={13} />
            <span>Send to Background (z-0)</span>
          </button>

          <button
            onClick={() => { onDeleteSelection(); onClose(); }}
            className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* 4. EMPTY CANVAS MENU */}
      {contextMenu.targetType === 'CANVAS' && (
        <div className="space-y-0.5">
          <div className={`px-3 py-1.5 border-b ${styles.panelBorder} flex items-center justify-between ${styles.panelHeaderBg}`}>
            <span className="font-bold text-[11px]">Canvas Menu</span>
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-bold tracking-wider">Canvas</span>
          </div>

          {/* Quick Add Submenu */}
          <div className="px-3 py-1.5">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Quick Add Device</div>
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => { onQuickAddNode('ROUTER'); onClose(); }}
                className={`px-2 py-1.5 rounded text-left flex items-center gap-1.5 border ${styles.inputBorder} ${styles.inputBg} hover:border-orange-500 hover:text-orange-400 transition-colors`}
              >
                <Router size={13} className="text-orange-500" /> Router
              </button>
              <button
                onClick={() => { onQuickAddNode('SWITCH'); onClose(); }}
                className={`px-2 py-1.5 rounded text-left flex items-center gap-1.5 border ${styles.inputBorder} ${styles.inputBg} hover:border-blue-500 hover:text-blue-400 transition-colors`}
              >
                <SwitchCamera size={13} className="text-blue-500" /> Switch
              </button>
              <button
                onClick={() => { onQuickAddNode('PC'); onClose(); }}
                className={`px-2 py-1.5 rounded text-left flex items-center gap-1.5 border ${styles.inputBorder} ${styles.inputBg} hover:border-slate-400 hover:text-slate-300 transition-colors`}
              >
                <Monitor size={13} className="text-slate-400" /> PC
              </button>
              <button
                onClick={() => { onQuickAddNode('TEXT'); onClose(); }}
                className={`px-2 py-1.5 rounded text-left flex items-center gap-1.5 border ${styles.inputBorder} ${styles.inputBg} hover:border-purple-500 hover:text-purple-400 transition-colors`}
              >
                <Type size={13} className="text-purple-400" /> Text Box
              </button>
            </div>
          </div>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            disabled={!clipboard || !clipboard.nodes || clipboard.nodes.length === 0}
            onClick={() => { onPasteAtCursor(contextMenu.worldX, contextMenu.worldY); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center justify-between text-left ${(!clipboard || !clipboard.nodes || clipboard.nodes.length === 0) ? 'opacity-40 cursor-not-allowed' : `${styles.buttonHover} text-blue-400`} transition-colors`}
          >
            <div className="flex items-center gap-2">
              <Clipboard size={13} />
              <span>Paste at Cursor</span>
            </div>
          </button>

          <button
            onClick={() => { onCenterView(); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Maximize size={13} />
            <span>Center View & Reset Zoom</span>
          </button>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          {/* Grid & Snapping Toggles */}
          <div className="grid grid-cols-2 gap-1 px-3 py-1">
            <button
              onClick={() => { onToggleGrid(); onClose(); }}
              className={`px-2 py-1.5 rounded border text-left flex items-center gap-1.5 ${showGrid ? 'bg-blue-500/15 border-blue-500/40 text-blue-400 font-semibold' : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle}`}`}
            >
              <Grid size={13} /> Grid: {showGrid ? "On" : "Off"}
            </button>
            <button
              onClick={() => { onToggleSnapping(); onClose(); }}
              className={`px-2 py-1.5 rounded border text-left flex items-center gap-1.5 ${isSnapping ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400 font-semibold' : `${styles.inputBg} ${styles.inputBorder} ${styles.textSubtle}`}`}
            >
              <Magnet size={13} /> Snap: {isSnapping ? "On" : "Off"}
            </button>
          </div>
        </div>
      )}

      {/* 5. MULTI-SELECTION MENU */}
      {contextMenu.targetType === 'MULTI' && (
        <div className="space-y-0.5">
          <div className={`px-3 py-1.5 border-b ${styles.panelBorder} flex items-center justify-between ${styles.panelHeaderBg}`}>
            <span className="font-bold text-[11px]">Multi-Selection</span>
            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-bold tracking-wider">{selectedIds.length} Items</span>
          </div>

          {/* Align Tools */}
          <div className="px-3 py-1.5">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Align Tools</div>
            <div className="grid grid-cols-5 gap-1">
              <button onClick={() => { onAlignNodes('left'); onClose(); }} className={`p-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center justify-center`} title="Align Left"><AlignLeft size={13} /></button>
              <button onClick={() => { onAlignNodes('center'); onClose(); }} className={`p-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center justify-center`} title="Align Center"><AlignCenter size={13} /></button>
              <button onClick={() => { onAlignNodes('right'); onClose(); }} className={`p-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center justify-center`} title="Align Right"><AlignRight size={13} /></button>
              <button onClick={() => { onAlignNodes('top'); onClose(); }} className={`p-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center justify-center text-[10px] font-bold`} title="Align Top">Top</button>
              <button onClick={() => { onAlignNodes('bottom'); onClose(); }} className={`p-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center justify-center text-[10px] font-bold`} title="Align Bottom">Btm</button>
            </div>
          </div>

          {/* Distribute Spacing */}
          <div className="px-3 py-1">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${styles.textSubtle}`}>Distribute Spacing</div>
            <div className="grid grid-cols-2 gap-1">
              <button onClick={() => { onDistributeNodes('horizontal'); onClose(); }} className={`px-2 py-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center gap-1.5 text-[11px]`}>
                <MoveHorizontal size={13} /> Horizontal
              </button>
              <button onClick={() => { onDistributeNodes('vertical'); onClose(); }} className={`px-2 py-1.5 rounded border ${styles.inputBg} ${styles.inputBorder} hover:text-blue-400 flex items-center gap-1.5 text-[11px]`}>
                <MoveVertical size={13} /> Vertical
              </button>
            </div>
          </div>

          <div className={`border-t ${styles.panelBorder} my-1`} />

          <button
            onClick={() => { onGroupSelection(); onClose(); }}
            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left ${styles.buttonHover} transition-colors`}
          >
            <Box size={13} className="text-blue-400" />
            <span>Group into Box (z-0)</span>
          </button>

          <button
            onClick={() => { onDeleteSelection(); onClose(); }}
            className="w-full px-3 py-1.5 flex items-center gap-2 text-left text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
            <span>Delete Selection</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default function App() {
  // Theme & View State
  const [theme, setTheme] = useState("dark");
  const [selectedBrand, setSelectedBrand] = useState("default");
  const isDark = theme === "dark";
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  
  // Logic State
  const { projects, setProjects, activeProjectId, setActiveProjectId, activeProject, setNodes, setConnections, updateProject } = useProjectManager();
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [selectionType, setSelectionType] = useState<any>(null); // 'NODE' | 'CONNECTION'
  const [mode, setMode] = useState("SELECT");
  const [clipboard, setClipboard] = useState<any>(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const [showGrid, setShowGrid] = useState(true);

  // Interaction State
  const [connectSourceId, setConnectSourceId] = useState<any>(null);
  const [pendingLink, setPendingLink] = useState<any>(null); // {source, target} for modal
  const [dragState, setDragState] = useState<any>(null); // { id, startX, startY, offsetX, offsetY, type: 'move'|'resize'|'pan' }
  const [selectionBox, setSelectionBox] = useState<any>(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [panStruggleCount, setPanStruggleCount] = useState(0);
  const [showPanTip, setShowPanTip] = useState(false);
  
  // Tab Management State
  const [openMenuId, setOpenMenuId] = useState<any>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 }); // New state for menu position
  const [editingProjectId, setEditingProjectId] = useState<any>(null);
  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const [interfaceToDelete, setInterfaceToDelete] = useState<any>(null); // { nodeId, ifaceId, isSub, parentIfId }

  // Interactive Naming Modal and DoS Guard State
  const [isNamingModalOpen, setIsNamingModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [isMaxProjectsAlertOpen, setIsMaxProjectsAlertOpen] = useState(false);

  // Undo / Redo History Stack State
  const [history, setHistory] = useState<{ past: any[]; future: any[] }>({ past: [], future: [] });

  // Template Modal State
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  // Canvas Minimap & Context Menu State
  const [showMinimap, setShowMinimap] = useState(true);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    worldX: number;
    worldY: number;
    targetType: 'NODE' | 'CONNECTION' | 'SHAPE' | 'MULTI' | 'CANVAS';
    targetId?: string;
  } | null>(null);

  // Close context menu on Escape or click outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    const handleMouseDownGlobal = (e: MouseEvent) => {
      if (e.button === 0) {
        setContextMenu(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDownGlobal);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDownGlobal);
    };
  }, [contextMenu]);

  // Clear History on Tab Switch
  useEffect(() => {
    setHistory({ past: [], future: [] });
  }, [activeProjectId]);

  // Derived state for Panning UI
  const isPanning = dragState?.type === 'pan';

  // UI Overlays
  const [showCalculator, setShowCalculator] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [isBackgroundSettingsOpen, setIsBackgroundSettingsOpen] = useState(false);
  const [runTutorial, setRunTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [hasRunTutorialSession, setHasRunTutorialSession] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any[] | null>(null);
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);
  
  const canvasRef = useRef<any>(null);
  // FIX: Ref to track the last touch event timestamp to prevent ghost clicks on the overlay
  const lastTouchTimeRef = useRef(0);
  // FIX: Ref to track if the current drag operation has moved significantly (to distinguish tap vs drag)
  const isDraggingMovedRef = useRef(false);

  // Styles
  const styles = useMemo(() => ({
    bg: isDark ? "bg-slate-900" : "bg-slate-50", 
    text: isDark ? "text-slate-100" : "text-slate-900", 
    textSubtle: isDark ? "text-slate-400" : "text-slate-600",
    panelBg: isDark ? "bg-slate-950" : "bg-white", 
    panelBorder: isDark ? "border-slate-800" : "border-slate-200", 
    panelHeaderBg: isDark ? "bg-slate-900/50" : "bg-slate-50/50",
    inputBg: isDark ? "bg-slate-950" : "bg-white", 
    inputBorder: isDark ? "border-slate-800" : "border-slate-300", 
    cardBg: isDark ? "bg-slate-900" : "bg-slate-50",
    buttonHover: isDark ? "hover:bg-slate-800" : "hover:bg-slate-100", 
    modalBg: isDark ? "bg-slate-900" : "bg-white", 
    modalOverlay: isDark ? "bg-slate-950/80" : "bg-slate-200/80",
    label: isDark ? "text-slate-500" : "text-slate-700" // Added helper for labels
  }), [isDark]);

  // Initialization & Events
  
  // --- History & Actions ---

  const takeSnapshot = useCallback(() => {
    setHistory(prev => ({
      past: [...prev.past, { nodes: activeProject.nodes, connections: activeProject.connections }],
      future: []
    }));
  }, [activeProject.nodes, activeProject.connections]);

  const handleUndo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, prev.past.length - 1);
      const current = { nodes: activeProject.nodes, connections: activeProject.connections };

      setNodes(previous.nodes);
      setConnections(previous.connections);

      return {
        past: newPast,
        future: [...prev.future, current]
      };
    });
  }, [activeProject.nodes, activeProject.connections, setNodes, setConnections]);

  const handleRedo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[prev.future.length - 1];
      const newFuture = prev.future.slice(0, prev.future.length - 1);
      const current = { nodes: activeProject.nodes, connections: activeProject.connections };

      setNodes(next.nodes);
      setConnections(next.connections);

      return {
        past: [...prev.past, current],
        future: newFuture
      };
    });
  }, [activeProject.nodes, activeProject.connections, setNodes, setConnections]);

  const handleSelectTemplate = useCallback((template: any) => {
    if (projects.length >= MAX_PROJECTS) {
      setIsTemplateModalOpen(false);
      setIsMaxProjectsAlertOpen(true);
      return;
    }
    const uniqueName = getUniqueProjectName(template.name, projects);
    const id = generateId("proj");

    setProjects(p => [...p, {
      id,
      name: uniqueName,
      color: "BLUE",
      isLocked: false,
      nodes: JSON.parse(JSON.stringify(template.nodes)),
      connections: JSON.parse(JSON.stringify(template.connections)),
      zoom: 1,
      viewOffset: { x: 0, y: 0 },
      customBackground: null,
      customBackgroundColor: null
    }]);
    setActiveProjectId(id);
    setIsTemplateModalOpen(false);
  }, [projects, setProjects, setActiveProjectId]);

  const copyToClipboard = useCallback(() => {
    if (selectedIds.length === 0) return;
    const nodes = activeProject.nodes.filter(n => selectedIds.includes(n.id));
    const conns = activeProject.connections.filter(c => selectedIds.includes(c.id) || (selectedIds.includes(c.from) && selectedIds.includes(c.to)));
    if (nodes && conns) {
        setClipboard({ nodes, conns });
    }
  }, [selectedIds, activeProject]);

  const pasteFromClipboard = useCallback(() => {
    if (!clipboard || !clipboard.nodes) return;

    // Security Fix 6: Check for DoS (Paste)
    if (activeProject.nodes.length + clipboard.nodes.length > MAX_NODES) return;

    const idMap = new Map();
    const newNodes = clipboard.nodes.map(n => {
       const newId = generateId("node");
       idMap.set(n.id, newId);
       // Deep copy interfaces - safely handle nodes without interfaces (e.g., text/shape)
       const newIfs = (n.interfaces || []).map(i => {
         const iId = generateId("if"); idMap.set(i.id, iId);
         return { ...i, id: iId, subInterfaces: (i.subInterfaces || []).map(s => { const sId = generateId("sub"); idMap.set(s.id, sId); return {...s, id: sId}; })};
       });
       return { ...n, id: newId, x: n.x + 20, y: n.y + 20, interfaces: newIfs };
    });
    
    // Safety check for connections
    const newConns = (clipboard.conns || []).map(c => {
       const from = idMap.get(c.from), to = idMap.get(c.to);
       if(!from || !to) return null;
       return { ...c, id: generateId("conn"), from, to, fromIf: idMap.get(c.fromIf), toIf: idMap.get(c.toIf) };
    }).filter(Boolean);

    setNodes(prev => [...prev, ...newNodes]);
    setConnections(prev => [...prev, ...newConns]);
    setSelectedIds(newNodes.map(n => n.id));
    setSelectionType("NODE");
  }, [clipboard, setNodes, setConnections, activeProject]);

  const deleteSelection = useCallback(() => {
    if (selectedIds.length === 0) return;
    takeSnapshot();
    if(selectionType === "NODE") {
      setNodes(prev => prev.filter(n => !selectedIds.includes(n.id)));
      setConnections(prev => prev.filter(c => !selectedIds.includes(c.from) && !selectedIds.includes(c.to)));
    } else {
      setConnections(prev => prev.filter(c => !selectedIds.includes(c.id)));
    }
    setSelectedIds([]);
  }, [selectedIds, selectionType, setNodes, setConnections, takeSnapshot]);
  
  const performDeleteInterface = useCallback((nodeId, ifaceId, isSub, parentIfId) => {
      takeSnapshot();
      // Remove associated connections
      setConnections(prev => prev.filter(c => !((c.from === nodeId && c.fromIf === ifaceId) || (c.to === nodeId && c.toIf === ifaceId))));

      // Remove interface from node
      setNodes(prev => prev.map(n => {
        if (n.id !== nodeId) return n;
        
        if (isSub) {
           // Handle Sub-Interface deletion
           return { 
             ...n, 
             interfaces: n.interfaces.map(i => 
               i.id === parentIfId ? { ...i, subInterfaces: i.subInterfaces.filter(s => s.id !== ifaceId) } : i
             ) 
           };
        }
        
        // Handle Main Interface deletion
        return { ...n, interfaces: n.interfaces.filter(i => i.id !== ifaceId) };
      }));
  }, [setConnections, setNodes, takeSnapshot]);

  const requestDeleteInterface = useCallback((nodeId, ifaceId, isSub = false, parentIfId = null) => {
     // Check if any connections use this interface
     const hasLinks = activeProject.connections.some(c => (c.from === nodeId && c.fromIf === ifaceId) || (c.to === nodeId && c.toIf === ifaceId));
     
     if (hasLinks) {
        setInterfaceToDelete({ nodeId, ifaceId, isSub, parentIfId });
     } else {
        performDeleteInterface(nodeId, ifaceId, isSub, parentIfId);
     }
  }, [activeProject.connections, performDeleteInterface]);

  const handleSelectAll = useCallback((e) => {
    e?.preventDefault();
    const allIds = activeProject.nodes.map(n => n.id);
    setSelectedIds(allIds);
    setSelectionType("NODE");
  }, [activeProject]);

  const exportProjectToFile = useCallback(() => {
    try {
      const dataStr = JSON.stringify(projects, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const projectNameClean = activeProject.name.replace(/[^a-zA-Z0-9]/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const timeDateStamp = `${year}${month}${day}_${hours}${minutes}`;

      link.download = `NetMapper_Project_${projectNameClean}_${timeDateStamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to save layout configuration.");
    }
  }, [projects, activeProject]);

  const handleOpenLayout = useCallback(() => {
    document.getElementById("project-file-import")?.click();
  }, []);

  const handleImportFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // JSON Import Size Cap: Limit configuration file uploads to 5MB before parsing
    if (file.size > 5 * 1024 * 1024) {
      alert("File is too large. Configuration file import is limited to 5MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const raw = JSON.parse(event.target?.result as string);
        const sanitized = sanitizeImportedData(raw);
        if (sanitized && sanitized.length > 0) {
          setPendingImportData(sanitized);
        } else {
          alert("Imported file contains no valid network diagrams.");
        }
      } catch (err) {
        alert("Failed to parse JSON configuration file. Please ensure it is valid JSON.");
      }
      e.target.value = "";
    };
    reader.readAsText(file);
  }, []);

  const handleOverwriteImport = useCallback(() => {
    if (!pendingImportData) return;
    setProjects(pendingImportData);
    setActiveProjectId(pendingImportData[0].id);
    setPendingImportData(null);
  }, [pendingImportData, setProjects, setActiveProjectId]);

  const handleAppendImport = useCallback(() => {
    if (!pendingImportData) return;
    const mappedAppended = pendingImportData.map(proj => {
      // Create new randomized unique key for each project .id block to prevent duplicate tab indexing
      const newProjId = generateId("proj");
      return {
        ...proj,
        id: newProjId
      };
    });
    setProjects(prev => [...prev, ...mappedAppended]);
    setActiveProjectId(mappedAppended[0].id);
    setPendingImportData(null);
  }, [pendingImportData, setProjects, setActiveProjectId]);

  const handleCreateProject = useCallback(() => {
    const sanitized = sanitizeProjectName(newProjectName);
    const uniqueName = getUniqueProjectName(sanitized, projects);
    const id = generateId("proj");
    
    setProjects(p => [...p, {
      id,
      name: uniqueName,
      color: "SLATE",
      isLocked: false,
      nodes: [],
      connections: [],
      zoom: 1,
      viewOffset: { x: 0, y: 0 },
      customBackground: null,
      customBackgroundColor: null
    }]);
    setActiveProjectId(id);
    setIsNamingModalOpen(false);
    setNewProjectName("");
  }, [newProjectName, projects, setProjects, setActiveProjectId]);

  const handleExport = async () => {
    if (!canvasRef.current || isExporting) return;
    setIsExporting(true);
    // Wait for render cycle to ensure watermark is visible in DOM
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      let html2canvas = (window as any).html2canvas;
      if (!html2canvas) {
         await new Promise<void>((res, rej) => {
           const s = document.createElement("script");
           s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
           // Security Fix 1: SRI and CrossOrigin for external scripts
           s.integrity = "sha512-BNaRQnYJYiPSqHHDb58B0yaPfCu+Wgds8Gp/gU33kqBtgNS4tSPHuGibyoeqMV/TJlSKda6FXzoEyYGjTe+vXA==";
           s.crossOrigin = "anonymous";
           s.onload = () => { html2canvas = (window as any).html2canvas; res(); };
           s.onerror = () => rej(new Error("Failed to load html2canvas"));
           document.head.appendChild(s);
         });
      }
      const canvas = await html2canvas(canvasRef.current, { backgroundColor: activeProject.customBackgroundColor || (isDark ? "#0f172a" : "#f8fafc"), scale: 2, logging: false, useCORS: true });
      const link = document.createElement("a");
      link.download = `${activeProject.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.9);
      link.click();
    } catch (e) { console.error(e); } finally { setIsExporting(false); }
  };

  const handlePrint = async () => {
    if (!canvasRef.current || isExporting) return;
    setIsExporting(true);
    // Wait for render cycle to ensure watermark is visible in DOM
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
      let html2canvas = (window as any).html2canvas;
      if (!html2canvas) {
          await new Promise<void>((res, rej) => {
             const s = document.createElement("script");
             s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
             // Security Fix 1: SRI and CrossOrigin for external scripts
             s.integrity = "sha512-BNaRQnYJYiPSqHHDb58B0yaPfCu+Wgds8Gp/gU33kqBtgNS4tSPHuGibyoeqMV/TJlSKda6FXzoEyYGjTe+vXA==";
             s.crossOrigin = "anonymous";
             s.onload = () => { html2canvas = (window as any).html2canvas; res(); };
             s.onerror = () => rej(new Error("Failed to load html2canvas"));
             document.head.appendChild(s);
          });
      }
      const canvas = await html2canvas(canvasRef.current, { backgroundColor: activeProject.customBackgroundColor || (isDark ? "#0f172a" : "#f8fafc"), scale: 2, logging: false, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        // Trusted Types compatibility for the new window
        if (typeof (printWindow as any).trustedTypes !== 'undefined') {
           try {
             (printWindow as any).trustedTypes.createPolicy('default', {
               createHTML: (string: string) => string,
               createScriptURL: (string: string) => string,
             });
           } catch (e) {
             // Policy might already exist
           }
        }

        printWindow.document.write(`
          <html>
            <head>
              <title>Print Diagram - NetMapper</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; }
                img { max-width: 100%; max-height: 100%; object-fit: contain; }
                @media print { body { display: block; } img { max-width: 100%; width: 100%; height: auto; } }
              </style>
            </head>
            <body>
              <img src="${imgData}" onload="window.print(); window.close();" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (e) { console.error("Print failed", e); } finally { setIsExporting(false); }
  };
  
  // FIX: Use ref to access latest functions in event listener (Stale Closure Fix)
  const actionsRef = useRef({
    copy: copyToClipboard,
    paste: pasteFromClipboard,
    delete: deleteSelection,
    selectAll: handleSelectAll,
    undo: handleUndo,
    redo: handleRedo
  });

  // Update ref when functions change
  useEffect(() => {
    actionsRef.current = {
      copy: copyToClipboard,
      paste: pasteFromClipboard,
      delete: deleteSelection,
      selectAll: handleSelectAll,
      undo: handleUndo,
      redo: handleRedo
    };
  }, [copyToClipboard, pasteFromClipboard, deleteSelection, handleSelectAll, handleUndo, handleRedo]);

  const handleGetStarted = () => {
    setShowAbout(false);
    // Trigger tutorial only if not seen before AND not already triggered in this session via this button
    if (!localStorage.getItem("netmapper_tutorial_seen")) {
        setRunTutorial(true);
        setHasRunTutorialSession(true);
    }
  };

  // Auto-dismiss pan tip after 8 seconds
  useEffect(() => {
    if (showPanTip) {
      const timer = setTimeout(() => {
        setShowPanTip(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showPanTip]);

  // FIX: Updated Event Listener with dependencies handling
  useEffect(() => {
    // First time visitor check
    if (!localStorage.getItem("netmapper_tutorial_seen")) {
        // Show About window automatically for first timers
        setTimeout(() => setShowAbout(true), 500); 
    }

    const handleKeyDown = (e: KeyboardEvent) => {
       const isInput = ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName || "");

       if (e.code === 'Space' && !isInput) {
           e.preventDefault();
           setIsSpacePressed(true);
           return;
       }

       // Shortcuts
       if (e.ctrlKey || e.metaKey) {
           if (e.key === "z" || e.key === "Z") {
               if (isInput) return;
               e.preventDefault();
               if (e.shiftKey) {
                   actionsRef.current.redo();
               } else {
                   actionsRef.current.undo();
               }
               return;
           }
           if (e.key === "y" || e.key === "Y") {
               if (isInput) return;
               e.preventDefault();
               actionsRef.current.redo();
               return;
           }
           if (e.key === "a" || e.key === "A") {
               if(isInput) return;
               actionsRef.current.selectAll(e);
           }
           if (e.key === "c" || e.key === "C") {
               if(isInput) return; 
               if (!window.getSelection()?.toString()) actionsRef.current.copy();
           }
           if (e.key === "v" || e.key === "V") {
               if(isInput) return;
               actionsRef.current.paste();
           }
       }

       if (e.key === "Delete" || e.key === "Backspace") {
           if(isInput) return;
           actionsRef.current.delete();
       }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
       if (e.code === 'Space') {
           setIsSpacePressed(false);
       }
    };

    const handleBlur = () => {
       setIsSpacePressed(false);
    };

    window.addEventListener("keydown", handleKeyDown); 
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => { 
        window.removeEventListener("keydown", handleKeyDown); 
        window.removeEventListener("keyup", handleKeyUp);
        window.removeEventListener("blur", handleBlur);
    };
  }, []); // Empty dependency array = stable listener

  // --- Mouse & Touch Handlers (Ref-based for Stability) ---
  
  // Create refs to hold latest state values without causing re-renders
  const activeProjectRef = useRef(activeProject);
  useLayoutEffect(() => { activeProjectRef.current = activeProject; });

  const dragStateRef = useRef(dragState);
  useLayoutEffect(() => { dragStateRef.current = dragState; }, [dragState]);

  const selectionBoxRef = useRef(selectionBox);
  useLayoutEffect(() => { selectionBoxRef.current = selectionBox; }, [selectionBox]);

  const handleResizeMouseDown = useCallback((e, node, handle) => {
    if (isSpacePressed || mode === "PAN") return;
    e.stopPropagation(); // Stop node selection/drag
    takeSnapshot();
    // Support both mouse and touch events for coords
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    if (clientX === undefined || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const pos = {
       x: (clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom,
       y: (clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom
    };

    setDragState({
        type: 'resize',
        id: node.id,
        handle: handle,
        startX: pos.x,
        startY: pos.y,
        initial: { x: node.x, y: node.y, w: node.width, h: node.height, endX: (node as any).endX, endY: (node as any).endY }
    });
  }, [activeProject, takeSnapshot, isSpacePressed, mode]);

  
  // ==========================================
  // Context-Aware Menu & Minimap Action Handlers
  // ==========================================
  const handleDuplicateNode = useCallback((targetNode: any) => {
    if (!targetNode) return;
    if (activeProject.nodes.length >= MAX_NODES) return;
    takeSnapshot();
    const newId = generateId("node");
    const idMap = new Map();
    const newIfs = (targetNode.interfaces || []).map((i: any) => {
      const iId = generateId("if");
      idMap.set(i.id, iId);
      return {
        ...i,
        id: iId,
        subInterfaces: (i.subInterfaces || []).map((s: any) => {
          const sId = generateId("sub");
          idMap.set(s.id, sId);
          return { ...s, id: sId };
        })
      };
    });
    const clonedNode = {
      ...targetNode,
      id: newId,
      x: targetNode.x + 20,
      y: targetNode.y + 20,
      interfaces: newIfs,
      name: targetNode.name ? `${targetNode.name} (Copy)` : targetNode.name
    };
    setNodes(prev => [...prev, clonedNode]);
    setSelectedIds([newId]);
    setSelectionType("NODE");
  }, [activeProject.nodes, takeSnapshot, setNodes]);

  const handleToggleLockNode = useCallback((nodeId: string) => {
    takeSnapshot();
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, isLocked: !n.isLocked } : n));
  }, [setNodes, takeSnapshot]);

  const handleRecolorNode = useCallback((nodeId: string, color?: string) => {
    takeSnapshot();
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, color } : n));
  }, [setNodes, takeSnapshot]);

  const handleAddPort = useCallback((targetNode: any) => {
    if (!targetNode) return;
    takeSnapshot();
    const isAp = targetNode.type === "AP";
    const newIf = isAp
      ? { id: generateId("if"), name: `SSID_${(targetNode.interfaces || []).filter((i: any) => i.type === "SSID").length + 1}`, type: "SSID", ip: "", subInterfaces: [] }
      : { id: generateId("if"), name: `eth${(targetNode.interfaces || []).filter((i: any) => i.type === "PHYSICAL").length}`, type: "PHYSICAL", ip: "", subInterfaces: [] };
    setNodes(prev => prev.map(n => n.id === targetNode.id ? { ...n, interfaces: [...(n.interfaces || []), newIf] } : n));
  }, [setNodes, takeSnapshot]);

  const handleCopyPrimaryIp = useCallback((targetNode: any) => {
    if (!targetNode) return;
    const ip = targetNode.interfaces?.find((i: any) => i.ip && i.ip.trim() !== '')?.ip
      || targetNode.interfaces?.[0]?.ip
      || '';
    if (ip && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(ip);
    }
  }, []);

  const handleBringToFront = useCallback((nodeId: string) => {
    takeSnapshot();
    setNodes(prev => {
      const found = prev.find(n => n.id === nodeId);
      return found ? [...prev.filter(n => n.id !== nodeId), found] : prev;
    });
  }, [setNodes, takeSnapshot]);

  const handleSendToBack = useCallback((nodeId: string) => {
    takeSnapshot();
    setNodes(prev => {
      const found = prev.find(n => n.id === nodeId);
      return found ? [found, ...prev.filter(n => n.id !== nodeId)] : prev;
    });
  }, [setNodes, takeSnapshot]);

  const handleSwitchCable = useCallback((connId: string, cable: string) => {
    takeSnapshot();
    setConnections(prev => prev.map(c => c.id === connId ? { ...c, cable } : c));
  }, [setConnections, takeSnapshot]);

  const handleAddBendPoint = useCallback((connId: string, x: number, y: number) => {
    takeSnapshot();
    setConnections(prev => prev.map(c => c.id === connId ? { ...c, controlPoint: { x, y, type: 'curve' } } : c));
  }, [setConnections, takeSnapshot]);

  const handleResetPath = useCallback((connId: string) => {
    takeSnapshot();
    setConnections(prev => prev.map(c => c.id === connId ? { ...c, controlPoint: undefined } : c));
  }, [setConnections, takeSnapshot]);

  const handleToggleAnimation = useCallback((connId: string) => {
    takeSnapshot();
    setConnections(prev => prev.map(c => c.id === connId ? { ...c, animated: !c.animated } : c));
  }, [setConnections, takeSnapshot]);

  const handleToggleLabels = useCallback((connId: string) => {
    takeSnapshot();
    setConnections(prev => prev.map(c => {
      if (c.id !== connId) return c;
      const currentVisible = (c.showSourceLabel !== false || c.showTargetLabel !== false);
      return { ...c, showSourceLabel: !currentVisible, showTargetLabel: !currentVisible };
    }));
  }, [setConnections, takeSnapshot]);

  const handleDeleteLink = useCallback((connId: string) => {
    takeSnapshot();
    setConnections(prev => prev.filter(c => c.id !== connId));
    setSelectedIds([]);
    setSelectionType(null);
  }, [setConnections, takeSnapshot]);

  const handleApplyShapePreset = useCallback((shapeId: string, preset: { color: string; borderStyle: string; borderWidth: number }) => {
    takeSnapshot();
    setNodes(prev => prev.map(n => n.id === shapeId ? {
      ...n,
      color: preset.color,
      borderStyle: preset.borderStyle,
      borderWidth: preset.borderWidth
    } : n));
  }, [setNodes, takeSnapshot]);

  const handleSendShapeToBackground = useCallback((shapeId: string) => {
    takeSnapshot();
    setNodes(prev => {
      const found = prev.find(n => n.id === shapeId);
      return found ? [found, ...prev.filter(n => n.id !== shapeId)] : prev;
    });
  }, [setNodes, takeSnapshot]);

  const handleQuickAddNode = useCallback((type: string) => {
    takeSnapshot();
    if (activeProject.nodes.length >= MAX_NODES) return;
    const worldPos = contextMenu ? { x: contextMenu.worldX, y: contextMenu.worldY } : { x: 0, y: 0 };
    let newNode: any;
    if (type === "TEXT") {
      newNode = { id: generateId("txt"), type: "TEXT", x: snapValue(worldPos.x, isSnapping), y: snapValue(worldPos.y, isSnapping), content: "New Label", fontSize: 14 };
    } else if (type === "RECTANGLE" || type === "CIRCLE" || type === "LINE" || type === "SHAPE") {
      const shapeType = type === "CIRCLE" ? "CIRCLE" : type === "LINE" ? "LINE" : "RECTANGLE";
      newNode = {
        id: generateId("shape"),
        type: "SHAPE",
        shapeType,
        x: snapValue(worldPos.x, isSnapping),
        y: snapValue(worldPos.y, isSnapping),
        color: "#3b82f6",
        borderWidth: 4,
        borderStyle: "solid"
      };
      if (shapeType === "LINE") {
        newNode.endX = snapValue(worldPos.x + 150, isSnapping);
        newNode.endY = snapValue(worldPos.y + 100, isSnapping);
      } else {
        newNode.width = 140;
        newNode.height = 140;
      }
    } else {
      newNode = createNodeObject(type, snapValue(worldPos.x, isSnapping), snapValue(worldPos.y, isSnapping), activeProject.nodes, selectedBrand);
    }
    setNodes(prev => [...prev, newNode]);
    setSelectedIds([newNode.id]);
    setSelectionType("NODE");
    setMode("SELECT");
  }, [activeProject.nodes, contextMenu, isSnapping, selectedBrand, setNodes, takeSnapshot]);

  const handlePasteAtCursor = useCallback((worldX: number, worldY: number) => {
    if (!clipboard || !clipboard.nodes || clipboard.nodes.length === 0) return;
    if (activeProject.nodes.length + clipboard.nodes.length > MAX_NODES) return;
    takeSnapshot();

    const minClipX = Math.min(...clipboard.nodes.map((n: any) => n.x));
    const minClipY = Math.min(...clipboard.nodes.map((n: any) => n.y));

    const idMap = new Map();
    const newNodes = clipboard.nodes.map((n: any) => {
      const newId = generateId("node");
      idMap.set(n.id, newId);
      const newIfs = (n.interfaces || []).map((i: any) => {
        const iId = generateId("if");
        idMap.set(i.id, iId);
        return {
          ...i,
          id: iId,
          subInterfaces: (i.subInterfaces || []).map((s: any) => {
            const sId = generateId("sub");
            idMap.set(s.id, sId);
            return { ...s, id: sId };
          })
        };
      });
      const offsetX = n.x - minClipX;
      const offsetY = n.y - minClipY;
      return {
        ...n,
        id: newId,
        x: snapValue(worldX + offsetX, isSnapping),
        y: snapValue(worldY + offsetY, isSnapping),
        interfaces: newIfs
      };
    });

    const newConns = (clipboard.conns || []).map((c: any) => {
      const from = idMap.get(c.from);
      const to = idMap.get(c.to);
      if (!from || !to) return null;
      return {
        ...c,
        id: generateId("conn"),
        from,
        to,
        fromIf: idMap.get(c.fromIf),
        toIf: idMap.get(c.toIf)
      };
    }).filter(Boolean);

    setNodes(prev => [...prev, ...newNodes]);
    setConnections(prev => [...prev, ...newConns]);
    setSelectedIds(newNodes.map((n: any) => n.id));
    setSelectionType("NODE");
  }, [clipboard, activeProject.nodes.length, isSnapping, setNodes, setConnections, takeSnapshot]);

  const handleCenterView = useCallback(() => {
    updateProject(p => ({ ...p, viewOffset: { x: 0, y: 0 }, zoom: 1 }));
  }, [updateProject]);

  const handleAlignNodes = useCallback((direction: 'left' | 'center' | 'right' | 'top' | 'bottom') => {
    const selNodes = activeProject.nodes.filter(n => selectedIds.includes(n.id));
    if (selNodes.length <= 1) return;
    takeSnapshot();

    if (direction === 'left') {
      const minX = Math.min(...selNodes.map(n => n.x));
      setNodes(prev => prev.map(n => selectedIds.includes(n.id) ? { ...n, x: minX } : n));
    } else if (direction === 'center') {
      const centerXs = selNodes.map(n => n.x + (n.width || NODE_WIDTH) / 2);
      const avgCenter = centerXs.reduce((a, b) => a + b, 0) / centerXs.length;
      setNodes(prev => prev.map(n => selectedIds.includes(n.id) ? { ...n, x: Math.round(avgCenter - (n.width || NODE_WIDTH) / 2) } : n));
    } else if (direction === 'right') {
      const maxRight = Math.max(...selNodes.map(n => n.x + (n.width || NODE_WIDTH)));
      setNodes(prev => prev.map(n => selectedIds.includes(n.id) ? { ...n, x: maxRight - (n.width || NODE_WIDTH) } : n));
    } else if (direction === 'top') {
      const minY = Math.min(...selNodes.map(n => n.y));
      setNodes(prev => prev.map(n => selectedIds.includes(n.id) ? { ...n, y: minY } : n));
    } else if (direction === 'bottom') {
      const maxBottom = Math.max(...selNodes.map(n => n.y + (n.height || NODE_HEIGHT)));
      setNodes(prev => prev.map(n => selectedIds.includes(n.id) ? { ...n, y: maxBottom - (n.height || NODE_HEIGHT) } : n));
    }
  }, [activeProject.nodes, selectedIds, setNodes, takeSnapshot]);

  const handleDistributeNodes = useCallback((axis: 'horizontal' | 'vertical') => {
    const selNodes = activeProject.nodes.filter(n => selectedIds.includes(n.id));
    if (selNodes.length <= 2) return;
    takeSnapshot();

    if (axis === 'horizontal') {
      const sorted = [...selNodes].sort((a, b) => a.x - b.x);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const step = (last.x - first.x) / (sorted.length - 1);
      const newXMap = new Map();
      sorted.forEach((n, i) => newXMap.set(n.id, Math.round(first.x + i * step)));
      setNodes(prev => prev.map(n => newXMap.has(n.id) ? { ...n, x: newXMap.get(n.id) } : n));
    } else {
      const sorted = [...selNodes].sort((a, b) => a.y - b.y);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const step = (last.y - first.y) / (sorted.length - 1);
      const newYMap = new Map();
      sorted.forEach((n, i) => newYMap.set(n.id, Math.round(first.y + i * step)));
      setNodes(prev => prev.map(n => newYMap.has(n.id) ? { ...n, y: newYMap.get(n.id) } : n));
    }
  }, [activeProject.nodes, selectedIds, setNodes, takeSnapshot]);

  const handleGroupSelection = useCallback(() => {
    const selNodes = activeProject.nodes.filter(n => selectedIds.includes(n.id));
    if (selNodes.length === 0) return;
    takeSnapshot();

    const minX = Math.min(...selNodes.map(n => n.x)) - 20;
    const minY = Math.min(...selNodes.map(n => n.y)) - 20;
    const maxX = Math.max(...selNodes.map(n => n.x + (n.width || NODE_WIDTH))) + 20;
    const maxY = Math.max(...selNodes.map(n => n.y + (n.height || NODE_HEIGHT))) + 20;

    const groupShape = {
      id: generateId("shape"),
      type: "SHAPE",
      shapeType: "RECTANGLE",
      x: snapValue(minX, isSnapping),
      y: snapValue(minY, isSnapping),
      width: snapValue(maxX - minX, isSnapping),
      height: snapValue(maxY - minY, isSnapping),
      color: "#3b82f6",
      borderStyle: "solid",
      borderWidth: 2,
      label: "Group"
    };
    setNodes(prev => [groupShape, ...prev]);
  }, [activeProject.nodes, selectedIds, isSnapping, setNodes, takeSnapshot]);

  const handleNodeContextMenu = useCallback((e: React.MouseEvent, node: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const worldX = (e.clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom;
    const worldY = (e.clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom;

    if (selectedIds.length > 1 && selectedIds.includes(node.id)) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType: 'MULTI',
        targetId: node.id
      });
    } else {
      setSelectedIds([node.id]);
      setSelectionType('NODE');
      const targetType = (node.type === 'SHAPE' || node.type === 'TEXT' || node.type === 'IMAGE') ? 'SHAPE' : 'NODE';
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType,
        targetId: node.id
      });
    }
  }, [selectedIds, activeProject]);

  const handleConnectionContextMenu = useCallback((e: React.MouseEvent, conn: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const worldX = (e.clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom;
    const worldY = (e.clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom;

    if (selectedIds.length > 1 && selectedIds.includes(conn.id)) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType: 'MULTI',
        targetId: conn.id
      });
    } else {
      setSelectedIds([conn.id]);
      setSelectionType('CONNECTION');
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType: 'CONNECTION',
        targetId: conn.id
      });
    }
  }, [selectedIds, activeProject]);

  const handleCanvasContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const worldX = (e.clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom;
    const worldY = (e.clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom;

    if (selectedIds.length > 1) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType: 'MULTI'
      });
    } else {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        worldX,
        worldY,
        targetType: 'CANVAS'
      });
    }
  }, [selectedIds, activeProject]);

  const handleMouseDown = useCallback((e) => {
    // Track panning struggle on Right Click (button 2) on empty canvas
    if (e.button === 2) {
      setPanStruggleCount(prev => {
        const next = prev + 1;
        if (next >= 3) {
          if (typeof window !== 'undefined' && !localStorage.getItem('netmapper_pan_tip_dismissed')) {
            setShowPanTip(true);
          }
          return 0;
        }
        return next;
      });
      return;
    }

    // Only handle left click, middle click, or touch
    if (e.button !== 0 && e.type === 'mousedown' && e.button !== 1) return;

    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    // Middle click pan (button 1) OR Spacebar + Left drag OR PAN mode + Left drag
    if (e.button === 1 || (e.button === 0 && (isSpacePressed || mode === "PAN"))) {
      setDragState({ 
        type: 'pan', 
        startX: clientX, 
        startY: clientY,
        initialViewOffset: activeProject.viewOffset // FIX: Store initial offset for absolute calculation
      });
      return;
    }

    // Track panning struggle on left click (button 0) in SELECT mode on empty canvas without selecting nodes
    if (e.button === 0 && mode === "SELECT") {
      setPanStruggleCount(prev => {
        const next = prev + 1;
        if (next >= 3) {
          if (typeof window !== 'undefined' && !localStorage.getItem('netmapper_pan_tip_dismissed')) {
            setShowPanTip(true);
          }
          return 0;
        }
        return next;
      });
    }

    const pos = getMouseWorldPos(e, canvasRef.current.getBoundingClientRect(), activeProject.viewOffset, activeProject.zoom);
    
    if (mode === "SELECT") {
      setSelectionBox({ startX: pos.x, startY: pos.y, currentX: pos.x, currentY: pos.y });
      if (!e.ctrlKey && !e.shiftKey) setSelectedIds([]);
    } else if (mode === "CONNECT") {
       // Deselect link/node when clicking empty space in Link mode
       setSelectedIds([]);
       setConnectSourceId(null);
    } else if (mode === "TEXT") {
       if (activeProject.nodes.length >= MAX_NODES) return;
       // Updated to use adaptive default color (undefined) which renders as Slate 300 (Dark) or Slate 800 (Light)
       setNodes(prev => [...prev, { id: generateId("txt"), type: "TEXT", x: snapValue(pos.x, true), y: snapValue(pos.y, true), content: "New Label", fontSize: 14 }]);
       setMode("SELECT");
    }
  }, [mode, isSpacePressed, activeProject, setNodes]);

  const handleNodeMouseDown = useCallback((e, node) => {
    // If Spacebar is pressed or mode is PAN, clicking on nodes initiates canvas pan
    if (mode === "PAN" || isSpacePressed) {
      if (e.button === 0 || e.type === 'touchstart') {
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        const clientY = e.clientY || e.touches?.[0]?.clientY;
        if (clientX !== undefined && clientY !== undefined) {
          setDragState({
            type: 'pan',
            startX: clientX,
            startY: clientY,
            initialViewOffset: activeProject.viewOffset
          });
          return;
        }
      }
    }

    e.stopPropagation(); // Essential for preventing canvas drag
    
    // FIX: Update timestamp for touch events to prevent ghost clicks on overlay
    if (e.type === 'touchstart') {
      lastTouchTimeRef.current = Date.now();
      // We can try preventDefault but purely relying on it is flaky with passive listeners
      // e.preventDefault(); 
    }
    
    if (mode === "CONNECT") {
       if (!node.interfaces && node.type !== "CLOUD") return; // Allow CLOUD (handled separately) but block others without interfaces
       
       if (connectSourceId) {
         if (connectSourceId !== node.id) setPendingLink({ source: activeProject.nodes.find(n => n.id === connectSourceId), target: node });
         setConnectSourceId(null);
       } else {
         setConnectSourceId(node.id);
       }
       return;
    }

    takeSnapshot();

    // Determine intended selection for this action
    const isMulti = e.ctrlKey || e.shiftKey;
    let dragNodeIds: any[] = [];

    if (isMulti) {
       if (selectedIds.includes(node.id)) {
         setSelectedIds(prev => prev.filter(id => id !== node.id));
         return; 
       } else {
         dragNodeIds = [...selectedIds, node.id];
         setSelectedIds(dragNodeIds);
         setSelectionType("NODE");
       }
    } else {
       if (selectedIds.includes(node.id)) {
         dragNodeIds = selectedIds;
       } else {
         dragNodeIds = [node.id];
         setSelectedIds(dragNodeIds);
         setSelectionType("NODE");
         // NOTE: Removed immediate setIsPropertiesOpen here to support drag-without-open
       }
    }
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    if (!clientX) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const pos = {
       x: (clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom,
       y: (clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom
    };
    
    const initialPositions = {};
    activeProject.nodes.forEach((n: any) => {
       if (dragNodeIds.includes(n.id)) {
          initialPositions[n.id] = { x: n.x, y: n.y, endX: n.endX, endY: n.endY, width: n.width, height: n.height };
       }
    });

    setDragState({ 
        type: 'move', 
        id: node.id, 
        startX: pos.x, 
        startY: pos.y, 
        initialPositions,
        screenStartX: clientX, // Capture screen start X for drag threshold
        screenStartY: clientY  // Capture screen start Y for drag threshold
    });
    isDraggingMovedRef.current = false; // Reset movement flag

  }, [mode, connectSourceId, activeProject, selectedIds, takeSnapshot]);

  // Touch Handlers
  const handleTouchStart = useCallback((e) => {
     if (e.touches.length === 2) {
        // Pinch Zoom Start
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        
        // Calculate center of the pinch in screen coordinates
        const centerX = (t1.clientX + t2.clientX) / 2;
        const centerY = (t1.clientY + t2.clientY) / 2;

        // Calculate that center point in world coordinates before zoom
        const rect = canvasRef.current.getBoundingClientRect();
        const worldX = (centerX - rect.left - activeProject.viewOffset.x) / activeProject.zoom;
        const worldY = (centerY - rect.top - activeProject.viewOffset.y) / activeProject.zoom;

        setDragState({ 
           type: 'pinch', 
           startDist: dist, 
           startZoom: activeProject.zoom,
           startWorld: { x: worldX, y: worldY }
        });
        return;
     } else if (e.touches.length === 1) {
        // If we reach here, it means we touched the background (nodes stop propagation)
        // So we can safely start panning.
        setDragState({ 
            type: 'pan', 
            startX: e.touches[0].clientX, 
            startY: e.touches[0].clientY,
            initialViewOffset: activeProject.viewOffset // FIX: Store initial offset for absolute calculation
        });
     }
  }, [activeProject]);

  const handleTouchMove = useCallback((e) => {
     if (dragState?.type === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        
        // Calculate new scale
        const scale = dist / dragState.startDist;
        const newZoom = Math.min(2, Math.max(0.1, dragState.startZoom * scale));

        // Calculate current center of pinch
        const centerX = (t1.clientX + t2.clientX) / 2;
        const centerY = (t1.clientY + t2.clientY) / 2;
        const rect = canvasRef.current.getBoundingClientRect();

        // Calculate new offset to keep the world point at the center point
        // screenX = worldX * zoom + offsetX + rectLeft
        // offsetX = screenX - rectLeft - worldX * zoom
        const newOffsetX = centerX - rect.left - (dragState.startWorld.x * newZoom);
        const newOffsetY = centerY - rect.top - (dragState.startWorld.y * newZoom);

        updateProject(p => ({ 
            ...p, 
            zoom: newZoom,
            viewOffset: { x: newOffsetX, y: newOffsetY }
        }));
        return;
     }
     
     if (e.touches.length === 1 && dragState) {
         const touch = e.touches[0];
         handleGlobalMouseMove({
             preventDefault: () => e.preventDefault(),
             clientX: touch.clientX,
             clientY: touch.clientY
         });
     }
  }, [dragState, updateProject]); 

  const handleTouchEnd = useCallback(() => {
      // Logic for detecting tap on mobile
      const ds = dragStateRef.current;
      if (ds && ds.type === 'move' && !isDraggingMovedRef.current) {
          if (window.innerWidth < 768) setIsPropertiesOpen(true);
      }
      setDragState(null);
  }, []);


  // STABLE Global Handlers
  const handleGlobalMouseMove = useCallback((e) => {
    const ds = dragStateRef.current;
    const sb = selectionBoxRef.current;
    
    if (!ds && !sb) return;

    const proj = activeProjectRef.current;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();

    if (ds) {
       e.preventDefault?.(); 
       
       if (ds.type === 'pan') {
         const dx = e.clientX - ds.startX;
         const dy = e.clientY - ds.startY;
         
         // FIX: Use absolute calculation from initialViewOffset to prevent lag/jitter
         if (ds.initialViewOffset) {
             setProjects(prev => prev.map(p => p.id === proj.id ? { 
                ...p, 
                viewOffset: { 
                    x: ds.initialViewOffset.x + dx, 
                    y: ds.initialViewOffset.y + dy 
                } 
             } : p));
         }
         return;
       }

       const pos = {
           x: (e.clientX - rect.left - proj.viewOffset.x) / proj.zoom,
           y: (e.clientY - rect.top - proj.viewOffset.y) / proj.zoom
       };

       const dx = pos.x - ds.startX;
       const dy = pos.y - ds.startY;
       
       if (ds.type === 'linkControlPoint') {
           setConnections(prev => prev.map(c => c.id === ds.id ? {
               ...c,
               controlPoint: { ...ds.initialCP, x: ds.initialCP.x + dx, y: ds.initialCP.y + dy }
           } : c));
           return;
       }

       if (ds.type === 'resize') {
          setNodes(prev => prev.map(n => {
             if (n.id !== ds.id) return n;
             
             let updates: any = {};
             if (ds.handle === 'p1') {
                updates = {
                   x: ds.initial.x + dx,
                   y: ds.initial.y + dy
                };
             } else if (ds.handle === 'p2') {
                updates = {
                   endX: ds.initial.endX + dx,
                   endY: ds.initial.endY + dy
                };
             } else if (ds.handle === 'se') {
                updates = {
                   width: Math.max(20, ds.initial.w + dx),
                   height: Math.max(20, ds.initial.h + dy)
                };
             } else if (ds.handle === 'nw') {
                updates = {
                   x: ds.initial.x + dx,
                   y: ds.initial.y + dy,
                   width: Math.max(20, ds.initial.w - dx),
                   height: Math.max(20, ds.initial.h - dy)
                };
             }
             
             if(isSnapping) {
                 if(updates.width) updates.width = snapValue(updates.width, true);
                 if(updates.height) updates.height = snapValue(updates.height, true);
                 if(updates.x) updates.x = snapValue(updates.x, true);
                 if(updates.y) updates.y = snapValue(updates.y, true);
                 if(updates.endX) updates.endX = snapValue(updates.endX, true);
                 if(updates.endY) updates.endY = snapValue(updates.endY, true);
             }
             
             return { ...n, ...updates };
          }));
          return; 
       }

       if (ds.type === 'move') {
          // Track significant movement (Tap vs Drag detection)
          const screenDx = e.clientX - ds.screenStartX;
          const screenDy = e.clientY - ds.screenStartY;
          if (Math.hypot(screenDx, screenDy) > 5) { // 5 pixel threshold
             isDraggingMovedRef.current = true;
          }

          setNodes(prev => prev.map(n => {
            if (n.isLocked) return n;
            if (ds.initialPositions && ds.initialPositions[n.id]) {
               const init = ds.initialPositions[n.id];
               if ((n as any).shapeType === 'LINE') {
                   // Fallbacks for old projects missing endX/endY
                   const eX = init.endX !== undefined ? init.endX : init.x + (init.width || 100);
                   const eY = init.endY !== undefined ? init.endY : init.y + (init.height || 0);
                   return { 
                       ...n, 
                       x: snapValue(init.x + dx, isSnapping), 
                       y: snapValue(init.y + dy, isSnapping),
                       endX: snapValue(eX + dx, isSnapping),
                       endY: snapValue(eY + dy, isSnapping)
                   };
               }
               return { ...n, x: snapValue(init.x + dx, isSnapping), y: snapValue(init.y + dy, isSnapping) };
            }
            return n;
          }));
       }
    } else if (sb) {
       const pos = {
           x: (e.clientX - rect.left - proj.viewOffset.x) / proj.zoom,
           y: (e.clientY - rect.top - proj.viewOffset.y) / proj.zoom
       };
       setSelectionBox(prev => ({ ...prev, currentX: pos.x, currentY: pos.y }));
    }
  }, [isSnapping, setNodes, setProjects]); 

  const handleGlobalMouseUp = useCallback(() => {
    const ds = dragStateRef.current;
    const sb = selectionBoxRef.current;
    
    if (sb) {
       const proj = activeProjectRef.current;
       const x1 = Math.min(sb.startX, sb.currentX);
       const x2 = Math.max(sb.startX, sb.currentX);
       const y1 = Math.min(sb.startY, sb.currentY);
       const y2 = Math.max(sb.startY, sb.currentY);
       
       const ids = proj.nodes.filter(n => 
         n.x < x2 && n.x + NODE_WIDTH > x1 && 
         n.y < y2 && n.y + NODE_HEIGHT > y1
       ).map(n => n.id);
       
       if (ids.length) { 
         setSelectedIds(ids); 
         setSelectionType("NODE"); 
         if (window.innerWidth < 768) setIsPropertiesOpen(true);
       }
    }

    if (ds || sb) {
      // Handle tap detection for mouse interactions too (e.g., hybrid devices)
      if (ds && ds.type === 'move' && !isDraggingMovedRef.current) {
         if (window.innerWidth < 768) setIsPropertiesOpen(true);
      }

      setDragState(null);
      setSelectionBox(null);
    }
  }, []);

  // Global Event Listeners - Bound Once (or very rarely)
  useEffect(() => {
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [handleGlobalMouseMove, handleGlobalMouseUp]);

  // Mobile Tap-to-Add Handler
  const handleAddNode = (type, brand = 'default') => {
    if (activeProject.nodes.length >= MAX_NODES) return;
    takeSnapshot();
    
    let x = 100, y = 100;
    if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        x = (-activeProject.viewOffset.x + (rect.width / 2)) / activeProject.zoom;
        y = (-activeProject.viewOffset.y + (rect.height / 2)) / activeProject.zoom;
        x = snapValue(x, true);
        y = snapValue(y, true);
    }

    setNodes(prev => [...prev, createNodeObject(type, x, y, prev, brand)]);
  };

  const handleAddShape = (shapeType: string) => {
    if (activeProject.nodes.length >= MAX_NODES) return;
    takeSnapshot();

    const shapeWidth = 140;
    const shapeHeight = 140;
    let x = 100, y = 100;
    if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        x = (-activeProject.viewOffset.x + (rect.width / 2)) / activeProject.zoom - (shapeType === "LINE" ? 75 : shapeWidth / 2);
        y = (-activeProject.viewOffset.y + (rect.height / 2)) / activeProject.zoom - (shapeType === "LINE" ? 50 : shapeHeight / 2);
        x = snapValue(x, true);
        y = snapValue(y, true);
    }

    let finalX = x, finalY = y;
    let attempts = 0;
    while (activeProject.nodes.some(n => Math.abs(n.x - finalX) < 40 && Math.abs(n.y - finalY) < 40) && attempts < 50) {
      finalX += GRID_SIZE;
      finalY += GRID_SIZE;
      attempts++;
    }

    const newShape: any = {
      id: generateId("shape"),
      type: "SHAPE",
      shapeType,
      x: finalX,
      y: finalY,
      color: "#3b82f6",
      borderWidth: 4,
      borderStyle: "solid"
    };

    if (shapeType === "LINE") {
      newShape.endX = finalX + 150;
      newShape.endY = finalY + 100;
    } else {
      newShape.width = shapeWidth;
      newShape.height = shapeHeight;
    }

    setNodes(prev => [...prev, newShape]);
    setSelectedIds([newShape.id]);
    setSelectionType("NODE");
    setMode("SELECT");
    if (window.innerWidth < 768) {
      setIsPropertiesOpen(true);
    }
  };

  const handleImportImage = (base64Data, file) => {
    if (activeProject.nodes.length >= MAX_NODES) return;
    takeSnapshot();

    let finalX = 100, finalY = 100;
    let attempts = 0;
    while (activeProject.nodes.some(n => Math.abs(n.x - finalX) < 100 && Math.abs(n.y - finalY) < 100) && attempts < 50) {
      finalX += GRID_SIZE;
      finalY += GRID_SIZE;
      attempts++;
    }

    const newNode = {
      id: generateId("img"),
      type: "IMAGE",
      x: finalX,
      y: finalY,
      width: 100,
      height: 100,
      src: base64Data,
      fileName: file.name
    };

    setNodes(prev => [...prev, newNode]);
  };

  const handleInsertSvgIcons = (selectedIcons: SvgIconItem[]) => {
    if (!selectedIcons || selectedIcons.length === 0) return;
    takeSnapshot();

    const canvasRect = canvasRef.current?.getBoundingClientRect();
    const centerWorld = canvasRect
      ? getMouseWorldPos({ clientX: canvasRect.left + canvasRect.width / 2, clientY: canvasRect.top + canvasRect.height / 2 }, canvasRect, activeProject.viewOffset, activeProject.zoom)
      : { x: 350, y: 250 };

    const cols = Math.min(selectedIcons.length, 4);
    const spacingX = 140;
    const spacingY = 140;

    const startX = centerWorld.x - ((cols - 1) * spacingX) / 2;
    const startY = centerWorld.y - (Math.floor((selectedIcons.length - 1) / cols) * spacingY) / 2;

    setNodes(prev => {
      const newNodes = selectedIcons.map((item, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        const posX = snapValue(startX + col * spacingX, true);
        const posY = snapValue(startY + row * spacingY, true);

        const nodeObj = createNodeObject(item.key, posX, posY, [...prev], item.brand);
        nodeObj.name = item.name;
        return nodeObj;
      });
      return [...prev, ...newNodes];
    });
  };

  return (
    <div className={`flex h-screen w-full ${styles.bg} ${styles.text} overflow-hidden select-none relative`} onClick={() => setOpenMenuId(null)}>
      {runTutorial && (
        <TutorialOverlay
          step={tutorialStep}
          steps={TUTORIAL_STEPS}
          onNext={() => {
            if (tutorialStep < TUTORIAL_STEPS.length - 1) {
              setTutorialStep(s => s + 1);
            } else {
              setRunTutorial(false);
              setTutorialStep(0);
              localStorage.setItem("netmapper_tutorial_seen", "true");
            }
          }}
          onSkip={() => {
            setRunTutorial(false);
            setTutorialStep(0);
          }}
          isDark={isDark}
        />
      )}
      
      {/* Mobile Overlay for Sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
      
      <LeftSidebar 
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        onAddNode={handleAddNode} 
        onImportImage={handleImportImage}
        onOpenIconModal={() => setIsIconModalOpen(true)}
        styles={styles} 
        onShowFAQ={() => setShowFAQ(true)} 
        onShowAbout={() => setShowAbout(true)} 
        selectedBrand={selectedBrand}
        onBrandChange={(brand) => {
          setSelectedBrand(brand);
        }}
      />
      
      <div className="flex-1 flex flex-col relative w-full">
        <div id="tutorial-tabs" onScroll={() => setOpenMenuId(null)} className={`${styles.panelBg} ${styles.panelBorder} border-b flex items-end px-2 gap-1 pt-2 overflow-x-auto no-scrollbar`}>
          {projects.map(p => (
            <div 
                key={p.id} 
                onClick={() => setActiveProjectId(p.id)} 
                className={`relative flex items-center min-w-[140px] h-9 px-3 rounded-t-lg border-t border-x cursor-pointer group select-none ${p.id === activeProjectId ? `${styles.bg} text-blue-500 font-bold border-b-transparent` : `${styles.panelBg} text-slate-500 hover:bg-slate-100`}`}
            >
               <div className={`w-2 h-2 rounded-full mr-2 ${TAB_COLORS[p.color]}`} />
               
               {/* Rename Input or Label */}
               {editingProjectId === p.id ? (
                   <input 
                      autoFocus
                      className={`flex-1 min-w-0 bg-transparent outline-none text-xs border-b border-blue-500 text-inherit font-inherit`}
                      value={p.name}
                      onChange={(e) => setProjects(prev => prev.map(proj => proj.id === p.id ? { ...proj, name: e.target.value } : proj))}
                      onBlur={() => setEditingProjectId(null)}
                      onKeyDown={(e) => e.key === 'Enter' && setEditingProjectId(null)}
                      onClick={(e) => e.stopPropagation()}
                   />
               ) : (
                   <span className="truncate flex-1 text-xs">{p.name}</span>
               )}

               {/* Lock Indicator */}
               {p.isLocked && <Lock size={10} className="text-slate-400 ml-1" />}

               {/* Menu Trigger */}
               <button 
                  onClick={(e) => { 
                      e.stopPropagation(); 
                      if (openMenuId === p.id) {
                          setOpenMenuId(null);
                      } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ x: rect.right, y: rect.bottom });
                          setOpenMenuId(p.id); 
                      }
                  }} 
                  className={`ml-1 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${openMenuId === p.id ? "text-blue-500 bg-blue-50" : "text-slate-400"}`}
               >
                 <MoreVertical size={12} />
               </button>

               {/* Dropdown Menu */}
               {openMenuId === p.id && (
                 <div 
                    className={`fixed w-32 ${styles.panelBg} border ${styles.panelBorder} shadow-xl rounded-lg z-[100] overflow-hidden flex flex-col py-1`}
                    style={{ top: menuPos.y + 4, left: menuPos.x - 112 }}
                 >
                    <button 
                        onClick={(e) => { e.stopPropagation(); setEditingProjectId(p.id); setOpenMenuId(null); }}
                        className={`text-xs text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2 ${styles.textSubtle}`}
                    >
                        <Edit3 size={12} /> Rename
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); setProjects(prev => prev.map(proj => proj.id === p.id ? { ...proj, isLocked: !proj.isLocked } : proj)); setOpenMenuId(null); }}
                        className={`text-xs text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2 ${styles.textSubtle}`}
                    >
                        {p.isLocked ? <Unlock size={12} /> : <Lock size={12} />} {p.isLocked ? "Unlock" : "Lock"}
                    </button>
                    <div className={`h-px mx-2 ${styles.panelBorder} border-b my-1`} />
                    <button 
                        disabled={p.isLocked || projects.length <= 1}
                        onClick={(e) => { e.stopPropagation(); if(projects.length > 1 && !p.isLocked) { setProjectToDelete(p.id); } setOpenMenuId(null); }}
                        className={`text-xs text-left px-3 py-2 flex items-center gap-2 ${p.isLocked || projects.length <= 1 ? "text-slate-300 cursor-not-allowed" : "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"}`}
                    >
                        <Trash2 size={12} /> Delete
                    </button>
                 </div>
               )}
            </div>
          ))}
          <button 
            onClick={() => {
              if (projects.length >= MAX_PROJECTS) {
                setIsMaxProjectsAlertOpen(true);
              } else {
                setNewProjectName("");
                setIsNamingModalOpen(true);
              }
            }} 
            className="px-2 mb-1 hover:text-blue-500"
            title="Create New Project Tab"
          >
            <Plus size={18} />
          </button>
        </div>

                <TopToolbar
            mode={mode} setMode={setMode}
            showCalc={showCalculator} setShowCalc={setShowCalculator}
            zoom={activeProject.zoom} setZoom={(val) => updateProject(p => ({...p, zoom: typeof val === 'function' ? val(p.zoom) : val}))}
            isExporting={isExporting} onExport={handleExport}
            onPrint={handlePrint}
            isFullScreen={isFullScreen} onToggleFullScreen={() => (!document.fullscreenElement ? document.documentElement.requestFullscreen() : document.exitFullscreen(), setIsFullScreen(!isFullScreen))}
            styles={styles} isDark={isDark} connectSourceId={connectSourceId} isPanning={isPanning}
            toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            onDeselectSource={() => setConnectSourceId(null)}
            onUploadBackground={() => setIsBackgroundSettingsOpen(true)}
            onSaveLayout={exportProjectToFile}
            onOpenLayout={handleOpenLayout}
            onAddShape={handleAddShape}
            onShowWhatsNew={() => setShowWhatsNew(true)}
            onUndo={handleUndo}
            onRedo={handleRedo}
            history={history}
            onOpenTemplates={() => setIsTemplateModalOpen(true)}
        />        
        <div 
            ref={canvasRef} 
            className={`flex-1 flex flex-col overflow-hidden relative ${isSpacePressed || mode === 'PAN' ? (dragState?.type === 'pan' ? 'cursor-grabbing' : 'cursor-grab') : ''}`} 
            onContextMenu={handleCanvasContextMenu}
            onDrop={(e) => { e.preventDefault(); const t = e.dataTransfer.getData("deviceType"); const b = e.dataTransfer.getData("deviceBrand") || "default"; if(t) { const pos = getMouseWorldPos(e, canvasRef.current.getBoundingClientRect(), activeProject.viewOffset, activeProject.zoom); setNodes(prev => [...prev, createNodeObject(t, snapValue(pos.x, true), snapValue(pos.y, true), prev, b)]); } }} 
            onDragOver={e => e.preventDefault()} 
            onWheel={(e) => {
              // Zoom towards mouse pointer logic
              if (!canvasRef.current) return;
              const rect = canvasRef.current.getBoundingClientRect();
              const mouseX = e.clientX - rect.left;
              const mouseY = e.clientY - rect.top;
              const delta = -e.deltaY * 0.001;

              updateProject(prev => {
                const oldZoom = prev.zoom;
                const newZoom = Math.min(2, Math.max(0.1, oldZoom + delta));
                
                // Calculate new offset to zoom towards the mouse pointer
                // Formula: newOffset = mouse - (mouse - oldOffset) * (newZoom / oldZoom)
                const scaleFactor = newZoom / oldZoom;
                const newOffsetX = mouseX - (mouseX - prev.viewOffset.x) * scaleFactor;
                const newOffsetY = mouseY - (mouseY - prev.viewOffset.y) * scaleFactor;

                return { ...prev, zoom: newZoom, viewOffset: { x: newOffsetX, y: newOffsetY } };
              });
            }}
        >
           <CanvasArea
              customBackground={activeProject.customBackground}
              customBackgroundColor={activeProject.customBackgroundColor}
              nodes={activeProject.nodes}
              connections={activeProject.connections}
              zoom={activeProject.zoom}
              viewOffset={activeProject.viewOffset}
              selectedIds={selectedIds} 
              selectionType={selectionType} 
              connectSourceId={connectSourceId} 
              isDark={isDark} 
              showGrid={showGrid} 
              selectedBrand={selectedBrand}
              isSpacePressed={isSpacePressed}
              isPanning={isPanning}
              mode={mode}
              onMouseDown={handleMouseDown} 
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onNodeMouseDown={handleNodeMouseDown} 
              onResizeMouseDown={handleResizeMouseDown}
              onNodeContextMenu={handleNodeContextMenu}
              onConnectionContextMenu={handleConnectionContextMenu}
              onConnectionClick={(e, id) => { 
                  if (isSpacePressed || mode === "PAN") return;
                  e.stopPropagation(); 
                  setSelectedIds([id]); 
                  setSelectionType("CONNECTION"); 
                  if (window.innerWidth < 768) setIsPropertiesOpen(true); 
              }} 
              onConnectionMouseDown={(e, connId, isHandle) => {
                  if (isSpacePressed || mode === "PAN") {
                      if (e.button === 0 || e.type === 'touchstart') {
                          const clientX = e.clientX || e.touches?.[0]?.clientX;
                          const clientY = e.clientY || e.touches?.[0]?.clientY;
                          if (clientX !== undefined && clientY !== undefined) {
                              setDragState({
                                  type: 'pan',
                                  startX: clientX,
                                  startY: clientY,
                                  initialViewOffset: activeProject.viewOffset
                              });
                              return;
                          }
                      }
                  }
                  if (e.button !== 0 && e.type !== 'touchstart') return;
                  const clientX = e.clientX || e.touches?.[0]?.clientX;
                  const clientY = e.clientY || e.touches?.[0]?.clientY;
                  if (clientX === undefined) return;

                  const rect = canvasRef.current.getBoundingClientRect();
                  const pos = {
                      x: (clientX - rect.left - activeProject.viewOffset.x) / activeProject.zoom,
                      y: (clientY - rect.top - activeProject.viewOffset.y) / activeProject.zoom
                  };

                  if (isHandle) {
                      e.stopPropagation();
                      const conn = activeProject.connections.find(c => c.id === connId);
                      if (!conn || !(conn as any).controlPoint) return;
                      setDragState({
                          type: 'linkControlPoint',
                          id: connId,
                          startX: pos.x,
                          startY: pos.y,
                          initialCP: { ...(conn as any).controlPoint }
                      });
                      return;
                  }

                  if (e.ctrlKey || e.altKey) {
                      e.stopPropagation();
                      const type = e.altKey ? 'rightAngle' : 'curve';
                      setConnections(prev => prev.map(c => c.id === connId ? { ...c, controlPoint: { x: pos.x, y: pos.y, type } } : c));
                      setDragState({
                          type: 'linkControlPoint',
                          id: connId,
                          startX: pos.x,
                          startY: pos.y,
                          initialCP: { x: pos.x, y: pos.y, type }
                      });
                  }
              }}
              selectionBox={selectionBox} 
              isExporting={isExporting}
            />
           
           <div id="tutorial-bottom-controls" data-html2canvas-ignore="true" className="absolute bottom-20 left-4 md:bottom-6 md:left-6 flex flex-col gap-2 z-50">
              <button onClick={() => setShowGrid(!showGrid)} className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border transition-all hover:border-blue-500 hover:shadow-blue-500/30 ${isDark ? "bg-slate-800 border-slate-600" : "bg-white border-slate-300"}`}><Grid size={16} className={showGrid ? "text-blue-500" : "text-slate-400"} /><span className="text-[10px] font-bold">GRID</span></button>
              <button onClick={() => setIsSnapping(!isSnapping)} className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border transition-all ${isSnapping ? "bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500 hover:border-emerald-400 hover:shadow-emerald-500/30" : `${isDark ? "bg-slate-800 border-slate-600" : "bg-white border-slate-300"} hover:border-blue-500 hover:shadow-blue-500/30`}`}><Magnet size={16} /><span className="text-[10px] font-bold">SNAP</span></button>
              <button onClick={() => setTheme(isDark ? "light" : "dark")} className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border transition-all hover:border-blue-500 hover:shadow-blue-500/30 ${isDark ? "bg-slate-800 border-slate-600" : "bg-white border-slate-300"}`}>{isDark ? <Moon size={16} className="text-blue-400" /> : <Sun size={16} className="text-orange-500" />}<span className="text-[10px] font-bold">THEME</span></button>
           </div>
           
           {/* Watermark for Exports/Prints */}
           {isExporting && (
             <div className="absolute bottom-6 left-6 z-[60] opacity-50">
                 {/* Switched to solid color for export to fix html2canvas rendering issue with gradients */}
                 <h1 className={`text-2xl font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>NetMapper</h1>
                 <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"} font-mono tracking-widest uppercase mt-1`}>Infrastructure Builder</p>
             </div>
           )}
        </div>
        
        {/* Floating Pan Tip Banner */}
        {showPanTip && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 bg-slate-900/95 dark:bg-slate-800/95 text-white rounded-xl shadow-2xl border border-slate-700/80 backdrop-blur-md animate-fade-in text-sm max-w-[90vw] md:max-w-max">
            <span>💡 Laptop user? Hold Spacebar + Left Drag or use Middle Mouse to pan the canvas!</span>
            <button
              onClick={() => {
                setShowPanTip(false);
                localStorage.setItem('netmapper_pan_tip_dismissed', 'true');
              }}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap shadow-sm cursor-pointer"
            >
              Got it
            </button>
          </div>
        )}

        {showCalculator && <SubnetCalculator initialPos={{x: 100, y: 100}} onClose={() => setShowCalculator(false)} styles={styles} />}
        {pendingLink && <ConnectionModal source={pendingLink.source} target={pendingLink.target} isInterfaceUsed={(nid, iid) => activeProject.connections.some(c => (c.from === nid && c.fromIf === iid) || (c.to === nid && c.toIf === iid))} onConfirm={(sIf, tIf, cable) => { setConnections(prev => [...prev, { id: generateId("c"), from: pendingLink.source.id, to: pendingLink.target.id, fromIf: sIf, toIf: tIf, cable }]); setPendingLink(null); }} onCancel={() => setPendingLink(null)} styles={styles} isDark={isDark} />}
        {showFAQ && (
          <FAQModal
            onClose={() => setShowFAQ(false)}
            onRestartTutorial={() => {
              setShowFAQ(false);
              setTutorialStep(0);
              setRunTutorial(true);
            }}
            styles={styles}
            data={FAQ_DATA}
            isDark={isDark}
          />
        )}
        {showAbout && <AboutModal onClose={() => setShowAbout(false)} onGetStarted={handleGetStarted} styles={styles} isDark={isDark} />}
        {showWhatsNew && <WhatsNewModal onClose={() => setShowWhatsNew(false)} styles={styles} isDark={isDark} />}
        {isTemplateModalOpen && (
          <TemplateModal
            onClose={() => setIsTemplateModalOpen(false)}
            onSelectTemplate={handleSelectTemplate}
            styles={styles}
            isDark={isDark}
          />
        )}
        {isBackgroundSettingsOpen && (
          <BackgroundSettingsModal
            onClose={() => setIsBackgroundSettingsOpen(false)}
            isDark={isDark}
            styles={styles}
            currentColor={activeProject.customBackgroundColor}
            onUpload={(result) => updateProject(p => ({ ...p, customBackground: result }))}
            onColorChange={(color) => updateProject(p => ({ ...p, customBackgroundColor: color }))}
            onReset={() => updateProject(p => ({ ...p, customBackground: null, customBackgroundColor: null }))}
          />
        )}
        <SvgIconModal
          isOpen={isIconModalOpen}
          onClose={() => setIsIconModalOpen(false)}
          onInsert={handleInsertSvgIcons}
          styles={styles}
          isDark={isDark}
          initialBrand={selectedBrand !== 'default' ? (selectedBrand as any) : 'aws'}
        />
        {projectToDelete && (
           <DeleteProjectModal 
              projectName={projects.find(p => p.id === projectToDelete)?.name}
              onConfirm={() => { setProjects(prev => prev.filter(p => p.id !== projectToDelete)); setProjectToDelete(null); }}
              onCancel={() => setProjectToDelete(null)}
              styles={styles}
              isDark={isDark}
           />
        )}
        {interfaceToDelete && (
           <ConfirmationModal
              title="Delete Interface?"
              message="This interface is connected to a link. Deleting it will remove the link as well. Are you sure?"
              onConfirm={() => { performDeleteInterface(interfaceToDelete.nodeId, interfaceToDelete.ifaceId, interfaceToDelete.isSub, interfaceToDelete.parentIfId); setInterfaceToDelete(null); }}
              onCancel={() => setInterfaceToDelete(null)}
              styles={styles}
              isDark={isDark}
           />
        )}
        {pendingImportData && (
          <ImportOptionModal
            pendingDataCount={pendingImportData.length}
            onOverwrite={handleOverwriteImport}
            onAppend={handleAppendImport}
            onCancel={() => setPendingImportData(null)}
            styles={styles}
            isDark={isDark}
          />
        )}
        {isNamingModalOpen && (
          <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`}>
            <div className={`${styles.modalBg} w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`}>
              <h3 className={`font-bold text-lg mb-2 ${styles.text} flex items-center gap-2`}>
                <Edit3 size={20} className="text-blue-500" /> New Project Tab
              </h3>
              <p className={`text-sm mb-4 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Please enter a name for your new project tab.
              </p>
              <div className="mb-6">
                <input
                  autoFocus
                  type="text"
                  maxLength={30}
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateProject();
                    } else if (e.key === "Escape") {
                      setIsNamingModalOpen(false);
                      setNewProjectName("");
                    }
                  }}
                  placeholder="e.g., London HQ"
                  className={`w-full ${styles.inputBg} border ${styles.inputBorder} ${styles.text} rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500`}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsNamingModalOpen(false);
                    setNewProjectName("");
                  }}
                  className={`px-4 py-2 text-xs font-bold ${styles.textSubtle} hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow-lg"
                >
                  Create Tab
                </button>
              </div>
            </div>
          </div>
        )}
        {isMaxProjectsAlertOpen && (
          <div className={`absolute inset-0 z-[100] flex items-center justify-center ${styles.modalOverlay} backdrop-blur-sm animate-in fade-in`}>
            <div className={`${styles.modalBg} w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${styles.panelBorder} m-4`}>
              <h3 className="font-bold text-lg mb-2 text-amber-500 flex items-center gap-2">
                <AlertTriangle size={20} /> Workspace Cap Reached
              </h3>
              <p className={`text-sm mb-6 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                You have reached the maximum allowed limit of <strong>{MAX_PROJECTS} concurrent project tabs</strong>. To prevent browser memory exhaustion and maintain workspace performance, you must close or delete an existing project tab before creating a new one.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setIsMaxProjectsAlertOpen(false)}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold shadow-lg"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
        <input 
          type="file" 
          id="project-file-import" 
          accept=".json" 
          className="hidden" 
          onChange={handleImportFile} 
        />
      </div>
      
      {/* Mobile Overlay for Properties */}
      {/* FIX: Add timestamp check to prevent ghost click from immediately closing the panel */}
      {isPropertiesOpen && <div className="fixed inset-0 bg-black/20 z-20 md:hidden" onClick={(e) => {
         e.stopPropagation();
         if (Date.now() - lastTouchTimeRef.current > 500) {
            setIsPropertiesOpen(false);
         }
      }} />}

      {/* Canvas Minimap / Overview Box */}
      <Minimap
        nodes={activeProject.nodes}
        connections={activeProject.connections}
        viewOffset={activeProject.viewOffset}
        zoom={activeProject.zoom}
        canvasRef={canvasRef}
        onPanTo={(newOffset) => updateProject(p => ({ ...p, viewOffset: newOffset }))}
        showMinimap={showMinimap}
        setShowMinimap={setShowMinimap}
        styles={styles}
        isDark={isDark}
      />

      {/* Context-Aware Right-Click Menu */}
      {contextMenu && (
        <ContextMenu
          contextMenu={contextMenu}
          onClose={() => setContextMenu(null)}
          activeProject={activeProject}
          selectedIds={selectedIds}
          styles={styles}
          isDark={isDark}
          showGrid={showGrid}
          isSnapping={isSnapping}
          clipboard={clipboard}
          onDuplicateNode={handleDuplicateNode}
          onToggleLockNode={handleToggleLockNode}
          onRecolorNode={handleRecolorNode}
          onAddPort={handleAddPort}
          onCopyPrimaryIp={handleCopyPrimaryIp}
          onBringToFront={handleBringToFront}
          onSendToBack={handleSendToBack}
          onSwitchCable={handleSwitchCable}
          onAddBendPoint={handleAddBendPoint}
          onResetPath={handleResetPath}
          onToggleAnimation={handleToggleAnimation}
          onToggleLabels={handleToggleLabels}
          onDeleteLink={handleDeleteLink}
          onApplyShapePreset={handleApplyShapePreset}
          onSendShapeToBackground={handleSendShapeToBackground}
          onQuickAddNode={handleQuickAddNode}
          onPasteAtCursor={handlePasteAtCursor}
          onCenterView={handleCenterView}
          onToggleGrid={() => setShowGrid(prev => !prev)}
          onToggleSnapping={() => setIsSnapping(prev => !prev)}
          onAlignNodes={handleAlignNodes}
          onDistributeNodes={handleDistributeNodes}
          onGroupSelection={handleGroupSelection}
          onDeleteSelection={deleteSelection}
        />
      )}

      <PropertiesPanel 
        selectedIds={selectedIds} 
        nodes={activeProject.nodes} 
        connections={activeProject.connections} 
        setNodes={setNodes} 
        setConnections={setConnections} 
        deleteSelection={deleteSelection} 
        clipboard={clipboard} 
        copyToClipboard={copyToClipboard} 
        pasteFromClipboard={pasteFromClipboard} 
        styles={styles} 
        isDark={isDark} 
        isOpen={isPropertiesOpen}
        togglePanel={() => setIsPropertiesOpen(!isPropertiesOpen)}
        onDeleteInterface={requestDeleteInterface}
      />
    </div>
  );
}